"use strict";
/* Global Transit Stats frontend. Talks only to /api/* — every figure shown
   here comes from a documented API endpoint. (Project codename: GlobalTransitStats.) */

const API = "/api";
const STATUS_COLOR = {
  on_time: "#1a9e4b", late: "#e5484d", early: "#d6921a", unknown: "#8b97a8",
};
// Mode glyphs are hand-drawn inline SVGs (icons.js GTS_ICONS) — identical on
// every OS, currentColor, no icon library (replaces the old per-OS emoji).
function modeIcon(m, size) {
  return window.GTS_ICONS ? window.GTS_ICONS.mode(m, size || 14) : "";
}
function uiIcon(name, size) {
  return window.GTS_ICONS ? window.GTS_ICONS.svg(name, size || 14) : "";
}
const MODE_LABEL = {
  bus: "Bus", tram: "Tram / light rail", metro: "Metro / subway",
  rail: "Train", ferry: "Ferry", cable: "Cable / funicular", other: "Other",
};
// "other" is intentionally omitted — every vehicle is categorized into a real
// mode, so we never surface an "Other" bucket (keeps the on-time breakdown and
// the transit-type filter uniform).
const MODE_ORDER = ["bus", "tram", "metro", "rail", "ferry", "cable"];
const MODE_LINE_COLOR = {
  bus: "#4f8cff", tram: "#f59e0b", metro: "#d23035", rail: "#6b46c1",
  ferry: "#0ea5e9", cable: "#14b8a6", other: "#5c6776",
};
// Canonical time windows. These are POPULATED FROM /api/windows at boot (the
// single source of truth per the Compare/Windows API contract) so the set +
// order + labels + minimum-history gates never drift from the backend. The
// literals below are only a resilience fallback if that fetch fails — they
// mirror the contract's canonical list (5m 1h 24h 7d 30d 3mo 6mo 9mo 1y 5y all).
// `1d` is a backward-compat alias for `24h`; it is intentionally NOT offered in
// the picker (the canonical set uses 24h), but it is still accepted everywhere.
let WINDOWS = [
  ["5m", "5 minutes"], ["1h", "1 hour"], ["24h", "24 hours"], ["7d", "7 days"],
  ["30d", "30 days"], ["3mo", "3 months"], ["6mo", "6 months"], ["9mo", "9 months"],
  ["1y", "1 year"], ["5y", "5 years"], ["all", "All time"],
];
// Minimum days of history a window needs before it may be shown/selected. Shared
// by buildWindows() (the gate) and fillBestWindow() so the two never disagree.
// Rebuilt from /api/windows `min_days_required` at boot. `1d` retained as an
// alias (0 days) so any legacy state that still holds "1d" gates like "24h".
let WINDOW_MIN_DAYS = {
  "5m": 0, "1h": 0, "24h": 0, "1d": 0, "7d": 7, "30d": 30,
  "3mo": 90, "6mo": 180, "9mo": 270, "1y": 365, "5y": 1825, "all": 0,
};
// Per-window metadata from /api/windows: key -> {label, comparability,
// comparability_note, min_days_required, source}. Drives the comparability
// caption + the drift-proof window picker.
let WINDOW_META = {};
// Fetch /api/windows and rebuild WINDOWS / WINDOW_MIN_DAYS / WINDOW_META from it.
// Never throws — on failure the module-level fallbacks above stay in effect so
// the UI still renders a sensible canonical picker.
async function loadWindows() {
  try {
    const d = await getJSONRetry("/windows", 3);
    if (d && Array.isArray(d.windows) && d.windows.length) {
      const pairs = [], mins = {}, meta = {};
      d.windows.forEach((w) => {
        if (!w || !w.key) return;
        pairs.push([w.key, w.label || w.key]);
        mins[w.key] = (w.min_days_required == null) ? 0 : w.min_days_required;
        meta[w.key] = {
          label: w.label || w.key,
          comparability: w.comparability || "comparable",
          comparability_note: w.comparability_note || "",
          min_days_required: w.min_days_required,
          source: w.source || "",
        };
      });
      // Keep the `1d` alias gated as a raw window even though it's not offered.
      if (mins["1d"] == null) mins["1d"] = 0;
      WINDOWS = pairs;
      WINDOW_MIN_DAYS = mins;
      WINDOW_META = meta;
    }
  } catch (e) { /* keep the canonical fallbacks defined above */ }
}

let map, vehicleLayer, routeLayer, scheduledLayer, scheduledRenderer, cities = [], current = null;
let currentNoGeometry = false;  // true when the current city's feed has no shapes.txt (no route lines)
let currentGeometrySource = "";  // "route" (published GTFS shapes) | "scheduled" (stop-sequence path) | "none"
// Route-polyline highlight index (TASK D/E): route_id -> [Leaflet polylines].
// Rebuilt on every loadShapes; highlightedRoute tracks the active selection.
let routeIndex = {}, highlightedRoute = null;
// route_id -> {name, long, mode} for the floating route chip; count of vehicles
// currently shown (drives the dense-city dot declutter).
let routeMeta = {}, _vehShown = 0;
let window_ = "1h", cmpWindow = "1h", cmpMode = "all";
// compare v2 sort state + last-fetched data (for re-sort and CSV without re-fetch)
let cmpSortCol = null, cmpSortDir = 1; // col = field key; dir = 1 asc, -1 desc
let _lastCmpData = null;
let mapMode = new Set(["all"]);  // transit-type filter on the live-map view; Set for multi-select
// Agency filter on the live-map view (multi-agency clusters only, e.g. SF:
// BART / Muni / Caltrain / ...). Set for multi-select; "all" = every agency.
// Composes with mapMode (agency AND mode). Cached agency list for the current
// city so refreshes can rebuild the chips without an extra request.
let mapAgency = new Set(["all"]);
let cityAgencies = [];
// TASK 1 — historical agency filter for the stats panel. Sourced from
// /api/agencies (distinct stored agency strings seen in delay_obs), so an
// agency is selectable from HISTORY even with zero live vehicles right now.
// Single-select: null = all agencies. Distinct from mapAgency (the live-map
// namespace filter) because the stored delay_obs.agency column is a separate,
// history-meaningful identifier space.
let statsAgency = null;        // null = all; else a stored agency string
let statsAgencyOptions = [];   // ["BART", ...] for the current city
// TASK 2 — global earliest collection date (ISO) from /api/cities, used with
// each city's own `since` to decide which time-window buttons to render.
let collectingSince = null;
let userLocation = null;  // {lat, lon, source} — for nearest-city default
// "city" (default — bounded by the city bbox, so "Amsterdam" means Amsterdam)
// or "network" (the whole transit system — the entire Dutch feed). Mirrored
// from the toggle checkboxes on each view.
let scope = "city", cmpScope = "city";
let cmpSelected = new Set();
let cmpSearchQuery = "";
let view = "map", refreshTimer = null;

async function getJSON(path) {
  const r = await fetch(API + path);
  if (!r.ok) { const err = new Error(path + " -> " + r.status); err.status = r.status; throw err; }
  return r.json();
}

// Bootstrap resilience (F2-01): retry-with-backoff for the boot-critical
// fetches. A transient network/API blip must never leave a silently dead UI.
function _sleep(ms) { return new Promise((res) => setTimeout(res, ms)); }
async function getJSONRetry(path, attempts) {
  attempts = attempts || 3;
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try {
      // 12s per-attempt cap: a HUNG fetch (resolves neither way) must not
      // stall the retry loop — it counts as a failure and we go again.
      return await Promise.race([
        getJSON(path),
        _sleep(12000).then(() => { throw new Error(path + " -> timeout"); }),
      ]);
    }
    catch (e) { lastErr = e; if (i < attempts - 1) await _sleep(600 * Math.pow(2, i)); }
  }
  throw lastErr;
}

// A fetch failure is only "no data" when the API said 404. Anything else
// (5xx, network, timeout) is a service error and must be rendered as one
// (2026-10-23 QC #2: a 500 was being shown as "No on-time history").
function isNotFound(e) { return !!(e && e.status === 404); }
function serviceErrorNote(what, e, retryId) {
  var why = String((e && e.message) || e || "unknown error");
  return '<div class="coverage-empty-note" role="alert">'
    + '<strong>' + esc(what) + ' is temporarily unavailable.</strong> '
    + 'The server did not return it (' + esc(why) + '). The underlying history is intact; this is a service error, not missing data. '
    + '<button type="button" class="view-btn" id="' + esc(retryId) + '">Retry</button> '
    + '<a href="/methodology" class="view-link" style="padding:2px 8px">Methodology</a></div>';
}
function wireRetry(id, fn) {
  var b = document.getElementById(id);
  if (b) b.addEventListener("click", fn);
}

/* ─────────── client config + feature flags ─────────── */
// /api/config exposes only frontend-safe flags + the mode/window vocabularies.
// Cached briefly in sessionStorage so flag flips propagate without a hard reload.
let CONFIG = null;
async function loadConfig() {
  const KEY = "gts_config_v1", TTL = 5 * 60 * 1000;
  try {
    const cached = JSON.parse(sessionStorage.getItem(KEY) || "null");
    if (cached && cached.t && Date.now() - cached.t < TTL) { CONFIG = cached.c; return CONFIG; }
  } catch (e) { /* ignore bad cache */ }
  try {
    CONFIG = await getJSONRetry("/config", 3);
    try { sessionStorage.setItem(KEY, JSON.stringify({ t: Date.now(), c: CONFIG })); } catch (e) {}
  } catch (e) { CONFIG = CONFIG || { flags: {}, modes: [], windows: [] }; }
  return CONFIG;
}
function flag(name) { return !!(CONFIG && CONFIG.flags && CONFIG.flags[name]); }

/* ── Served UI vocabularies (F17 — nothing hardcoded) ──
   /api/config additionally exposes `grade_bands` ([{min_pct, letter}], from
   quality-config.json) and `windows` (the canonical ordered window list, also
   served via /api/windows). Both are consumed once at boot; the literals below
   are only a resilience fallback mirroring the shipped config, so the UI still
   renders against an older API during the contract transition. */
let GRADE_BANDS = [
  { min_pct: 90, letter: "A" }, { min_pct: 80, letter: "B" },
  { min_pct: 70, letter: "C" }, { min_pct: 60, letter: "D" },
  { min_pct: 0, letter: "F" },
];
function applyServedConfigVocab() {
  if (!CONFIG) return;
  if (Array.isArray(CONFIG.grade_bands) && CONFIG.grade_bands.length) {
    const bands = CONFIG.grade_bands
      .filter((b) => b && b.letter != null && b.min_pct != null &&
                     isFinite(Number(b.min_pct)))
      .map((b) => ({ min_pct: Number(b.min_pct), letter: String(b.letter) }))
      .sort((a, b) => b.min_pct - a.min_pct);
    if (bands.length) GRADE_BANDS = bands;
  }
}
// Canonical window keys as served by /api/config (`windows` may be a list of
// keys or of {key,…} objects); falls back to the /api/windows-derived WINDOWS.
function servedWindowKeys() {
  const w = CONFIG && CONFIG.windows;
  if (Array.isArray(w) && w.length) {
    return w.map((x) => (x && x.key) || x).filter((k) => typeof k === "string");
  }
  return WINDOWS.map(([k]) => k);
}

// 95% Wilson score interval lower bound (same formula/z as the methodology's
// documented ranking rule: leaderboards rank by the conservative lower bound,
// never the point estimate). k = successes, n = trials; returns a fraction.
function wilsonLower(k, n) {
  if (!n || n <= 0) return 0;
  const z = 1.96, p = k / n, z2 = z * z;
  const denom = 1 + z2 / n;
  const centre = p + z2 / (2 * n);
  const margin = z * Math.sqrt((p * (1 - p) + z2 / (4 * n)) / n);
  return (centre - margin) / denom;
}

/* ─────────── URL routing (path-based canonical permalinks) ─────────── */
// Caddy SPA-fallbacks unknown paths to index.html, so "/", "/city/{id}" and
// "/compare" are all real shareable URLs. setView() stays presentation-only;
// navigate()/applyRoute() are the entry points so back/forward and deep-links
// work. _routing guards against a selectCity<->navigate loop.
let _routing = false;
let cityAvailability = null;
function clearCityAvailability() {
  cityAvailability = null;
  const noindex = document.getElementById("gts-noindex");
  if (noindex) noindex.remove();
}
function currentRoute() {
  const path = location.pathname;
  let m;
  if (/^\/compare\/?$/.test(path)) return { view: "compare" };
  if (/^\/methodology\/?$/.test(path)) return { view: "methodology" };
  if ((m = path.match(/^\/headway\/([A-Za-z0-9_.-]+)\/?$/))) return { view: "headway", city: m[1] };
  if (/^\/headway\/?$/.test(path)) return { view: "headway" };
  if ((m = path.match(/^\/reliability\/([A-Za-z0-9_.-]+)\/?$/))) return { view: "reliability", city: m[1] };
  if (/^\/reliability\/?$/.test(path)) return { view: "reliability" };
  if ((m = path.match(/^\/data-quality\/([A-Za-z0-9_.-]+)\/?$/))) return { view: "dataquality", city: m[1] };
  if (/^\/data-quality\/?$/.test(path)) return { view: "dataquality" };
  if ((m = path.match(/^\/feeds\/([A-Za-z0-9_.-]+)\/?$/))) return { view: "feeds", city: m[1] };
  if (/^\/feeds\/?$/.test(path)) return { view: "feeds" };
  // City hub (2026-08-09): /city/{slug}[/reliability|/routes|/feeds] — each tab
  // of the selected city's hub is a real, deep-loadable URL.
  if ((m = path.match(/^\/city\/([A-Za-z0-9_.-]+)\/(reliability|routes|feeds)\/?$/)))
    return { view: "map", city: m[1], tab: m[2] };
  if ((m = path.match(/^\/city\/([A-Za-z0-9_.-]+)\/?$/))) return { view: "map", city: m[1], tab: "map" };
  return { view: "map" };
}
async function applyRoute() {
  _routing = true;
  clearCityAvailability();
  try {
    let r = currentRoute();
    // /feeds/{city} for a tracked city re-homes under the city hub as
    // /city/{city}/feeds — replaceState so the old URL keeps working as an
    // alias without minting an extra history entry. Untracked cities keep the
    // legacy standalone feed view.
    if (r.view === "feeds" && r.city && cities.find((c) => c.id === r.city)) {
      history.replaceState({}, "", "/city/" + r.city + "/feeds");
      r = { view: "map", city: r.city, tab: "feeds" };
    }
    if (r.view === "compare") { setView("compare"); return; }
    if (r.view === "methodology") { setView("methodology"); return; }
    if (r.view === "dataquality") {
      setView("dataquality");
      if (r.city) loadDataQualityCity(r.city); else loadDataQuality();
      return;
    }
    if (r.view === "feeds") {
      setView("feeds");
      if (r.city) loadFeedCity(r.city); else loadFeedDirectory();
      return;
    }
    if (r.view === "headway") {
      setView("headway");
      if (r.city && cities.find((c) => c.id === r.city)) current = r.city;
      loadHeadway(current);
      return;
    }
    if (r.view === "reliability") {
      setView("reliability");
      if (r.city && cities.find((c) => c.id === r.city)) current = r.city;
      // The board ranks history, but the catalog lists only cities live right
      // now (a Japanese network at 04:00 local is absent), so a ranked city's
      // own /reliability/{id} must load from the API, not fall back silently to
      // the leaderboard. An unknown id gets the report's honest 404 note.
      loadReliability(r.city || current);
      return;
    }
    // ── City hub tabs: /city/{slug}/{reliability|routes|feeds}. Deep-loading
    // any tab URL cold selects the city (full header/map state, so the Map tab
    // is instant afterwards) AND opens that tab. Reuses the existing
    // city-scoped renderers — no duplicated views. ──
    if (r.tab && r.tab !== "map") {
      if (cities.length && !cities.find((c) => c.id === r.city)) {
        setView("map");
        current = null;
        showCityNotFound(r.city);
        return;
      }
      // Start the full city load (map, vehicles, shapes, stats) but open the
      // requested tab NOW: its own small fetch must not queue behind a dense
      // city's map payload (Amsterdam's feed tab sat on "Loading…" 5-10 s).
      // selectCity sets `current` synchronously, before its first await.
      const selecting = (cities.length && r.city !== current) ? selectCity(r.city) : null;
      if (r.tab === "reliability") { setView("reliability"); loadReliability(r.city); }
      else if (r.tab === "feeds") { setView("feeds"); loadFeedCity(r.city); }
      else if (r.tab === "routes") { setView("cityroutes"); openCityRoutesTab(); }
      if (selecting) await selecting;
      return;
    }
    setView("map");
    let cityId = r.city;
    if (cityId && cities.length && !cities.find((c) => c.id === cityId)) {
      // Requested a specific city that isn't tracked: show a PERSISTENT not-found
      // state (not a fading toast) and do NOT silently load a default — the state
      // stays until the user picks a real city from the selector.
      current = null;
      showCityNotFound(cityId);
      return;
    }
    const target = cityId || current || ((pickDefaultCity() || {}).id);
    if (target && target !== current) await selectCity(target);
  } finally {
    _routing = false;
    updatePageSeo();   // F8 — every routed navigation refreshes title/canonical/description
    updateCityHub();   // city-hub tab strip follows every routed navigation
  }
}
function navigate(path, opts) {
  opts = opts || {};
  if (path === location.pathname && !opts.replace) { applyRoute(); return; }
  if (opts.replace) history.replaceState({}, "", path);
  else history.pushState({}, "", path);
  applyRoute();
}
window.addEventListener("popstate", applyRoute);

/* ── F8: per-route SEO — title, canonical link, meta description ──
   Every navigation (router, back/forward, direct selectCity/loadReliability
   pushes) updates all three, per view and per city. */
const DEFAULT_TITLE = document.title;
const DEFAULT_DESC = (document.querySelector('meta[name="description"]') || {}).content || "";
function updatePageSeo() {
  const SITE = "Global Transit Stats";
  const r = currentRoute();
  const cityId = r.city || (r.view === "map" ? current : null);
  const cityObj = cityId ? cities.find((c) => c.id === cityId) : null;
  const name = cityObj ? cityObj.name : "";
  let title = DEFAULT_TITLE, desc = DEFAULT_DESC;
  // City-hub tabs (/city/{slug}/…) get city-first titles distinct from the
  // matching global views' — e.g. "Oslo on-time reliability — Global Transit Stats".
  if (r.tab && r.tab !== "map" && name) {
    const CITY_TAB_SEO = {
      reliability: [
        name + " on-time reliability — " + SITE,
        name + " transit reliability report card: on-time performance by route, ranked from public GTFS-Realtime history.",
      ],
      routes: [
        name + " transit routes — " + SITE,
        name + " transit routes published in schedule and realtime feeds, with available live status and per-route detail.",
      ],
      feeds: [
        name + " feed health — " + SITE,
        "Feed uptime, staleness and vehicle counts for " + name + "'s public GTFS-Realtime feed.",
      ],
    };
    const seo = CITY_TAB_SEO[r.tab];
    if (seo) { title = seo[0]; desc = seo[1]; }
  } else switch (r.view) {
    case "compare":
      title = "Compare cities — " + SITE;
      desc = "Compare on-time performance, delays and live vehicle counts across tracked transit cities worldwide.";
      break;
    case "methodology":
      title = "Methodology — " + SITE;
      desc = "How Global Transit Stats turns public GTFS-Realtime feeds into on-time statistics — and the limits of those numbers.";
      break;
    case "dataquality":
      title = (name ? name + " data quality — " : "Data quality — ") + SITE;
      desc = name
        ? "Daily data-quality grades and feed-health history for " + name + "'s public transit feed."
        : "Data-quality grades for every monitored public transit feed: uptime, freshness and gradeable observations.";
      break;
    case "feeds":
      title = (name ? name + " feed health — " : "Feed health — ") + SITE;
      desc = name
        ? "Feed uptime, staleness and vehicle counts for " + name + "'s public GTFS-Realtime feed."
        : "Feed health directory: uptime, staleness and vehicle counts for every monitored public GTFS-Realtime feed.";
      break;
    case "headway":
      title = (name ? name + " headway & bunching — " : "Headway & bunching — ") + SITE;
      desc = name
        ? "How evenly transit vehicles are spaced in " + name + " — typical gaps and bunching, from live positions."
        : "How evenly transit vehicles are spaced, per route — typical gaps and bunching, from live vehicle positions.";
      break;
    case "reliability":
      title = (name ? name + " transit reliability — " : "Transit reliability leaderboard — ") + SITE;
      desc = name
        ? name + " transit reliability report card: on-time performance by route, ranked from public GTFS-Realtime history."
        : "On-time reliability leaderboard: the most and least reliable transit cities, ranked from public GTFS-Realtime history.";
      break;
    default: // live map (with or without a city)
      if (name) {
        title = name + " live transit map & on-time stats — " + SITE;
        desc = "Live " + name + " transit: real-time vehicle positions and on-time performance from the agency's public GTFS-Realtime feed.";
      }
  }
  // Availability metadata belongs to this exact route's lookup, not a previous city.
  if (cityAvailability && cityAvailability.city === r.city) {
    desc = cityAvailability.description;
    if (cityAvailability.known) title = r.city + " transit temporarily unavailable — " + SITE;
  }
  // A verified unknown city stays out of search indexes until navigation changes.
  if (document.getElementById("gts-noindex")) title = "City not found — " + SITE;
  document.title = title;
  let canon = document.querySelector('link[rel="canonical"]');
  if (!canon) {
    canon = document.createElement("link");
    canon.setAttribute("rel", "canonical");
    document.head.appendChild(canon);
  }
  // Same normalisation as the server-side template in index.html (Caddy
  // `templates`, QC 2026-10-23 #4): no trailing slash except the root, no query.
  var canonPath = location.pathname.replace(/\/+$/, "") || "/";
  var canonUrl = location.origin + canonPath;
  canon.setAttribute("href", canonUrl);
  var ogUrl = document.querySelector('meta[property="og:url"]');
  if (ogUrl) ogUrl.setAttribute("content", canonUrl);
  let md = document.querySelector('meta[name="description"]');
  if (!md) {
    md = document.createElement("meta");
    md.setAttribute("name", "description");
    document.head.appendChild(md);
  }
  md.setAttribute("content", desc);
}

function fmtDelay(sec) {
  if (sec === null || sec === undefined) return "—";
  sec = Math.round(sec);
  const sign = sec < 0 ? "-" : "+";
  const a = Math.abs(sec);
  if (a < 60) return sign + a + "s";
  const m = Math.floor(a / 60);
  if (m < 60) return sign + m + "m " + (a % 60) + "s";
  return sign + Math.floor(m / 60) + "h " + (m % 60) + "m";
}
function pct(v) { return v === null || v === undefined ? "—" : v + "%"; }
function titlecase(s) { return s ? s[0].toUpperCase() + s.slice(1) : s; }
// Long-form, locale-safe date ("19 May 2026" / "May 19, 2026") for the
// "collecting since" copy — never the ambiguous 5/19/2026 (F2-16).
function longDate(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch (e) { return String(iso); }
}

function previousIsoDay(iso) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(iso || ""))) return iso;
  const parts = iso.split("-").map(Number);
  const d = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2] - 1));
  return d.toISOString().slice(0, 10);
}

// Small, explicit interval label shared by every windowed response renderer.
// `effective_end` is exclusive; turn it into the last included agency-local
// calendar date for daily labels. Raw timestamps retain local wall time.
function effectiveWindowText(data) {
  if (!data) return "";
  if (data.timezone === "per-city") {
    return "Each result uses completed agency-local calendar dates in that agency's timezone.";
  }
  const tz = data.timezone || "UTC";
  const start = data.effective_start;
  const end = data.effective_end;
  if (data.partial_day) {
    const short = (v) => String(v || "").replace("T", " ").slice(0, 16);
    return `Effective window: ${short(start)}–${short(end)} · ${tz} · includes the current partial agency-local calendar date.`;
  }
  if (start && end) {
    return `Agency-local calendar dates: ${start}–${previousIsoDay(end)} · ${tz} · completed dates only.`;
  }
  if (end) {
    return `All completed agency-local calendar dates through ${previousIsoDay(end)} · ${tz}.`;
  }
  return `Agency-local calendar-date timezone: ${tz}.`;
}

/* ─── live-map filter predicates (agency AND mode compose; click-only) ─── */
// A vehicle/shape passes the mode filter when "all" is selected or its mode is
// in the selected set. It passes the agency filter when "all" is selected, or
// the city has no agency dimension (agency_id absent), or its agency_id is in
// the selected set. The two compose with AND — selecting an agency OR a mode
// shows ONLY the matching vehicles + routes and hides the rest.
function modeFilterPass(mode) {
  return mapMode.has("all") || mapMode.has(mode || "other");
}
function agencyFilterPass(agencyId) {
  if (mapAgency.has("all")) return true;
  if (!agencyId) return false;   // an agency is actively selected; untagged → hide
  return mapAgency.has(agencyId);
}

/* ─────────── view switching ─────────── */
const VIEW_IDS = ["map", "compare", "dataquality", "feeds", "headway", "reliability", "methodology", "cityroutes"];
// Keep the actual navigation controls (and their handlers) in one measured row.
// Overflow is a disclosure containing ordinary links/buttons, not a second menu API.
let primaryNav = null;
function refreshPrimaryNav() {
  if (primaryNav) primaryNav.refresh();
}
function initPrimaryNav() {
  const nav = document.querySelector(".views-scroll .views");
  if (!nav || primaryNav) return;
  const items = Array.from(nav.children).filter((el) => el.matches("button, a"));
  const more = document.createElement("button");
  more.type = "button";
  more.className = "nav-more";
  more.textContent = "More ▾";
  more.setAttribute("aria-expanded", "false");
  more.setAttribute("aria-controls", "primary-nav-more");
  const panel = document.createElement("div");
  panel.id = "primary-nav-more";
  panel.className = "nav-more-panel";
  panel.hidden = true;
  nav.append(more, panel);
  nav.classList.add("nav-overflow");
  let pending = 0;
  function close(restoreFocus) {
    panel.hidden = true;
    more.setAttribute("aria-expanded", "false");
    if (restoreFocus && !more.hidden) more.focus({ preventScroll: true });
  }
  function layout() {
    pending = 0;
    const focused = nav.contains(document.activeElement) ? document.activeElement : null;
    const wasOpen = !panel.hidden;
    // Put controls back in document order to measure their natural widths.
    items.forEach((item) => nav.insertBefore(item, more));
    panel.hidden = true;
    more.hidden = false;
    const visible = items.filter((item) => !item.hidden);
    const gap = parseFloat(getComputedStyle(nav).columnGap) || 0;
    const widths = new Map(visible.map((item) => [item, item.getBoundingClientRect().width]));
    const available = nav.clientWidth;
    const allWidth = visible.reduce((sum, item) => sum + widths.get(item), 0) + gap * Math.max(0, visible.length - 1);
    const keep = new Set();
    if (allWidth <= available) {
      visible.forEach((item) => keep.add(item));
      more.hidden = true;
    } else {
      let used = more.getBoundingClientRect().width;
      // Prefer the active destination; then fill remaining space in source order.
      const active = visible.find((item) => item.classList.contains("active"));
      const priority = active ? [active, ...visible.filter((item) => item !== active)] : visible;
      priority.forEach((item) => {
        if (used + gap + widths.get(item) <= available) {
          keep.add(item);
          used += gap + widths.get(item);
        }
      });
    }
    items.forEach((item) => {
      if (!item.hidden && !keep.has(item)) panel.appendChild(item);
      if (item.classList.contains("active")) item.setAttribute("aria-current", "page");
      else item.removeAttribute("aria-current");
    });
    panel.hidden = more.hidden || !wasOpen;
    more.setAttribute("aria-expanded", String(!panel.hidden));
    // The available vertical space is measured, so every destination remains scrollable.
    panel.style.maxHeight = Math.max(0, document.documentElement.clientHeight - nav.getBoundingClientRect().bottom - 12) + "px";
    if (focused) {
      const stillVisible = !focused.hidden && (!panel.contains(focused) || !panel.hidden);
      const target = stillVisible ? focused : (!more.hidden ? more : visible[0]);
      if (target) target.focus({ preventScroll: true });
    }
  }
  function refresh() {
    if (!pending) pending = requestAnimationFrame(layout);
  }
  function open() {
    if (more.hidden) return;
    panel.hidden = false;
    more.setAttribute("aria-expanded", "true");
  }
  more.addEventListener("click", () => panel.hidden ? open() : close(false));
  more.addEventListener("keydown", (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault(); open();
      const first = panel.querySelector("button:not([hidden]), a:not([hidden])");
      if (first) first.focus();
    }
  });
  nav.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !panel.hidden) {
      event.preventDefault(); close(true);
    }
  });
  panel.addEventListener("click", (event) => {
    if (event.target.closest("button, a")) close(false);
  });
  nav.addEventListener("focusout", (event) => {
    if (event.relatedTarget && !nav.contains(event.relatedTarget)) close(false);
  });
  document.addEventListener("click", (event) => {
    if (!nav.contains(event.target)) close(false);
  });
  const resize = new ResizeObserver(refresh);
  resize.observe(nav);
  window.addEventListener("resize", refresh, { passive: true });
  // Observe only original controls; layout's own disclosure changes cannot loop.
  const changes = new MutationObserver(refresh);
  items.forEach((item) => changes.observe(item, { attributes: true, attributeFilter: ["hidden", "class"], childList: true }));
  if (document.fonts) document.fonts.ready.then(refresh);
  primaryNav = { refresh };
  layout();
}

function setView(v) {
  view = v;
  // City-routes tab borrows the live route-browser/most-delayed cards from the
  // map panel (re-parented, never cloned); give them back on any other view.
  if (v === "cityroutes") moveRoutesCardsToTab(); else restoreRoutesCards();
  document.querySelectorAll(".view-btn").forEach((b) =>
    b.classList.toggle("active", b.dataset.view === v));
  refreshPrimaryNav();
  VIEW_IDS.forEach((name) => {
    const el = document.getElementById("view-" + name);
    if (el) el.hidden = v !== name;
  });
  const picker = document.querySelector(".city-picker");
  if (picker) picker.style.display = v === "map" ? "" : "none";
  // Single refresh timer, re-pointed per view so both map and compare stay live.
  if (refreshTimer) { clearInterval(refreshTimer); refreshTimer = null; }
  if (v === "map") {
    if (map) setTimeout(() => map.invalidateSize(), 50);
    if (current) refreshTimer = setInterval(loadVehicles, 20000);
  } else if (v === "compare") {
    loadCompare();
    // F6 — skip a refresh tick while the previous (possibly multi-batch,
    // heavy) load is still in flight, so ticks never stack requests.
    refreshTimer = setInterval(() => { if (!_cmpInFlight) loadCompare(true); }, 20000);
  }
}

/* ─────────── city hub sub-navigation (2026-08-09) ───────────
   "Load a city and select what you want": once a city is selected it becomes a
   hub with a persistent tab strip — Map / Reliability / Routes / Feed health —
   each tab a real URL (/city/{slug}[/reliability|/routes|/feeds]) with its own
   history entry. The tab set is DERIVED per city from what the API says it can
   serve (coverage tier + feature flags), never hardcoded: a timetable-only city
   has no realtime feed, so the reliability / feed-health tabs (which grade that
   feed) are simply not offered — honest tabs, no empty panes. */
const CITY_TABS = [
  { tab: "map",
    label: (c) => (c.coverage === "scheduled" ? "Timetable map" : "Live map"),
    path: (id) => "/city/" + encodeURIComponent(id),
    panel: "view-map",
    avail: () => true },
  { tab: "reliability",
    label: () => "Reliability",
    path: (id) => "/city/" + encodeURIComponent(id) + "/reliability",
    panel: "view-reliability",
    avail: (c) => flag("reliability_report") && c.coverage !== "scheduled" },
  { tab: "routes",
    label: (c) => (c.coverage === "scheduled" ? "Routes (timetable)" : "Routes"),
    path: (id) => "/city/" + encodeURIComponent(id) + "/routes",
    panel: "view-cityroutes",
    avail: () => true },
  { tab: "feeds",
    label: () => "Feed health",
    path: (id) => "/city/" + encodeURIComponent(id) + "/feeds",
    panel: "view-feeds",
    avail: (c) => flag("feed_health_page") && c.coverage !== "scheduled" },
];
// Rebuild the hub strip from the current URL + selected city. Runs after every
// routed navigation (applyRoute finally) and after direct selectCity() calls.
function updateCityHub() {
  // One primary navigation; city-specific routes use the selected city.
  const button = document.getElementById("city-routes-nav");
  if (!button) return;
  const cityId = currentRoute().city || current;
  const city = cities.find((item) => item.id === cityId);
  button.hidden = !city;
  button.textContent = city && city.coverage === "scheduled" ? "Timetable routes" : "Routes";
  button.title = city ? "Routes in " + city.name : "";
  refreshPrimaryNav();
}

/* /city/{slug}/routes — the route list as a first-class tab. The EXISTING live
   DOM cards (#route-browser-card catalog/detail + #delayed-routes-card) are
   re-parented into the tab (and back to the map panel on leave) via placeholder
   comment nodes, so their listeners, lazy catalog and detail state all survive —
   wrap, don't fork. */
const _hubCardHomes = {};   // card id -> placeholder comment marking its panel slot
function moveRoutesCardsToTab() {
  const host = document.getElementById("cityroutes-host");
  if (!host) return;
  ["route-browser-card", "delayed-routes-card"].forEach((id) => {
    const el = document.getElementById(id);
    if (!el || el.parentNode === host) return;
    if (!_hubCardHomes[id]) {
      _hubCardHomes[id] = document.createComment("home:" + id);
      el.parentNode.insertBefore(_hubCardHomes[id], el);
    }
    host.appendChild(el);
  });
}
function restoreRoutesCards() {
  ["route-browser-card", "delayed-routes-card"].forEach((id) => {
    const el = document.getElementById(id);
    const ph = _hubCardHomes[id];
    if (el && ph && ph.parentNode && el.parentNode !== ph.parentNode) {
      ph.parentNode.insertBefore(el, ph);
    }
  });
}
// Render/refresh the routes tab for the current city: heading + honest framing
// per coverage tier, then open the borrowed cards (opening the route-browser
// <details> fires its existing lazy rbLoadCatalog toggle handler).
function openCityRoutesTab() {
  const c = cities.find((x) => x.id === current);
  const title = document.getElementById("cityroutes-title");
  if (title) title.textContent = "Routes — " + ((c && c.name) || current || "");
  const note = document.getElementById("cityroutes-note");
  if (note) {
    note.textContent = (c && c.coverage === "scheduled")
      ? "Routes in this city's published timetable. No live tracking — the agency publishes no realtime feed, so there are no live positions or delay figures here."
      : "Routes present in the published schedule or observed realtime feed — running, idle or retired — plus the routes with the worst delays right now. Select a route for detail.";
  }
  const card = document.getElementById("route-browser-card");
  if (card) {
    if (!card.open) card.open = true;   // toggle handler lazy-loads the catalog
    else rbLoadCatalog();               // already open — ensure THIS city's catalog
  }
  const delayed = document.getElementById("delayed-routes-card");
  if (delayed && !delayed.open) delayed.open = true;
}

/* ─────────── live map view ─────────── */
function initMap() {
  map = L.map("map", { zoomControl: true, maxZoom: 20 }).setView([30, 0], 2);
  // Direct OpenStreetMap basemaps. No CARTO account, key, or tile dependency.
  // Browser caching and Referer are preserved; no tile prefetch/offline scraping.
  // Designed CalTopo-style: several base maps + semi-transparent overlays that
  // stack cleanly on ANY base (groundwork for future topo/terrain analysis).
  // Topo/imagery layers set maxNativeZoom + maxZoom so they UP-SCALE past their
  // native limit instead of going blank when a city is zoomed in close.
  const _att = {
    osm: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    esri: 'Tiles &copy; <a href="https://www.esri.com">Esri</a>',
    otm: 'Map data: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)',
  };
  const osmBase = (className = "") => L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
    maxNativeZoom: 19, maxZoom: 20, attribution: _att.osm,
    referrerPolicy: "strict-origin-when-cross-origin", className,
  });
  const bases = {
    // Default: clean but colourful, streets + parks + water — transit overlays
    // (bright route lines, mode-coloured dots) still read clearly on top.
    "Streets": osmBase("gts-osm-streets"),
    "Topographic": L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}",
      { maxNativeZoom: 19, maxZoom: 20, attribution: _att.esri }),
    "Outdoors (topo + relief)": L.tileLayer("https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
      { subdomains: "abc", maxNativeZoom: 17, maxZoom: 20, attribution: _att.otm }),
    "Satellite": L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      { maxNativeZoom: 19, maxZoom: 20, attribution: _att.esri }),
    "OpenStreetMap": osmBase(),
    "Light": osmBase("gts-osm-light"),
    "Dark": osmBase("gts-osm-dark"),
  };
  // Stackable overlays (default off). Hillshade relief reads under transit on any
  // base — the seed of CalTopo-style terrain overlays (slope/contours can join here).
  const overlays = {
    "Hillshade relief": L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Elevation/World_Hillshade/MapServer/tile/{z}/{y}/{x}",
      { maxNativeZoom: 19, maxZoom: 20, opacity: 0.4, attribution: _att.esri }),
    // OpenStreetMap already includes place labels; no separate CARTO overlay.
  };
  // CalTopo-style slope-angle shading (terrain-engine.js). Self-contained
  // GridLayer that decodes AWS Terrarium DEM tiles + paints the CalTopo palette;
  // it manages its own colour legend on add/remove. Created lazily so we only
  // touch the DEM source when the overlay actually exists.
  if (window.GTS_TERRAIN && typeof window.GTS_TERRAIN.slopeLayer === "function") {
    overlays["Slope-angle shading"] = window.GTS_TERRAIN.slopeLayer();
  }
  // ── Free vs Pro split (designed with the product stand-in) ──
  // Free stays a complete live-transit map; Pro = terrain/satellite/overlays +
  // (roadmap) analysis tools. Per the online "free/full, pricing hidden" posture
  // Pro is DEMOABLE now — a 30-second preview + contact-to-buy, never hard-locked.
  const FREE_BASES = ["Streets", "Light", "Dark", "OpenStreetMap"];
  const PRO_BASES = ["Topographic", "Outdoors (topo + relief)", "Satellite"];
  const PRO_OVERLAYS = ["Hillshade relief", "Slope-angle shading", "Place labels"]
    .filter(function (n) { return overlays[n]; });
  // Pro analysis tools. Each carries its backend entitlement key (see
  // /api/v1/license entitlements map) so the click can be gated two-sidedly via
  // window.GTS_ENT.ensure(): a locked click opens the trial/upgrade interstitial
  // instead of the raw mailto; an entitled click passes through.
  const PRO_TOOLS = [
    { key: "terrain-profile", label: "Route terrain profile" },
    { key: "measure-distance", label: "Measure distance" },
    { key: "coverage-shading", label: "Coverage / reachable-area shading" },
    { key: "reliability-terrain", label: "Reliability × terrain overlay" },
    { key: "evidence-map", label: "Export / print evidence map" },
  ];
  // Terrain tools that are DEMOABLE now — clicking launches the real feature
  // (via terrain-engine.js) even for free users, matching the 30s Pro-base
  // preview posture. A non-blocking Pro affordance is shown alongside unless the
  // user is entitled. Keys NOT listed here keep the classic locked interstitial.
  const TERRAIN_TOOLS = {
    "terrain-profile": function () { launchTerrainProfile(); },
    "measure-distance": function () {
      if (window.GTS_TERRAIN && map) { window.GTS_TERRAIN.measureDistance(map); }
    },
    "evidence-map": function () {
      if (window.GTS_TERRAIN && map) { window.GTS_TERRAIN.exportMapPNG(map); }
    },
    // Stateful toggles (implemented below at module level): a `false` return
    // means "this click DEactivated the tool" — trigger() then skips the
    // upsell nudge, so turning a tool off never re-pitches Pro.
    "coverage-shading": function () { return toggleCoverageShading(); },
    "reliability-terrain": function () { return toggleReliabilityTerrain(overlays["Hillshade relief"]); },
  };
  // Pick a representative route polyline for the current city (the visible route
  // with the most points) and draw its terrain profile in a floating panel.
  async function launchTerrainProfile() {
    if (!window.GTS_TERRAIN) { showToast("Terrain engine unavailable.", "error"); return; }
    let best = null, bestLen = 0;
    if (routeLayer) {
      routeLayer.eachLayer(function (ln) {
        if (ln && typeof ln.getLatLngs === "function") {
          let lls = ln.getLatLngs();
          if (lls.length && Array.isArray(lls[0])) { lls = lls[0]; } // multi-segment
          if (lls.length > bestLen) { bestLen = lls.length; best = lls; }
        }
      });
    }
    if (!best || bestLen < 2) {
      showToast(currentNoGeometry
        ? "This city's feed has stops and timetables but no route-line geometry, so a terrain profile can't be drawn here. Try a city that shows route lines on the map."
        : "No route geometry loaded yet — pick a city and let its routes load, then try again.");
      return;
    }
    // Scheduled-geometry cities publish a stop list — snap the ones along this
    // route onto the profile. Only these cities have the /public/scheduled endpoint,
    // so gate the fetch on the geometry source: live-tier route-geometry cities
    // skip it (avoids a benign-but-noisy 404) and render the profile without stops.
    let stops = null;
    if (current && currentGeometrySource === "scheduled") {
      try {
        const sd = await getJSON(`/public/scheduled/${current}`);
        if (sd && Array.isArray(sd.stops)) { stops = sd.stops; }
      } catch (e) { /* no schedule feed for this city — profile renders stopless */ }
    }
    // Title carries the city's display name, never the slug (F2-09).
    const cityObj = cities.find(function (c) { return c.id === current; });
    const title = cityObj ? "Route terrain profile — " + cityObj.name : "Route terrain profile";
    window.GTS_TERRAIN.openProfilePanel(best, { title: title, map: map, stops: stops, geometrySource: currentGeometrySource });
  }
  const CONTACT = "mailto:support@globaltransitstats.com?subject=Public%20Transit%20Stats%20Pro%20%E2%80%94%20interest";

  // Every free style uses direct OpenStreetMap tiles. Dark/light styling affects
  // only the basemap images, never transit markers, routes, or attribution.
  let activeBase = "Streets";
  try {
    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) activeBase = "Dark";
  } catch (e) {}
  try { const s = localStorage.getItem("gts_basemap"); if (s && FREE_BASES.indexOf(s) !== -1) activeBase = s; } catch (e) {}
  bases[activeBase].addTo(map);

  // Zoom-reactive presentation: dot diameters (CSS classes on #map) + route
  // line weights re-derive on every zoom change (F2-03/F2-07).
  map.on("zoomend", function () { updateMapZoomClass(); restyleRouteLines(); });
  updateMapZoomClass();
  // F4 — suspend the marker-migration transition (vehicle.css) around zoom /
  // view changes, including NON-animated jumps (city switch, big zoom-outs),
  // so dots never sweep across the screen when the projection changes; the
  // 1.5s glide is only for genuine per-poll GPS movement.
  const _mapContainer = document.getElementById("map");
  const _vehNoTransOn = function () { if (_mapContainer) _mapContainer.classList.add("veh-notrans"); };
  const _vehNoTransOff = function () {
    if (!_mapContainer) return;
    // Two frames: let the post-zoom marker transforms land untransitioned first.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { _mapContainer.classList.remove("veh-notrans"); });
    });
  };
  map.on("zoomstart viewreset movestart", _vehNoTransOn);
  map.on("zoomend moveend", _vehNoTransOff);
  // Esc clears an active route focus (complements clicking empty map / ×).
  // F23 — scoped: while the city picker is open, Escape belongs to the picker
  // (its own document handler closes it); never close the picker AND clear the
  // route focus on the same keypress. This handler registers first (boot order:
  // initMap before initCityPicker), so it reads the picker's pre-close state.
  document.addEventListener("keydown", function (ev) {
    if (ev.key !== "Escape" || !highlightedRoute) return;
    const cityPop = document.getElementById("citysel-pop");
    if (cityPop && !cityPop.hidden) return;
    clearRouteHighlight();
  });

  if (!document.getElementById("gts-mapctl-css")) {
    const st = document.createElement("style"); st.id = "gts-mapctl-css";
    st.textContent =
      // One visual family for every map control (zoom, layers, legend):
      // white, 1px hairline, 8px radius, one shared shadow (map plan #5).
      ".leaflet-touch .leaflet-bar,.leaflet-bar{border:1px solid #d9dee6;border-radius:8px;box-shadow:0 1px 6px rgba(16,24,40,.18);overflow:hidden}" +
      ".leaflet-bar a{border-bottom-color:#e8ecf1;color:#1f2937}" +
      ".leaflet-touch .leaflet-bar a:first-child{border-top-left-radius:7px;border-top-right-radius:7px}" +
      ".leaflet-touch .leaflet-bar a:last-child{border-bottom-left-radius:7px;border-bottom-right-radius:7px}" +
      ".gts-layerctl{background:#fff;border:1px solid #d9dee6;border-radius:8px;box-shadow:0 1px 6px rgba(16,24,40,.18);font:13px/1.3 system-ui,sans-serif;overflow:hidden}" +
      ".glc-toggle{border:0;background:#fff;padding:7px 11px;cursor:pointer;font-weight:600;color:#1f2937;width:100%;text-align:left;display:flex;align-items:center;gap:7px;font:inherit;font-weight:600}" +
      ".glc-toggle:hover{background:#f3f4f6}.glc-toggle .gi{flex:none;color:#5c6776}" +
      ".glc-body{padding:6px 8px 10px;max-height:72vh;overflow:auto;overscroll-behavior:contain;min-width:196px}" +
      // Mobile: the control lives inside a map that is only a slice of the page, so
      // a 72vh panel overflows into the bottom OSM attribution and its lower tools
      // (Route terrain profile) become untappable. Cap the height on small screens
      // and lift the top-right control above the attribution corner so taps land.
      "@media (max-width:640px){.glc-body{max-height:min(46vh,calc(100% - 52px))}}" +
      ".leaflet-top.leaflet-right{z-index:1001}" +
      ".glc-sec{display:flex;align-items:center;gap:6px;font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:#6b7280;margin:9px 2px 3px;font-weight:700}" +
      ".glc-row{display:flex;align-items:center;gap:7px;padding:4px 5px;border-radius:6px;cursor:pointer}" +
      ".glc-row:hover{background:#f3f4f6}.glc-row input{margin:0;cursor:pointer;accent-color:#0a7ea4}.glc-name{flex:1;color:#111827}" +
      // Pro rows read quieter than the free ones — the section header carries
      // the single Pro pill (F2-10); no per-row pills, no lock glyphs.
      ".glc-pro .glc-name{color:#6b7280}" +
      ".pro-pill{background:#8f4f2d;color:#fff;font-size:9.5px;font-weight:800;letter-spacing:.03em;padding:1px 6px;border-radius:9px;text-transform:uppercase}" +
      ".glc-tools{display:flex;flex-direction:column;gap:2px}" +
      ".glc-tool{display:flex;align-items:center;gap:7px;padding:4px 5px;border-radius:6px;color:#6b7280;cursor:pointer;font-size:12px}" +
      ".glc-tool:hover{background:#fdf3ec;color:#374151}" +
      ".glc-tool:focus{outline:2px solid #0a7ea4;outline-offset:-2px}" +
      ".glc-tool-name{flex:1}" +
      ".glc-upsell,.leaflet-container a.glc-upsell{display:block;margin:10px 2px 1px;text-align:center;background:#8f4f2d;color:#fff;padding:7px;border-radius:6px;text-decoration:none;font-weight:600}" +
      ".glc-upsell:hover,.leaflet-container a.glc-upsell:hover{background:#744124}" +
      ".pro-preview-card{position:absolute;left:50%;bottom:22px;transform:translateX(-50%);z-index:1200;background:#fff;border-radius:10px;box-shadow:0 4px 18px rgba(0,0,0,.25);padding:11px 14px;font:13px/1.35 system-ui,sans-serif;max-width:330px;border:1px solid #e7c8ab}" +
      ".ppc-h{font-weight:700;color:#1f2937;display:flex;align-items:center;gap:7px;margin-bottom:3px}" +
      ".ppc-b{color:#4b5563;margin-bottom:9px}.ppc-count{font-weight:700;color:#92400e}" +
      ".ppc-actions{display:flex;gap:8px}.ppc-cta,.leaflet-container a.ppc-cta{flex:1;text-align:center;background:#8f4f2d;color:#fff;padding:7px;border-radius:6px;text-decoration:none;font-weight:600}" +
      ".ppc-cta:hover,.leaflet-container a.ppc-cta:hover{background:#744124}.ppc-x{background:#f3f4f6;border:0;border-radius:6px;padding:7px 10px;cursor:pointer;color:#374151}" +
      // Floating route chip (top-center) — the highlight explains itself.
      "#route-chip{position:absolute;top:12px;left:50%;transform:translateX(-50%);z-index:1150;display:flex;align-items:center;gap:9px;background:#fff;border:1px solid #d9dee6;border-radius:10px;box-shadow:0 4px 18px rgba(16,24,40,.18);padding:7px 8px 7px 9px;font:12px/1.3 system-ui,sans-serif;max-width:min(430px,calc(100% - 24px))}" +
      "#route-chip .rc-badge{flex:none;color:#fff;font-weight:800;font-size:12px;border-radius:6px;padding:3px 8px;text-shadow:0 0 2px rgba(0,0,0,.55)}" +
      "#route-chip .rc-body{display:flex;flex-direction:column;min-width:0}" +
      "#route-chip .rc-name{font-weight:600;color:#1b2330;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}" +
      "#route-chip .rc-sub{color:#6b7280;font-size:11px;white-space:nowrap}" +
      "#route-chip .rc-x{flex:none;border:0;background:#f3f4f6;border-radius:6px;width:22px;height:22px;line-height:1;font-size:14px;color:#374151;cursor:pointer}" +
      "#route-chip .rc-x:hover{background:#e5e7eb}" +
      // Map-corner legend (bottom-left, collapsed by default — map plan #5).
      ".gts-legendctl .glc-body{min-width:188px;padding:8px 11px 11px}" +
      ".gts-legend-sub{font-size:10.5px;text-transform:uppercase;letter-spacing:.04em;color:#6b7280;font-weight:700;margin:7px 0 4px}" +
      ".gts-legend-sub:first-child{margin-top:0}" +
      ".gts-legend-row{display:flex;align-items:center;gap:7px;padding:2px 0;color:#374151;font-size:12px}" +
      ".gts-legend-row .gi{flex:none;color:#5c6776}" +
      ".gts-legend-dot{width:10px;height:10px;border-radius:50%;flex:none}" +
      ".gts-legend-line{width:16px;height:3px;border-radius:2px;flex:none}" +
      ".gts-legend-wrap{display:flex;flex-wrap:wrap;gap:2px 12px}" +
      // Quiet the basemap's saturated arterials a notch so the transit layer
      // owns the saturated colour (map plan #10).
      ".leaflet-tile-pane{filter:saturate(.88)}" +
      // Slope-angle analysis blends with (instead of hiding) the streets.
      ".gts-slope-tiles{mix-blend-mode:multiply}" +
      // Coverage-shading + reliability×terrain legends (reuse the slope-legend
      // family from style.css; these add a close × and the radius toggle).
      ".gts-cov-legend,.gts-rel-legend{position:relative;padding-right:24px;max-width:236px}" +
      ".gts-legend-x{position:absolute;top:5px;right:5px;border:0;background:transparent;font-size:16px;line-height:1;cursor:pointer;color:#5c6776;padding:0 2px}" +
      ".gts-legend-x:hover{color:#1f2937}" +
      ".gcl-btns{display:flex;gap:5px;margin:6px 0 2px}" +
      ".gcl-btn{border:1px solid #d9dee6;background:#fff;border-radius:6px;padding:2px 8px;cursor:pointer;font:inherit;font-size:11px;color:#374151}" +
      ".gcl-btn[aria-pressed=true]{background:#0a7ea4;border-color:#0a7ea4;color:#fff}" +
      ".gts-legend-note{color:#6b7280;font-size:10.5px;margin-top:5px;line-height:1.35}" +
      // "on" pill for the two toggleable analysis tools in the Layers panel.
      ".glc-on{background:#0a7ea4;color:#fff;font-size:9.5px;font-weight:800;letter-spacing:.03em;padding:1px 6px;border-radius:9px;text-transform:uppercase;flex:none}";
    document.head.appendChild(st);
  }

  let pv = { timer: null, layer: null, isBase: false, prevBase: null };
  function setBase(name) {
    Object.keys(bases).forEach((k) => { if (map.hasLayer(bases[k])) map.removeLayer(bases[k]); });
    bases[name].addTo(map); bases[name].bringToBack();
  }
  function clearPreview(revert) {
    if (pv.timer) { clearInterval(pv.timer); pv.timer = null; }
    const c = document.getElementById("pro-preview-card"); if (c) c.remove();
    if (pv.layer) {
      if (pv.isBase) { if (revert) setBase(pv.prevBase || activeBase); }
      else if (map.hasLayer(pv.layer)) { map.removeLayer(pv.layer); }
    }
    pv = { timer: null, layer: null, isBase: false, prevBase: null };
    renderPanel();
  }
  function intent(name) {
    try { fetch("/api/public/pro-intent?layer=" + encodeURIComponent(name), { method: "POST", keepalive: true }).catch(function () {}); } catch (e) {}
  }
  function showCard(name) {
    // One-floating-card rule (F2-20): a new preview retires any tool upsell.
    const oldUp = document.getElementById("pro-tool-upsell"); if (oldUp) oldUp.remove();
    const old = document.getElementById("pro-preview-card"); if (old) old.remove();
    const card = document.createElement("div"); card.id = "pro-preview-card"; card.className = "pro-preview-card";
    card.innerHTML = '<div class="ppc-h"><span class="pro-pill">Pro</span> Terrain preview</div>' +
      '<div class="ppc-b">' + esc(name) + ' — preview active. Pro unlocks terrain, satellite &amp; analysis layers across all cities.</div>' +
      '<div class="ppc-actions"><a class="ppc-cta" href="' + CONTACT + '">Contact to buy</a>' +
      '<button class="ppc-x" type="button">Keep free map</button></div>';
    document.getElementById("map").appendChild(card);
    // Keep the card's actions reachable: lift it above whatever it would sit
    // under at the bottom of the map (the mobile stats sheet, the legend), then
    // close an open Layers panel it still covers (same rule as the terrain tools).
    const area = map.getContainer().getBoundingClientRect();
    const first = card.getBoundingClientRect();
    let lift = 22;
    [document.getElementById("panel"), map.getContainer().querySelector(".gts-legendctl")].forEach(function (el) {
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.width && r.left < first.right && r.right > first.left && r.top < area.bottom && r.bottom > area.top) {
        lift = Math.max(lift, Math.round(area.bottom - r.top) + 8);
      }
    });
    card.style.bottom = lift + "px";
    if (panelOpen && panelEl) {
      const placed = card.getBoundingClientRect(), p = panelEl.getBoundingClientRect();
      if (p.left < placed.right && p.right > placed.left && p.top < placed.bottom && p.bottom > placed.top) {
        const tog = panelEl.querySelector(".glc-toggle");
        if (tog) tog.click();
      }
    }
    L.DomEvent.disableClickPropagation(card);
    card.querySelector(".ppc-x").addEventListener("click", function () { clearPreview(true); });
    card.querySelector(".ppc-cta").addEventListener("click", function () { intent("contact:" + name); });
    // Auto-revert after 30s (no visible countdown — the artificial timer read dev-like).
    pv.timer = setTimeout(function () { clearPreview(true); }, 30000);
  }
  function startPreview(name, isBase) {
    clearPreview(true); intent(name);
    pv.isBase = isBase; pv.layer = isBase ? bases[name] : overlays[name];
    if (isBase) { pv.prevBase = activeBase; setBase(name); } else { overlays[name].addTo(map); }
    showCard(name); renderPanel();
  }

  // Non-blocking Pro affordance for the DEMOABLE terrain tools: the feature has
  // already launched; this is just the upsell nudge (dismissible, auto-fades),
  // never a lock. Entitled users never see it (checked by the caller).
  function showToolUpsell(label) {
    // One-floating-card rule (F2-20): launching a tool retires the layer
    // preview card so overlays never stack.
    const oldPv = document.getElementById("pro-preview-card"); if (oldPv) oldPv.remove();
    const old = document.getElementById("pro-tool-upsell");
    if (old) old.remove();
    const card = document.createElement("div");
    card.id = "pro-tool-upsell";
    card.className = "pro-preview-card";
    card.innerHTML = '<div class="ppc-h"><span class="pro-pill">Pro terrain</span> ' + esc(label) + '</div>' +
      '<div class="ppc-b">Live preview — DEM-sampled from public elevation tiles. Pro unlocks terrain profiles, slope-angle shading &amp; evidence-map export across every tracked city.</div>' +
      '<div class="ppc-actions"><a class="ppc-cta" href="' + CONTACT + '">Contact to buy</a>' +
      '<button class="ppc-x" type="button">Dismiss</button></div>';
    document.getElementById("map").appendChild(card);
    L.DomEvent.disableClickPropagation(card);
    const remove = function () { if (card.parentNode) card.parentNode.removeChild(card); };
    card.querySelector(".ppc-x").addEventListener("click", remove);
    card.querySelector(".ppc-cta").addEventListener("click", function () { intent("contact:tool:" + label); });
    setTimeout(remove, 12000);
  }

  let panelEl = null, panelOpen = false;
  function fitLayersPanel() {
    if (!panelEl || !panelOpen) return;
    const body = panelEl.querySelector(".glc-body");
    const area = map.getContainer().getBoundingClientRect();
    const rect = body.getBoundingClientRect();
    let bottom = Math.min(area.bottom, window.innerHeight);
    // Keep the map attribution visible and the mobile statistics sheet usable.
    const attribution = map.getContainer().querySelector(".leaflet-control-attribution");
    if (attribution) {
      const edge = attribution.getBoundingClientRect();
      if (edge.top > rect.top) bottom = Math.min(bottom, edge.top);
    }
    const statistics = document.getElementById("panel");
    if (statistics) {
      const sheet = statistics.getBoundingClientRect();
      if (sheet.left < rect.right && sheet.right > rect.left && sheet.top > rect.top) {
        bottom = Math.min(bottom, sheet.top);
      }
    }
    const border = parseFloat(getComputedStyle(panelEl).borderBottomWidth) || 0;
    body.style.boxSizing = "border-box";
    body.style.maxHeight = Math.max(0, bottom - rect.top - border) + "px";
  }
  function rowHtml(name, kind) {
    const pro = kind !== "free-base";
    const on = kind === "pro-ovl" ? map.hasLayer(overlays[name]) : map.hasLayer(bases[name]);
    return '<label class="glc-row' + (pro ? " glc-pro" : "") + '" data-name="' + esc(name) + '" data-kind="' + kind + '">' +
      '<input type="' + (kind === "pro-ovl" ? "checkbox" : "radio") + '" ' + (on ? "checked" : "") + '>' +
      '<span class="glc-name">' + esc(name) + '</span></label>';
  }
  function renderPanel() {
    // ONE Pro pill per section header (F2-10): rows themselves stay quiet —
    // muted text, no per-row pills, no lock glyphs.
    if (!panelEl) return;
    const body = panelEl.querySelector(".glc-body"); if (!body) return;
    let h = '<div class="glc-sec">Base map</div>';
    FREE_BASES.forEach(function (n) { h += rowHtml(n, "free-base"); });
    h += '<div class="glc-sec">Imagery &amp; topo <span class="pro-pill">Pro</span></div>';
    PRO_BASES.forEach(function (n) { h += rowHtml(n, "pro-base"); });
    h += '<div class="glc-sec">Terrain overlays <span class="pro-pill">Pro</span></div>';
    PRO_OVERLAYS.forEach(function (n) { h += rowHtml(n, "pro-ovl"); });
    h += '<div class="glc-sec">Analysis tools <span class="pro-pill">Pro</span></div>';
    h += '<div class="glc-tools">';
    PRO_TOOLS.forEach(function (t) {
      // The two overlay tools are stateful toggles — surface on/off both to
      // assistive tech (aria-pressed) and visually (the small "on" pill).
      const toggleable = t.key === "coverage-shading" || t.key === "reliability-terrain";
      const isOn = (t.key === "coverage-shading" && covShade.active) ||
                   (t.key === "reliability-terrain" && relTerrain.active);
      h += '<div class="glc-tool" role="button" tabindex="0"' +
        (toggleable ? ' aria-pressed="' + (isOn ? "true" : "false") + '"' : "") +
        ' data-key="' + esc(t.key) + '" data-label="' + esc(t.label) + '">' +
        '<span class="glc-tool-name">' + esc(t.label) + '</span>' +
        (isOn ? '<span class="glc-on">on</span>' : "") + '</div>';
    });
    h += '</div><a class="glc-upsell" href="' + CONTACT + '">Unlock Pro — contact to buy</a>';
    body.innerHTML = h;
    body.querySelectorAll(".glc-row").forEach(function (lab) {
      lab.addEventListener("click", function (ev) {
        ev.preventDefault();
        const name = lab.getAttribute("data-name"), kind = lab.getAttribute("data-kind");
        if (kind === "free-base") { clearPreview(false); activeBase = name; setBase(name); try { localStorage.setItem("gts_basemap", name); } catch (e) {} renderPanel(); }
        else if (kind === "pro-base") { startPreview(name, true); }
        else { if (map.hasLayer(overlays[name])) clearPreview(true); else startPreview(name, false); }
      });
    });
    body.querySelectorAll(".glc-tool").forEach(function (t) {
      function trigger() {
        const key = t.getAttribute("data-key");
        const label = t.getAttribute("data-label");
        intent("tool:" + label);
        // DEMOABLE terrain tools: launch the real feature now (so it's visible +
        // verifiable), and show a non-blocking Pro upsell unless entitled. These
        // are never hard-locked behind the interstitial.
        if (TERRAIN_TOOLS[key]) {
          // Close the Layers panel first so it doesn't stack under the terrain
          // panel + upsell (the mobile 3-overlay collision ChatGPT flagged).
          const _tog = document.querySelector(".glc-toggle");
          const _body = document.querySelector(".glc-body");
          if (_tog && _body && _body.style.display !== "none") { _tog.click(); }
          const res = TERRAIN_TOOLS[key]();
          const entitled = window.GTS_ENT && typeof window.GTS_ENT.has === "function" && window.GTS_ENT.has(key);
          // res === false ⇔ a toggle tool was just turned OFF — no upsell then.
          if (!entitled && res !== false) { showToolUpsell(label); }
          return;
        }
        // Other tools stay two-sided gated: entitled passes through; otherwise
        // the trial/upgrade interstitial opens. Falls back to contact-to-buy only
        // if the entitlements client failed to load at all.
        if (window.GTS_ENT && typeof window.GTS_ENT.ensure === "function") {
          window.GTS_ENT.ensure(key, label);
        } else {
          window.location.href = CONTACT;
        }
      }
      t.addEventListener("click", trigger);
      t.addEventListener("keydown", function (ev) {
        if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); trigger(); }
      });
    });
  }
  // Let the module-level overlay toggles refresh the panel's aria-pressed /
  // "on" pills without owning the closure-scoped renderPanel themselves.
  _layersPanelRefresh = renderPanel;
  const ctl = L.control({ position: "topright" });
  ctl.onAdd = function () {
    const c = L.DomUtil.create("div", "gts-layerctl");
    c.innerHTML = '<button class="glc-toggle" type="button" title="Map layers" aria-expanded="false">' +
      uiIcon("layers", 13) + ' Layers</button><div class="glc-body" style="display:none"></div>';
    panelEl = c;
    L.DomEvent.disableClickPropagation(c); L.DomEvent.disableScrollPropagation(c);
    c.querySelector(".glc-toggle").addEventListener("click", function () {
      panelOpen = !panelOpen;
      c.querySelector(".glc-body").style.display = panelOpen ? "block" : "none";
      this.setAttribute("aria-expanded", panelOpen ? "true" : "false");
      fitLayersPanel();
    });
    return c;
  };
  ctl.addTo(map);
  const layersResize = new ResizeObserver(fitLayersPanel);
  layersResize.observe(map.getContainer());
  const statisticsPanel = document.getElementById("panel");
  if (statisticsPanel) layersResize.observe(statisticsPanel);
  window.addEventListener("resize", fitLayersPanel);
  window.addEventListener("scroll", fitLayersPanel, { passive: true });
  map.on("unload", function () {
    layersResize.disconnect();
    window.removeEventListener("resize", fitLayersPanel);
    window.removeEventListener("scroll", fitLayersPanel);
  });
  renderPanel();
  addLegendControl();
  // routeLayer must be added BEFORE vehicleLayer so polylines render below
  // the vehicle markers.
  routeLayer = L.layerGroup().addTo(map);
  // Tier-B scheduled-stops layer (canvas-rendered: a scheduled city can have
  // thousands of stop dots, and SVG would be slow). Below vehicles/routes.
  scheduledLayer = L.layerGroup().addTo(map);
  // tolerance gives a click-slop around each tiny stop dot so they're easy to hit.
  scheduledRenderer = L.canvas({ padding: 0.5, tolerance: 6 });
  vehicleLayer = L.layerGroup().addTo(map);
}

/* ─────────── route highlighting (TASK D/E) ─────────── */
// Click (never hover) a polyline, a vehicle marker, or a worst/best-routes row
// to emphasize one route and dim the rest. Click empty map or the same route
// again to clear. Matching key is route_id (one route → many shape polylines).
function highlightRoute(routeId) {
  if (!routeLayer || !routeIndex[routeId] || !routeIndex[routeId].length) return;
  highlightedRoute = routeId;
  routeLayer.eachLayer(function (ln) {
    if (typeof ln.setStyle !== "function") return;
    if (routeIndex[routeId].indexOf(ln) !== -1) {
      // Focused: casing stays a halo under the bold line.
      if (ln._tpIsCasing) ln.setStyle({ weight: 9.5, opacity: 1 });
      else ln.setStyle({ weight: 7, opacity: 1 });
      if (typeof ln.bringToFront === "function") ln.bringToFront();
    } else {
      ln.setStyle({ opacity: ln._tpIsCasing ? 0.04 : 0.15 });
    }
  });
  // Dim vehicle dots that aren't on the focused route so the selection reads
  // clearly (focused route's dots stay fully opaque, everything else fades).
  if (vehicleLayer && typeof vehicleLayer.eachLayer === "function") {
    vehicleLayer.eachLayer(function (mk) {
      if (typeof mk.setOpacity === "function") {
        mk.setOpacity(mk._tpRouteId === routeId ? 1 : 0.25);
      }
    });
  }
  showRouteChip(routeId);
}

function clearRouteHighlight() {
  if (!routeLayer) return;
  highlightedRoute = null;
  hideRouteChip();
  routeLayer.eachLayer(function (ln) {
    if (ln._tpBase && typeof ln.setStyle === "function") {
      ln.setStyle({ color: ln._tpBase.color, weight: ln._tpBase.weight, opacity: ln._tpBase.opacity });
    }
  });
  // Restore every vehicle dot to full opacity.
  if (vehicleLayer && typeof vehicleLayer.eachLayer === "function") {
    vehicleLayer.eachLayer(function (mk) {
      if (typeof mk.setOpacity === "function") mk.setOpacity(1);
    });
  }
  // The base-style restore above wiped the reliability tint — re-apply it.
  relTerrainReapply();
}

/* ── Floating route chip: the highlight explains itself (map plan #4) ── */
function showRouteChip(routeId) {
  const mapEl = document.getElementById("map");
  if (!mapEl) return;
  const meta = routeMeta[routeId] || { name: routeId, long: "", mode: null };
  let n = 0;
  if (vehicleLayer && typeof vehicleLayer.eachLayer === "function") {
    vehicleLayer.eachLayer(function (mk) { if (mk._tpRouteId === routeId) n++; });
  }
  let chip = document.getElementById("route-chip");
  if (!chip) {
    chip = document.createElement("div");
    chip.id = "route-chip";
    mapEl.appendChild(chip);
    if (L.DomEvent) L.DomEvent.disableClickPropagation(chip);
  }
  chip.innerHTML =
    '<span class="rc-badge" style="background:' + esc(MODE_LINE_COLOR[meta.mode] || MODE_LINE_COLOR.other) + '">' + esc(meta.name) + "</span>" +
    '<span class="rc-body">' + (meta.long ? '<span class="rc-name">' + esc(meta.long) + "</span>" : "") +
    '<span class="rc-sub">' + (n ? n + " vehicle" + (n === 1 ? "" : "s") + " live now" : "route focus") + " · Esc to clear</span></span>" +
    '<button class="rc-x" type="button" aria-label="Clear route focus">×</button>';
  chip.querySelector(".rc-x").addEventListener("click", clearRouteHighlight);
}
function hideRouteChip() {
  const chip = document.getElementById("route-chip");
  if (chip) chip.remove();
}

function toggleRouteHighlight(routeId) {
  if (highlightedRoute === routeId) clearRouteHighlight();
  else highlightRoute(routeId);
}

async function loadShapes() {
  if (!current || !routeLayer) return;
  routeLayer.clearLayers();
  let data;
  try { data = await getJSON(`/shapes?city=${current}&scope=${scope}`); }
  catch (e) { return; }
  // Handle warming-up: available:false && loading:true means GTFS is being loaded
  if (!data.available && data.loading) {
    currentNoGeometry = false;
    const statusEl = document.getElementById("shapes-status");
    if (statusEl) {
      statusEl.textContent = data.note || "Route geometry warming up — try again shortly.";
    }
    startShapesStatusPolling();
    return;
  }
  // Terminal "no route-line geometry" state (feed has no shapes.txt): show the
  // honest note once and STOP — do not poll (that bounced forever previously).
  if (!data.available && data.no_geometry) {
    currentNoGeometry = true;
    currentGeometrySource = "none";
    shapesStatusToken++;                       // invalidate any in-flight poll
    if (shapesStatusTimer) { clearTimeout(shapesStatusTimer); shapesStatusTimer = null; }
    const statusEl = document.getElementById("shapes-status");
    if (statusEl) { statusEl.textContent = data.note || "This city has no route-line geometry."; }
    return;
  }
  currentNoGeometry = false;
  if (!data.available || !data.shapes || !data.shapes.length) return;
  currentGeometrySource = "route";  // published GTFS shapes.txt geometry
  routeIndex = {};
  routeMeta = {};
  highlightedRoute = null;
  hideRouteChip();
  const zoomNow = map ? map.getZoom() : 13;
  const drawable = data.shapes.filter((s) =>
    // Compose agency AND mode: a route line is drawn only if it passes BOTH
    // filters — selecting an agency or a mode hides the others' routes too
    // (not just dims them; the polyline is never added to the layer).
    modeFilterPass(s.mode) && agencyFilterPass(s.agency_id));
  // Pass 1 — white casing under the rail spines (metro/rail/tram/cable) so
  // they read as the network's backbone above the bus mesh (F2-03).
  drawable.forEach((s) => {
    if (!ROUTE_CASED_MODES[s.mode]) return;
    const st = routeLineStyle(s.mode, zoomNow);
    const casing = L.polyline(s.path, {
      color: "#ffffff", weight: st.weight + 2.6, opacity: 0.9, interactive: false,
    });
    casing._tpBase = { color: "#ffffff", weight: st.weight + 2.6, opacity: 0.9 };
    casing._tpMode = s.mode;
    casing._tpIsCasing = true;
    casing._tpRouteId = s.route_id;
    if (s.route_id) (routeIndex[s.route_id] = routeIndex[s.route_id] || []).push(casing);
    routeLayer.addLayer(casing);
  });
  // Pass 2 — the coloured lines themselves, zoom-scaled + mode-weighted.
  drawable.forEach((s) => {
    const st = routeLineStyle(s.mode, zoomNow);
    const head = (s.short_name || s.route_id || "");
    const tail = s.long_name || "";
    if (s.route_id && !routeMeta[s.route_id]) {
      routeMeta[s.route_id] = { name: head, long: tail, mode: s.mode };
    }
    const el = document.createElement("div");
    const b = document.createElement("b");
    b.textContent = tail ? `${head} — ${tail}` : head;
    el.appendChild(b);
    el.appendChild(document.createElement("br"));
    el.appendChild(document.createTextNode(MODE_LABEL[s.mode] || "Other"));
    const line = L.polyline(s.path, {
      color: st.color, weight: st.weight, opacity: st.opacity,
      dashArray: st.dashArray || null, interactive: true,
    });
    line._tpBase = { color: st.color, weight: st.weight, opacity: st.opacity };
    line._tpMode = s.mode;
    line._tpRouteId = s.route_id;
    if (s.route_id) {
      (routeIndex[s.route_id] = routeIndex[s.route_id] || []).push(line);
    }
    line.on("click", function (ev) {
      if (ev && ev.originalEvent && L.DomEvent) L.DomEvent.stopPropagation(ev.originalEvent);
      toggleRouteHighlight(s.route_id);
    });
    line.bindPopup(el);
    routeLayer.addLayer(line);
  });
  if (map && !map._tpClearBound) {
    map._tpClearBound = true;
    map.on("click", function () { clearRouteHighlight(); });
  }
  // Rebuilt polylines carry base styles — re-tint them if the reliability ×
  // terrain overlay is active (no-op otherwise).
  relTerrainReapply();
}

/* ── Route-line visual hierarchy (F2-03) ──
   vehicles > rail spines > bus mesh > basemap. Bus desaturates to a steel blue
   at ~half opacity; metro/rail/tram/cable keep their semantic colours over a
   white casing; ferry chords render dashed (schematic water links, not roads).
   Weights scale with zoom so a city-wide view is a fine web, close-up is bold. */
const ROUTE_CASED_MODES = { metro: 1, rail: 1, tram: 1, cable: 1 };
const ROUTE_BUS_COLOR = "#7aa5d8";
function routeLineStyle(mode, zoom) {
  const z = (zoom == null && map) ? map.getZoom() : (zoom == null ? 13 : zoom);
  let w = z <= 11 ? 1.6 : z <= 12 ? 2.2 : z <= 13 ? 2.8 : z <= 14 ? 3.4 : 4;
  let color = MODE_LINE_COLOR[mode] || MODE_LINE_COLOR.other;
  let opacity = 0.8, dashArray = null;
  if (mode === "bus" || mode === "other" || !mode) { color = mode === "bus" ? ROUTE_BUS_COLOR : color; opacity = 0.45; }
  if (mode === "ferry") { dashArray = "6 8"; opacity = 0.4; w = Math.max(1.4, w - 1); }
  return { color, weight: w, opacity, dashArray };
}
// Re-derive every route line's base style on zoom (bound once in initMap).
function restyleRouteLines() {
  if (!routeLayer || !map || currentGeometrySource !== "route") return;
  const z = map.getZoom();
  routeLayer.eachLayer(function (ln) {
    if (!ln._tpBase || typeof ln.setStyle !== "function") return;
    const st = routeLineStyle(ln._tpMode, z);
    ln._tpBase = ln._tpIsCasing
      ? { color: "#ffffff", weight: st.weight + 2.6, opacity: 0.9 }
      : { color: st.color, weight: st.weight, opacity: st.opacity };
    if (!highlightedRoute) ln.setStyle(ln._tpBase);
  });
  if (highlightedRoute) highlightRoute(highlightedRoute);
  // Zoom restyle resets to base colours — re-tint for the reliability overlay.
  relTerrainReapply();
}

/* ─────────── Pro analysis tools: coverage shading + reliability × terrain ───────────
   Implemented here (not terrain-engine.js) because both are TRANSIT-data
   overlays: they read the state this file owns (route polylines, the scheduled
   stop list, the reliability report card) and only borrow the terrain engine
   for DEM elevation sampling. Both are toggles — the Layers-panel tool click
   turns the overlay on, the next click (or the legend's ×) turns it off. They
   render in their own pane/controls and never touch vehicleLayer, so the
   marker-migration refresh keeps gliding untouched. Deactivated on city switch
   (selectCity) — their data snapshots are per-city. */

// Web-Mercator ground resolution at the current view centre (metres per CSS px).
function mapMetersPerPixel() {
  const c = map.getCenter();
  return 40075016.686 * Math.cos(c.lat * Math.PI / 180) / Math.pow(2, map.getZoom() + 8);
}

// Set by initMap → renderPanel so these module-level toggles can refresh the
// Layers panel's aria-pressed / "on" pills after a state change.
let _layersPanelRefresh = null;

/* ── Coverage / reachable-area shading ──
   Shades everything within a straight-line radius (400 m / 800 m toggle) of
   the transit network: around every published stop when the city ships stop
   coordinates to the client (Tier-B scheduled cities), else along the route
   polylines (live cities publish shapes.txt geometry but no client-side stop
   list — the legend says which basis is in use). Rendered as ONE flat
   translucent field: opaque circles/strokes on a canvas with the translucency
   on the canvas element itself, so overlapping buffers union instead of
   stacking darker. */
const COV_COLOR = "#0a7ea4";      // teal — the app/data colour (copper stays Pro-only)
const COV_RADII = [400, 800];     // offered walking radii, metres
let covShade = {
  active: false, radiusM: 400, city: null, src: null,
  canvas: null, legend: null, noteEl: null, swatchEl: null,
};

// What can we shade from? Scheduled cities cache their full stop list
// client-side (renderScheduledCity) → true stop-radius shading. Otherwise the
// drawn route polylines (route or stop-sequence geometry) → corridor shading.
// null ⇔ nothing drawable — the caller shows an honest note, like /api/shapes'
// available:false handling.
function covCollectSource() {
  if (window._schedStopsCity === current && Array.isArray(window._schedStopsData) &&
      window._schedStopsData.length) {
    return { kind: "stops", stops: window._schedStopsData };
  }
  const lines = [];
  if (routeLayer) {
    routeLayer.eachLayer(function (ln) {
      if (ln._tpIsCasing || typeof ln.getLatLngs !== "function") return;
      let lls = ln.getLatLngs();
      if (lls.length && Array.isArray(lls[0])) lls = lls[0];   // multi-segment
      if (lls.length < 2) return;
      let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
      const pts = lls.map(function (p) {
        if (p.lat < minLat) minLat = p.lat; if (p.lat > maxLat) maxLat = p.lat;
        if (p.lng < minLng) minLng = p.lng; if (p.lng > maxLng) maxLng = p.lng;
        return [p.lat, p.lng];
      });
      // Per-line bbox → cheap viewport culling on every redraw.
      lines.push({ pts: pts, b: [minLat, minLng, maxLat, maxLng] });
    });
  }
  if (lines.length) return { kind: "lines", lines: lines };
  return null;
}

function covHide() { if (covShade.canvas) covShade.canvas.style.display = "none"; }

function covRedraw() {
  if (!covShade.active || !map || !covShade.canvas) return;
  const cv = covShade.canvas;
  cv.style.display = "";
  const size = map.getSize();
  cv.width = size.x; cv.height = size.y;
  L.DomUtil.setPosition(cv, map.containerPointToLayerPoint([0, 0]));
  const ctx = cv.getContext("2d");
  ctx.clearRect(0, 0, size.x, size.y);
  const rPx = Math.max(1, covShade.radiusM / mapMetersPerPixel());
  ctx.fillStyle = COV_COLOR;
  ctx.strokeStyle = COV_COLOR;
  if (covShade.src.kind === "stops") {
    let n = 0;
    ctx.beginPath();
    covShade.src.stops.forEach(function (pt) {
      if (!Array.isArray(pt) || pt.length < 2) return;
      const p = map.latLngToContainerPoint([pt[0], pt[1]]);
      if (p.x < -rPx || p.y < -rPx || p.x > size.x + rPx || p.y > size.y + rPx) return;
      ctx.moveTo(p.x + rPx, p.y);
      ctx.arc(p.x, p.y, rPx, 0, Math.PI * 2);
      // Flush every ~400 arcs: one megapath over a 50k-stop national feed
      // would stall the raster; batched fills stay flat (opaque + CSS alpha).
      if (++n % 400 === 0) { ctx.fill(); ctx.beginPath(); }
    });
    ctx.fill();
  } else {
    // Corridor buffer: a stroke of width 2r with round caps/joins IS the union
    // of r-radius discs along the line — the exact buffer, for free.
    ctx.lineWidth = rPx * 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    const vb = map.getBounds();
    const padDeg = covShade.radiusM / 111000 + 0.002;
    covShade.src.lines.forEach(function (line) {
      const b = line.b;
      if (b[2] < vb.getSouth() - padDeg || b[0] > vb.getNorth() + padDeg ||
          b[3] < vb.getWest() - padDeg || b[1] > vb.getEast() + padDeg) return;
      ctx.beginPath();
      for (let i = 0; i < line.pts.length; i++) {
        const p = map.latLngToContainerPoint(line.pts[i]);
        if (i === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y);
      }
      ctx.stroke();
    });
  }
}

function covUpdateLegendCopy() {
  const stops = covShade.src && covShade.src.kind === "stops";
  if (covShade.swatchEl) {
    covShade.swatchEl.textContent = "≤ " + covShade.radiusM + " m of a " +
      (stops ? "stop" : "route line");
  }
  if (covShade.noteEl) {
    covShade.noteEl.textContent = stops
      ? "Straight-line distance around every published stop (" +
        covShade.src.stops.length.toLocaleString() + " stops from the timetable feed)."
      : "Straight-line distance along the drawn route lines — this feed doesn't " +
        "publish a client-side stop list, so shading follows the lines.";
  }
}

function covLegendControl() {
  const ctl = L.control({ position: "bottomright" });
  ctl.onAdd = function () {
    const div = L.DomUtil.create("div", "gts-slope-legend gts-cov-legend");
    const head = L.DomUtil.create("div", "gsl-head", div);
    head.textContent = "Transit coverage";
    const x = L.DomUtil.create("button", "gts-legend-x", div);
    x.type = "button";
    x.setAttribute("aria-label", "Turn off coverage shading");
    x.textContent = "×";
    x.addEventListener("click", coverageShadingOff);
    const row = L.DomUtil.create("div", "gsl-row", div);
    const sw = L.DomUtil.create("span", "gsl-swatch", row);
    sw.style.background = COV_COLOR;
    sw.style.opacity = "0.45";
    covShade.swatchEl = L.DomUtil.create("span", "gsl-label", row);
    const btns = L.DomUtil.create("div", "gcl-btns", div);
    btns.setAttribute("role", "group");
    btns.setAttribute("aria-label", "Walking radius");
    COV_RADII.forEach(function (r) {
      const b = L.DomUtil.create("button", "gcl-btn", btns);
      b.type = "button";
      b.textContent = r + " m";
      b.setAttribute("aria-pressed", r === covShade.radiusM ? "true" : "false");
      b.setAttribute("aria-label", "Shade within " + r + " metres");
      b.addEventListener("click", function () {
        covShade.radiusM = r;
        btns.querySelectorAll(".gcl-btn").forEach(function (o) {
          o.setAttribute("aria-pressed", o === b ? "true" : "false");
        });
        covUpdateLegendCopy();
        covRedraw();
      });
    });
    covShade.noteEl = L.DomUtil.create("div", "gts-legend-note", div);
    covUpdateLegendCopy();
    L.DomEvent.disableClickPropagation(div);
    return div;
  };
  ctl.onRemove = function () { covShade.swatchEl = null; covShade.noteEl = null; };
  return ctl;
}

// Layers-panel entry point. Returns false ⇔ this click turned the tool OFF.
function toggleCoverageShading() {
  if (covShade.active) { coverageShadingOff(); return false; }
  if (!map || !current) {
    showToast("Pick a city first — coverage shading shades the area within walking reach of its network.");
    return true;
  }
  const src = covCollectSource();
  if (!src) {
    showToast(currentNoGeometry
      ? "This city's feed publishes neither stop coordinates nor route-line geometry to the map, so reachable-area shading can't be drawn here."
      : "No stops or route lines are loaded yet — let the city finish loading, then try again.");
    return true;
  }
  covShade.active = true;
  covShade.city = current;
  covShade.src = src;
  if (!map.getPane("gts-coverage")) {
    const pane = map.createPane("gts-coverage");
    pane.style.zIndex = 350;         // above tiles (200), below route lines (400)
    pane.style.pointerEvents = "none";
  }
  const cv = document.createElement("canvas");
  cv.className = "gts-coverage-canvas";
  cv.style.opacity = "0.3";          // translucency lives HERE → flat union
  cv.setAttribute("aria-hidden", "true");
  map.getPane("gts-coverage").appendChild(cv);
  covShade.canvas = cv;
  map.on("moveend zoomend resize", covRedraw);
  // Hide during the zoom animation (the canvas isn't zoom-animated) and
  // repaint crisp on zoomend.
  map.on("zoomstart", covHide);
  covRedraw();
  covShade.legend = covLegendControl();
  covShade.legend.addTo(map);
  if (_layersPanelRefresh) _layersPanelRefresh();
  return true;
}

function coverageShadingOff() {
  if (!covShade.active) return;
  covShade.active = false;
  if (map) {
    map.off("moveend zoomend resize", covRedraw);
    map.off("zoomstart", covHide);
    if (covShade.legend) { try { map.removeControl(covShade.legend); } catch (e) {} }
  }
  if (covShade.canvas && covShade.canvas.parentNode) {
    covShade.canvas.parentNode.removeChild(covShade.canvas);
  }
  covShade.canvas = null; covShade.legend = null; covShade.src = null;
  covShade.city = null; covShade.noteEl = null; covShade.swatchEl = null;
  if (_layersPanelRefresh) _layersPanelRefresh();
}

/* ── Reliability × terrain overlay ──
   Colours the drawn route lines by their on-time record
   (/api/public/reliability/{city}/routes, 30-day window — EVERY route with
   any on-time history, not just the report card's ranked sets), letter-graded
   through the SERVED grade bands and painted with the same colours as the
   Compare bars — with the hillshade relief layer switched on underneath so
   reliability can be read against the terrain. Routes with genuinely no
   recorded history render neutral grey — nothing is estimated. Against an
   older API without the per-route endpoint it degrades gracefully to the
   report card's ranked best/worst/most-changed sets (grey = "no ranked
   record"). While active, a route's popup gains its on-time record plus a
   DEM-sampled elevation gain from the terrain engine. */
const REL_TERRAIN_WINDOW = "30d";
const REL_NEUTRAL = "#8b97a8";
let relTerrain = {
  active: false, city: null, window: REL_TERRAIN_WINDOW, byRoute: null,
  legend: null, hillshade: null, addedHillshade: false,
  full: false,   // true when data covers ALL routes (new endpoint), not just ranked sets
};

// Report-card stat for a shape's route_id. /api/shapes keys may carry a
// collision prefix "{numeric_idx}:{route_id}" (see api.py) — try both forms.
function relStatFor(rid) {
  if (!relTerrain.byRoute || rid == null) return null;
  if (relTerrain.byRoute[rid]) return relTerrain.byRoute[rid];
  const m = String(rid).match(/^\d+:(.*)$/);
  return (m && relTerrain.byRoute[m[1]]) || null;
}

// Re-tint the route polylines. Safe to call anytime: no-ops unless the overlay
// is active for the current city, and defers to an active route highlight
// (whose clear() calls back here).
function relTerrainReapply() {
  if (!relTerrain.active || !routeLayer || relTerrain.city !== current) return;
  if (highlightedRoute) return;
  routeLayer.eachLayer(function (ln) {
    if (typeof ln.setStyle !== "function" || !ln._tpBase || ln._tpIsCasing) return;
    const s = relStatFor(ln._tpRouteId);
    if (s && s.on_time_pct != null) {
      ln.setStyle({
        color: GRADE_BAR_COLOR[pctGrade(s.on_time_pct)] || REL_NEUTRAL,
        opacity: 0.95,
        weight: Math.max(ln._tpBase.weight, 3.2),
      });
    } else {
      // No ranked on-time record in this window — neutral, never invented.
      ln.setStyle({ color: REL_NEUTRAL, opacity: 0.5 });
    }
  });
}

// While the overlay is on, append the on-time record + a lazily DEM-sampled
// elevation gain to the clicked route's popup (once per popup content node —
// the node persists on the polyline, so the resolved text is kept).
function relTerrainOnPopup(e) {
  const src = e.popup && e.popup._source;
  if (!relTerrain.active || !src || !src._tpRouteId || src._tpIsCasing) return;
  const el = e.popup.getContent();
  if (!el || !el.querySelector || el.querySelector(".rel-terrain-pop")) return;
  const s = relStatFor(src._tpRouteId);
  const div = document.createElement("div");
  div.className = "rel-terrain-pop";
  div.style.marginTop = "4px";
  const ot = document.createElement("div");
  if (s && s.on_time_pct != null) {
    const bEl = document.createElement("b");
    bEl.style.color = GRADE_BAR_COLOR[pctGrade(s.on_time_pct)] || REL_NEUTRAL;
    bEl.textContent = s.on_time_pct + "% on time";
    ot.appendChild(bEl);
    ot.appendChild(document.createTextNode(
      " · " + (s.n_obs || 0).toLocaleString() + " obs · " + relTerrain.window));
  } else {
    ot.textContent = (relTerrain.full ? "No on-time record in the "
                                      : "No ranked on-time record in the ") +
      relTerrain.window + " window";
  }
  div.appendChild(ot);
  const elevEl = document.createElement("div");
  elevEl.textContent = "Reading terrain…";
  div.appendChild(elevEl);
  el.appendChild(div);
  let lls = typeof src.getLatLngs === "function" ? src.getLatLngs() : [];
  if (lls.length && Array.isArray(lls[0])) lls = lls[0];
  if (window.GTS_TERRAIN && lls.length > 1) {
    window.GTS_TERRAIN.elevationProfile(lls).then(function (rows) {
      let gain = 0, prev = null, any = false;
      (rows || []).forEach(function (r) {
        if (r.elev == null) return;
        if (prev != null && r.elev > prev) gain += r.elev - prev;
        prev = r.elev; any = true;
      });
      elevEl.textContent = any
        ? "Elevation gain +" + Math.round(gain) + " m along this line · Terrarium DEM"
        : "Elevation data unavailable for this line.";
      try { e.popup.update(); } catch (err) {}
    }, function () { elevEl.textContent = "Elevation data unavailable for this line."; });
  } else {
    elevEl.textContent = "";
  }
  try { e.popup.update(); } catch (err) {}
}

function relTerrainLegendControl() {
  const ctl = L.control({ position: "bottomright" });
  ctl.onAdd = function () {
    const div = L.DomUtil.create("div", "gts-slope-legend gts-rel-legend");
    const head = L.DomUtil.create("div", "gsl-head", div);
    head.textContent = "Route on-time % · " + relTerrain.window;
    const x = L.DomUtil.create("button", "gts-legend-x", div);
    x.type = "button";
    x.setAttribute("aria-label", "Turn off reliability and terrain overlay");
    x.textContent = "×";
    x.addEventListener("click", reliabilityTerrainOff);
    // Bands come from the SERVED grade config (F17) — never a hardcoded copy.
    GRADE_BANDS.forEach(function (b, i) {
      const row = L.DomUtil.create("div", "gsl-row", div);
      const sw = L.DomUtil.create("span", "gsl-swatch", row);
      sw.style.background = GRADE_BAR_COLOR[b.letter] || REL_NEUTRAL;
      const lb = L.DomUtil.create("span", "gsl-label", row);
      lb.textContent = i === 0
        ? b.letter + " · ≥ " + b.min_pct + "% on time"
        : b.letter + " · " + b.min_pct + " – <" + GRADE_BANDS[i - 1].min_pct + "%";
    });
    const row = L.DomUtil.create("div", "gsl-row", div);
    const sw = L.DomUtil.create("span", "gsl-swatch", row);
    sw.style.background = REL_NEUTRAL;
    const lb = L.DomUtil.create("span", "gsl-label", row);
    lb.textContent = relTerrain.full ? "No recorded history" : "No ranked record";
    const note = L.DomUtil.create("div", "gts-legend-note", div);
    note.textContent = (relTerrain.full
      ? "Colours: every route with on-time history in this window. " +
        "Grey routes have no recorded observations — nothing is estimated. "
      : "Colours: the report card's ranked routes for this window. " +
        "Grey routes have no ranked on-time record — nothing is estimated. ") +
      "Hillshade relief underneath shows the terrain; click a route for its " +
      "record + elevation gain.";
    L.DomEvent.disableClickPropagation(div);
    return div;
  };
  ctl.onRemove = function () {};
  return ctl;
}

// Layers-panel entry point. Returns false ⇔ this click turned the tool OFF.
// `hillshadeLayer` is initMap's "Hillshade relief" overlay instance — added
// underneath if not already on, and removed again only if WE added it.
function toggleReliabilityTerrain(hillshadeLayer) {
  if (relTerrain.active) { reliabilityTerrainOff(); return false; }
  if (!map || !current) {
    showToast("Pick a city first — the reliability overlay colours its route lines.");
    return true;
  }
  const cityObj = cities.find(function (c) { return c.id === current; });
  if (cityObj && cityObj.coverage === "scheduled") {
    showToast("This is a timetable-only city with no realtime feed, so there's no on-time history to overlay. Try a live-tracked city.");
    return true;
  }
  if (currentGeometrySource !== "route") {
    showToast(currentNoGeometry
      ? "This city's feed has no route-line geometry, so there are no lines to colour by reliability. Try a city that shows route lines on the map."
      : "No route geometry loaded yet — let the city's routes load, then try again.");
    return true;
  }
  relTerrain.active = true;          // optimistic; reverted if the fetch fails
  relTerrain.city = current;
  relTerrain.hillshade = hillshadeLayer || null;
  if (relTerrain.hillshade && !map.hasLayer(relTerrain.hillshade)) {
    relTerrain.hillshade.addTo(map);
    relTerrain.addedHillshade = true;
  }
  if (_layersPanelRefresh) _layersPanelRefresh();
  // Shared tail for both data sources: empty → honest toast + off; else tint,
  // legend, popup hook. `full` records whether the data covers ALL routes.
  function finish(by, win, full) {
    if (!relTerrain.active || relTerrain.city !== current) return;  // toggled off meanwhile
    if (!Object.keys(by).length) {
      reliabilityTerrainOff();
      showToast("No route here has enough on-time history in the " + REL_TERRAIN_WINDOW +
        " window yet, so the reliability overlay would be all grey. It stays off — nothing is estimated.");
      return;
    }
    relTerrain.byRoute = by;
    relTerrain.window = win || REL_TERRAIN_WINDOW;
    relTerrain.full = !!full;
    relTerrainReapply();
    relTerrain.legend = relTerrainLegendControl();
    relTerrain.legend.addTo(map);
    map.on("popupopen", relTerrainOnPopup);
  }
  function fail() {
    reliabilityTerrainOff();
    showToast("On-time history isn't available for this city yet, so the reliability × terrain overlay can't be drawn.", "error");
  }
  // Preferred source: the lean per-route endpoint — EVERY route with any
  // on-time history this window, so all drawn lines get an honest colour.
  getJSON("/public/reliability/" + encodeURIComponent(current) +
          "/routes?window=" + REL_TERRAIN_WINDOW).then(function (d) {
    const by = {};
    ((d && d.routes) || []).forEach(function (r) {
      if (r && r.route_id && !by[r.route_id]) by[r.route_id] = r;
    });
    finish(by, d && d.window, true);
  }, function () {
    // Older API without /routes (e.g. during deploy transition): degrade to the
    // report card's ranked sets — only ~32 routes get colour, the legend says so.
    getJSON("/public/reliability/" + encodeURIComponent(current) +
            "?window=" + REL_TERRAIN_WINDOW).then(function (d) {
      const by = {};
      ["best_routes", "worst_routes", "most_improved", "most_declined"].forEach(function (k) {
        ((d && d[k]) || []).forEach(function (r) {
          if (r && r.route_id && !by[r.route_id]) by[r.route_id] = r;
        });
      });
      finish(by, d && d.window, false);
    }, fail);
  });
  return true;
}

function reliabilityTerrainOff() {
  if (!relTerrain.active) return;
  relTerrain.active = false;
  if (map) {
    map.off("popupopen", relTerrainOnPopup);
    if (relTerrain.legend) { try { map.removeControl(relTerrain.legend); } catch (e) {} }
    if (relTerrain.addedHillshade && relTerrain.hillshade && map.hasLayer(relTerrain.hillshade)) {
      map.removeLayer(relTerrain.hillshade);
    }
  }
  if (routeLayer) {
    routeLayer.eachLayer(function (ln) {
      // Restore base styling (a live highlight restores through its own clear).
      if (!highlightedRoute && ln._tpBase && typeof ln.setStyle === "function") {
        ln.setStyle(ln._tpBase);
      }
      // Strip the appended popup rows so a later non-overlay popup is clean.
      const p = typeof ln.getPopup === "function" ? ln.getPopup() : null;
      const el = p && typeof p.getContent === "function" ? p.getContent() : null;
      if (el && el.querySelector) {
        const extra = el.querySelector(".rel-terrain-pop");
        if (extra && extra.parentNode) extra.parentNode.removeChild(extra);
      }
    });
  }
  relTerrain.byRoute = null; relTerrain.city = null; relTerrain.legend = null;
  relTerrain.hillshade = null; relTerrain.addedHillshade = false;
  relTerrain.window = REL_TERRAIN_WINDOW; relTerrain.full = false;
  if (_layersPanelRefresh) _layersPanelRefresh();
}

// Zoom classes resize transport badges in CSS without hiding vehicle glyphs.
function updateMapZoomClass() {
  const el = document.getElementById("map");
  if (!el || !map) return;
  const z = map.getZoom();
  el.classList.toggle("mz-far", z <= 11);
  el.classList.toggle("mz-near", z === 14 || z === 15);
  el.classList.toggle("mz-close", z >= 16);
}

// Map-corner legend (bottom-left, collapsed): where consumer maps keep their
// legends, replacing the sidebar-basement legend card (map plan #5).
function addLegendControl() {
  if (!map || !L.control) return;
  const legend = L.control({ position: "bottomleft" });
  legend.onAdd = function () {
    const c = L.DomUtil.create("div", "gts-layerctl gts-legendctl");
    let body = '<div class="gts-legend-sub">Ring — punctuality</div><div class="gts-legend-wrap">';
    [["On time", "#0f7a37"], ["Late", "#c1272d"], ["Early", "#b87514"], ["No data", "#5b6878"]].forEach(function (p) {
      body += '<div class="gts-legend-row"><span class="gts-legend-dot" style="background:' + p[1] + '"></span>' + p[0] + "</div>";
    });
    body += '</div><div class="gts-legend-sub">Vehicle type &amp; route line</div>';
    MODE_ORDER.forEach(function (m) {
      const st = routeLineStyle(m, 15);
      body += '<div class="gts-legend-row">' + modeIcon(m, 13) +
        '<span class="gts-legend-line" style="background:' + st.color + '"></span>' +
        (MODE_LABEL[m] || "Other") + "</div>";
    });
    c.innerHTML = '<button class="glc-toggle" type="button" title="Map legend" aria-expanded="false">' +
      uiIcon("list", 13) + ' Legend</button><div class="glc-body" style="display:none">' + body + "</div>";
    L.DomEvent.disableClickPropagation(c);
    L.DomEvent.disableScrollPropagation(c);
    let open = false;
    c.querySelector(".glc-toggle").addEventListener("click", function () {
      open = !open;
      c.querySelector(".glc-body").style.display = open ? "block" : "none";
      this.setAttribute("aria-expanded", open ? "true" : "false");
    });
    return c;
  };
  legend.addTo(map);
}

// Mode-iconed marker + rich popup live in vehicle_marker.js / vehicle_popup.js
// (loaded before app.js in index.html). These thin wrappers preserve the
// existing call sites here in app.js.
function vehicleIcon(v) {
  return window.TP_MARKER.vehicleIcon(v);
}

// Honest-label safety net: the backend is the source of truth for status, but
// the literal "unknown" must never reach the UI (owner-forbidden word). If a
// popup status falls back to "unknown", relabel it to an honest phrase.
function relabelUnknown(text) {
  return String(text).replace(/\bunknown\b/gi, "no realtime delay data");
}

function buildPopup(v) {
  const node = window.TP_POPUP.build(v);
  // Defensive relabel of any status row that rendered the forbidden "unknown".
  const st = node && node.querySelector ? node.querySelector(".tp-pop-status") : null;
  if (st && /\bunknown\b/i.test(st.textContent)) {
    st.textContent = relabelUnknown(st.textContent);
  }
  return node;
}

/* shapes-status toast (J.2) */
let shapesStatusTimer = null;
let shapesStatusDelay = 3000;
let shapesPrevLoading = false;
let shapesStatusToken = 0;  // incremented on each restart; stale in-flight polls no-op

function showToast(msg, kind) {
  const t = document.getElementById("tp-toast");
  if (!t) return;
  t.textContent = msg;
  t.classList.toggle("error", kind === "error");
  t.classList.add("show");
}
function hideToast() {
  const t = document.getElementById("tp-toast");
  if (t) { t.classList.remove("show"); t.textContent = ""; }
}

async function pollShapesStatus() {
  if (!current) return;
  const myToken = shapesStatusToken;
  let s;
  try { s = await getJSON(`/shapes/status?city=${current}`); }
  catch (e) {
    // A transient status-fetch failure must not permanently stop the retry
    // loop and leave the map empty — reschedule and try again.
    shapesStatusTimer = setTimeout(pollShapesStatus, shapesStatusDelay);
    return;
  }
  if (myToken !== shapesStatusToken) return;
  if (s.loading) {
    showToast("Loading route geometry...", "info");
    shapesPrevLoading = true;
    shapesStatusDelay = Math.min(30000, Math.round(shapesStatusDelay * 2));
    shapesStatusTimer = setTimeout(pollShapesStatus, shapesStatusDelay);
    return;
  }
  if (s.error) {
    showToast("Route geometry unavailable", "error");
    setTimeout(hideToast, 4000);
    return;
  }
  // Loaded (or no longer loading): always redraw. Dropping the prior
  // `shapesPrevLoading` gate fixes the first-poll race where a city that
  // warmed before the first status poll never got its polylines drawn.
  // loadShapes() is idempotent (clears the route layer before redraw).
  hideToast();
  const wasLoading = shapesPrevLoading;
  shapesPrevLoading = false;
  loadShapes();
  if (wasLoading) loadVehicles();
}
function startShapesStatusPolling() {
  if (shapesStatusTimer) clearTimeout(shapesStatusTimer);
  shapesStatusToken++;
  hideToast();
  shapesStatusDelay = 3000;
  shapesPrevLoading = false;
  shapesStatusTimer = setTimeout(pollShapesStatus, shapesStatusDelay);
}

function initCityPicker() {
  // Clean single-control dropdown: a button showing the current city opens a
  // searchable list. Replaces the old chips + typeahead + drawer + near-me mix.
  const sel = document.getElementById("citysel");
  const btn = document.getElementById("citysel-btn");
  const cur = document.getElementById("citysel-current");
  const pop = document.getElementById("citysel-pop");
  const search = document.getElementById("citysel-search");
  const list = document.getElementById("citysel-list");
  if (!sel || !btn || !pop || !list) return;

  // Reflect the current selection on the button label every (re)build.
  const active = cities.find((c) => c.id === current);
  cur.textContent = active ? active.name : "Select a city";

  const close = () => {
    pop.hidden = true;
    btn.setAttribute("aria-expanded", "false");
    if (search) search.removeAttribute("aria-activedescendant");
  };
  // F13 — combobox pattern: the search input keeps focus and drives an
  // active-option highlight via aria-activedescendant; the options themselves
  // are NOT tab stops (the old per-li tabIndex=0 made 1,000+ stops).
  const mkOpt = (c) => {
    const li = document.createElement("li");
    li.className = "citysel-opt" + (c.id === current ? " active" : "");
    li.setAttribute("role", "option");
    const live = c.coverage !== "scheduled";
    const meta = live
      ? `${c.country} · ${(c.n_vehicles || 0).toLocaleString()} veh`
      : `${c.country} · timetable`;
    li.innerHTML = `<span class="citysel-opt-name">` +
      (live ? '<span class="citysel-live-dot" aria-hidden="true"></span>' : "") +
      `${esc(c.name)}</span><span class="citysel-opt-meta">${esc(meta)}</span>`;
    li.addEventListener("click", () => { close(); selectCity(c.id); });
    return li;
  };
  const mkGroup = (label) => {
    const li = document.createElement("li");
    li.className = "citysel-group";
    li.setAttribute("role", "presentation");
    li.textContent = label;
    return li;
  };
  // F13 — active-option (kb-active) management. DOM-state based (never index
  // closures) so the once-bound handlers below stay correct across rebuilds.
  const kbOpts = () => Array.prototype.slice.call(list.querySelectorAll(".citysel-opt"));
  const kbSet = (el) => {
    const prev = list.querySelector(".citysel-opt.kb-active");
    if (prev) prev.classList.remove("kb-active");
    if (el) {
      el.classList.add("kb-active");
      el.scrollIntoView({ block: "nearest" });
      search.setAttribute("aria-activedescendant", el.id);
    } else {
      search.removeAttribute("aria-activedescendant");
    }
  };
  const renderList = (q) => {
    renderListItems(q);
    // Positional option ids for aria-activedescendant (a city can appear in
    // both the Saved and main groups, so ids can't be city-based).
    let n = 0;
    kbOpts().forEach((li) => { li.id = "citysel-opt-" + (n++); });
    kbSet(null);
  };
  const renderListItems = (q) => {
    const query = (q || "").toLowerCase().trim();
    list.innerHTML = "";
    if (query) {
      // Searching: one flat alphabetical list across live + scheduled.
      const items = cities.slice().sort((a, b) => a.name.localeCompare(b.name))
        .filter((c) => c.name.toLowerCase().includes(query) ||
                       (c.country || "").toLowerCase().includes(query));
      if (!items.length) {
        list.innerHTML = '<li class="citysel-empty">No matches</li>';
        return;
      }
      // Cap rendered rows so typing stays instant with 1,000+ cities; the full
      // list is always reachable by narrowing the search.
      const CAP = 60;
      items.slice(0, CAP).forEach((c) => list.appendChild(mkOpt(c)));
      if (items.length > CAP) {
        const more = document.createElement("li");
        more.className = "citysel-empty";
        more.textContent = `+${items.length - CAP} more — keep typing to narrow…`;
        list.appendChild(more);
      }
      return;
    }
    // Browsing (F2-06): favorites first, then "Live now" ranked by live
    // vehicle count (the flagship inventory leads), then scheduled cities.
    const favIds = flag("favorites") ? loadFavs() : [];
    const favs = favIds.map((id) => cities.find((c) => c.id === id)).filter(Boolean);
    const live = cities.filter((c) => c.coverage !== "scheduled")
      .sort((a, b) => (b.n_vehicles || 0) - (a.n_vehicles || 0) || a.name.localeCompare(b.name));
    const sched = cities.filter((c) => c.coverage === "scheduled")
      .sort((a, b) => a.name.localeCompare(b.name));
    if (favs.length) {
      list.appendChild(mkGroup("Saved"));
      favs.forEach((c) => list.appendChild(mkOpt(c)));
    }
    if (live.length) {
      list.appendChild(mkGroup(`Live vehicle tracking — ${live.length}`));
      live.forEach((c) => list.appendChild(mkOpt(c)));
    }
    if (sched.length) {
      const CAP_SCHED = 40;
      // "Timetable only", not "Scheduled" (owner 2026-07-27): "scheduled" read
      // as "coming soon". These cities have full timetables; their transit
      // agency just doesn't publish live vehicle positions.
      list.appendChild(mkGroup(`Timetable only (agency publishes no live tracking) — ${sched.length}`));
      sched.slice(0, CAP_SCHED).forEach((c) => list.appendChild(mkOpt(c)));
      if (sched.length > CAP_SCHED) {
        const more = document.createElement("li");
        more.className = "citysel-empty";
        more.textContent = `+${sched.length - CAP_SCHED} more timetable-only cities — type to search…`;
        list.appendChild(more);
      }
    }
  };

  // Bind interaction handlers once; rebuilds only refresh the label + list.
  if (!sel.dataset.bound) {
    sel.dataset.bound = "1";
    // F13 — combobox ARIA wiring on the search input.
    search.setAttribute("role", "combobox");
    search.setAttribute("aria-autocomplete", "list");
    search.setAttribute("aria-controls", "citysel-list");
    search.setAttribute("aria-expanded", "true");
    btn.addEventListener("click", () => {
      if (pop.hidden) {
        pop.hidden = false;
        btn.setAttribute("aria-expanded", "true");
        search.value = "";
        renderList("");
        setTimeout(() => search.focus(), 0);
      } else { close(); }
    });
    search.addEventListener("input", (e) => renderList(e.target.value));
    // F13 — keyboard: ArrowDown/ArrowUp move the active-option highlight
    // (wrapping), Enter selects it, Escape closes the picker.
    search.addEventListener("keydown", (e) => {
      const o = kbOpts();
      const cur = list.querySelector(".citysel-opt.kb-active");
      const i = o.indexOf(cur);
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (o.length) kbSet(o[i < 0 ? 0 : (i + 1) % o.length]);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (o.length) kbSet(o[i < 0 ? o.length - 1 : (i - 1 + o.length) % o.length]);
      } else if (e.key === "Enter") {
        if (cur) { e.preventDefault(); cur.click(); }
      } else if (e.key === "Escape") {
        // Stop here so the document-level handlers (route-focus clear) never
        // also act on the same keypress (F23).
        e.preventDefault();
        e.stopPropagation();
        close();
        btn.focus();
      }
    });
    document.addEventListener("click", (e) => { if (!sel.contains(e.target)) close(); });
    // F23 — scoped: Escape closes the picker ONLY while it is open. When it's
    // closed, the map's own Escape handler (clear route focus) takes over.
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !pop.hidden) close();
    });
  }

  // The button starts disabled in markup so an early click (before app.js binds
  // its handler on slow connections) isn't a silent no-op; enable it now that
  // the picker is wired and cities are loaded.
  btn.disabled = false;
  const cnt = document.getElementById("city-picker-count");
  if (cnt) {
    const liveN = cities.filter((c) => c.coverage !== "scheduled").length;
    // F18 — when running on the boot cache, state the cache age instead of
    // asserting "now"; the live wording returns once a fresh list lands.
    cnt.textContent = citiesCachedAt
      ? `${cities.length} cities · ${liveN} live-tracked as of ${cacheAgeMin(citiesCachedAt)} min ago`
      : `${cities.length} cities · ${liveN} live-tracked now`;
  }
}

function buildCityBar() {
  // Calls initCityPicker() after loadCities()
  initCityPicker();
}

// TASK 2 — days of history actually available for the current selection.
// Gated PER-CITY by that city's own `since` date: a city reveals each longer
// window (7d/30d/1y/5y) as soon as IT personally has that much history. We do
// NOT cap by the global collection span — a city can legitimately have more
// history than the global `collecting_since` marker (e.g. SF's backfilled data
// reaches 32 days while the global marker is only 29), and the old global cap
// wrongly hid the 30d window for such cities. Recomputed on each city load so
// windows auto-appear for every city as its own data accrues.
function availableHistoryDays() {
  const globalSpan = collectingSince ? Math.floor((Date.now() - new Date(collectingSince).getTime()) / 86400000) : 0;
  if (!current) return globalSpan;
  const cityObj = cities.find((c) => c.id === current);
  // City's own span when known; fall back to the global span only when the city
  // has no `since` date.
  return (cityObj && cityObj.since)
    ? Math.floor((Date.now() - new Date(cityObj.since).getTime()) / 86400000)
    : globalSpan;
}

function buildWindows() {
  const w = document.getElementById("windows");
  w.innerHTML = "";
  const have = availableHistoryDays();
  if (window_ !== "all" && (WINDOW_MIN_DAYS[window_] || 0) > have) window_ = "1h";
  WINDOWS.forEach(([key, label]) => {
    if (key !== "all" && (WINDOW_MIN_DAYS[key] || 0) > have) return;
    const b = document.createElement("button");
    b.className = "win-btn" + (key === window_ ? " active" : "");
    b.textContent = label;
    b.onclick = () => { window_ = key; buildWindows(); loadStats(); loadRoutes(); loadModes(); };
    w.appendChild(b);
  });
}

// (The mode legend now lives in the map's bottom-left Legend control —
// addLegendControl() — where consumer maps keep their legends.)

// True when window key `w` is permitted by the history gate for the current
// city. Shared with buildWindows()'s gate so fillBestWindow never lands window_
// on a window the gate would reset.
function allowedWindow(w) {
  return (WINDOW_MIN_DAYS[w] || 0) <= availableHistoryDays();
}

// Pick the shortest window that has BOTH stats and a route breakdown for the
// current city/scope. If no window can produce routes (the feed has no route_id,
// e.g. warsaw), fall back to the shortest window that at least has stats — so the
// city shows live data instead of being forced to a misleading all-time view.
// Only ever moves window_ to a window the history gate allows for this city, so
// the active button and the loaded data never disagree (BUG FIX).
async function fillBestWindow() {
  let s = await loadStats();
  let nr = await loadRoutes();
  if (s && s.n_observations && nr) return s;
  let statsWin = (s && s.n_observations) ? window_ : null;
  for (const w of ["24h", "7d", "30d", "all"]) {
    if (w === window_) continue;
    if (!allowedWindow(w)) continue;
    window_ = w; buildWindows();
    s = await loadStats();
    nr = await loadRoutes();
    if (s && s.n_observations && !statsWin) statsWin = w;
    if (s && s.n_observations && nr) return s;
  }
  if (statsWin && statsWin !== window_ && allowedWindow(statsWin)) {
    window_ = statsWin; buildWindows();
    s = await loadStats();
    nr = await loadRoutes();
  }
  return s;
}

async function selectCity(id) {
  const prev = current;
  current = id;
  // A real city is loading: clear the noindex that showCityNotFound set for an unknown slug.
  clearCityAvailability();
  // Keep the URL a shareable permalink for the selected city. Skip when we got
  // here FROM the router (applyRoute/popstate) to avoid a navigate loop.
  if (!_routing) {
    const want = "/city/" + id;
    if (location.pathname !== want) history.pushState({}, "", want);
  }
  if (prev !== id) {
    scope = "city";
    { const st = document.getElementById("scope-toggle"); if (st) st.checked = false; }
    // F12 — preserve the user's selected window across city switches when the
    // new city's own available_windows (from /api/cities) includes it;
    // otherwise fall back to the 1h default as before.
    const nc = cities.find((x) => x.id === id);
    const avail = nc && nc.available_windows;
    if (!(Array.isArray(avail) && avail.indexOf(window_) !== -1)) window_ = "1h";
    buildWindows();
  }
  updatePageSeo();
  updateCityHub();   // the selected city's hub tab strip follows the selection
  // F5 — the poll-health streak is per-city; a previous city's last-update
  // time must never back a stale warning for the new one.
  if (prev !== id) { _vehFailStreak = 0; _lastVehOkTime = null; _vehStale = false; }
  // The terrain-analysis overlays are per-city (their stops/routes/reliability
  // snapshots mean nothing elsewhere) — switch them off on a city change.
  if (prev !== id) { coverageShadingOff(); reliabilityTerrainOff(); }
  // Agency selections are city-specific (SF's "Muni" means nothing in Oslo) —
  // reset to "all" on every city switch so a stale agency never hides the new
  // city's vehicles/routes.
  mapAgency = new Set(["all"]);
  cityAgencies = [];
  // Historical-stats agency filter is city-specific too — reset on switch.
  statsAgency = null;
  statsAgencyOptions = [];
  buildCityBar();
  const c = cities.find((x) => x.id === id);
  document.getElementById("city-name").textContent = c.name;
  const scopeNote = scope === "city"
    ? " — limited to the city; toggle to include the whole network"
    : " — whole network: every vehicle on this feed";
  document.getElementById("city-meta").textContent =
    (c.scope || `${c.agency} · ${c.country}`) + scopeNote;
  const health = document.getElementById("feed-health");
  if (c.ok) {
    health.className = "ok";
    health.textContent = "● Feed live" +
      (c.last_poll ? " · updated " + new Date(c.last_poll).toLocaleTimeString() : "");
  } else {
    health.className = "down";
    health.textContent = "● Feed connected but returning no usable data yet";
  }
  // A stale catalog note can describe the static GTFS fallback even after the
  // city's realtime feed is serving usable observations. Replace only that
  // contradictory claim; preserve other source notes and scheduled-city text.
  const catalogNote = String(c.note || "");
  const claimsStaticOnly = /static routes? and stops|no realtime feed|no real-time feed|without realtime/i.test(catalogNote);
  const liveFeedNote = c.coverage !== "scheduled" && c.ok === true && claimsStaticOnly
    ? "Realtime feed is available; live vehicle positions are shown when reported."
    : catalogNote;
  document.getElementById("feed-note").textContent = liveFeedNote;
  map.setView(c.center, c.zoom);
  // ── Tier B: a scheduled city has no live vehicles/stats. Render its
  // timetable instead, hide the live-only panels, and stop here — keeping the
  // scheduled path fully isolated from the live data-loading machinery below. ──
  const isScheduled = flag("scheduled_tier") && c.coverage === "scheduled";
  toggleScheduledMode(isScheduled);
  rbCityChanged();  // route browser: clear the previous city's selection/overlay
  if (isScheduled) {
    if (refreshTimer) { clearInterval(refreshTimer); refreshTimer = null; }
    clearVehicleMarkers();
    routeLayer.clearLayers();
    await renderScheduledCity(c);
    updateFavStar();
    return;
  }
  // Fix 2: show a transient "Loading…" in the now cards during the city-switch
  // fetch so stale "—" doesn't read as broken. loadVehicles overwrites these.
  setNowCardsLoading();
  // F10 — parallelize the independent city-switch fetches: shapes, vehicles and
  // the stats-agency options don't depend on each other or on the stats window
  // probing below; only the fillBestWindow → modes chain is inherently
  // sequential (it moves window_). None of these reject — each catches its own
  // fetch errors — so Promise.all is safe.
  const shapesP = loadShapes().then(() => startShapesStatusPolling());
  const vehiclesP = loadVehicles();
  const agenciesP = loadStatsAgencies();
  let s = await fillBestWindow();
  // Scope fallback: still no stats in city scope -> this feed reports network-wide
  // (not bounded to the city box); show network scope so the city isn't blank.
  if (scope === "city" && (!s || !s.n_observations)) {
    scope = "network";
    const st = document.getElementById("scope-toggle");
    if (st) st.checked = true;
    await vehiclesP;          // let the city-scope poll settle before refetching
    await loadVehicles();
    window_ = "1h"; buildWindows();
    s = await fillBestWindow();
  }
  const modesData = await loadModes();
  await Promise.all([shapesP, vehiclesP, agenciesP]);
  renderMoreInfo(c, modesData);
  updateFavStar();
  if (refreshTimer) clearInterval(refreshTimer);
  refreshTimer = setInterval(loadVehicles, 20000);
}

/* ─────────── Tier B: scheduled-coverage rendering ─────────── */
// Show the scheduled card and hide every live-only panel (or the reverse).
// One DOM hook ([data-live-only]) keeps this decoupled from the live views.
const TAGLINE_LIVE = "Live public-transit positions & on-time performance, tracked over time.";
const TAGLINE_SCHEDULED = "Scheduled timetable view — this city doesn't publish live vehicle tracking.";
function toggleScheduledMode(on) {
  document.querySelectorAll("[data-live-only]").forEach((el) => { el.hidden = !!on; });
  const card = document.getElementById("scheduled-card");
  if (card) card.hidden = !on;
  if (!on && scheduledLayer) { scheduledLayer.clearLayers(); window._schedStopsData = null; window._schedStopsCity = null; }
  // Context-switch the page framing so a feedless city reads as an intentional
  // "Scheduled" map, not a broken live one (the top trust fix).
  const tag = document.querySelector("header .tag");
  if (tag) tag.textContent = on ? TAGLINE_SCHEDULED : TAGLINE_LIVE;
  document.body.classList.toggle("scheduled-mode", !!on);
}

// Persistent "city not found" state for an unknown /city/{id}: show a clear,
// non-transient message in the panel and hide the live-only panels so no blank
// cards remain (a fading toast is missed by users — ChatGPT flag). Loads no data;
// the state persists until the user picks a real city from the selector.
function showCityNotFound(cityId) {
  const lookup = {
    city: cityId,
    known: false,
    description: '"' + (cityId || "That city") + '" isn\'t a tracked city. Pick one from the selector above.',
  };
  cityAvailability = lookup;
  // Unknown slug is a soft-404 (HTTP 200): keep it out of search indexes.
  if (!document.getElementById("gts-noindex")) {
    var ni = document.createElement("meta");
    ni.setAttribute("name", "robots");
    ni.setAttribute("content", "noindex");
    ni.id = "gts-noindex";
    document.head.appendChild(ni);
  }
  document.title = "City not found — Global Transit Stats";
  var cityName = document.getElementById("city-name");
  if (cityName) cityName.textContent = "City not found";
  var cityMeta = document.getElementById("city-meta");
  if (cityMeta) cityMeta.textContent = lookup.description;
  // A tracked city temporarily held out of /api/cities by the health gate is
  // NOT "not a tracked city". Probe a cheap known-city endpoint (404 ⇔ truly
  // unknown id) and upgrade the copy when the city exists in the feed catalog.
  if (cityId) {
    fetch(API + "/shapes/status?city=" + encodeURIComponent(cityId))
      .then(function (r) {
        if (!r.ok) return;
        // A new lookup (even for the same slug) or another view supersedes this response.
        if (cityAvailability !== lookup || currentRoute().city !== cityId ||
            !cityName || cityName.textContent !== "City not found") return;
        lookup.known = true;
        lookup.description = '"' + cityId + '" is tracked but temporarily unavailable (feed issue). It returns automatically once its feed recovers — or pick another city above.';
        cityName.textContent = "Temporarily unavailable";
        { var ni2 = document.getElementById("gts-noindex"); if (ni2) ni2.remove(); }
        if (cityMeta) cityMeta.textContent = lookup.description;
        updatePageSeo();
      })
      .catch(function () { /* keep the not-found copy */ });
  }
  var feedHealth = document.getElementById("feed-health");
  if (feedHealth) { feedHealth.textContent = ""; feedHealth.className = ""; }
  var favStar = document.getElementById("fav-star");
  if (favStar) favStar.hidden = true;
  var feedNote = document.getElementById("feed-note");
  if (feedNote) feedNote.textContent = "";
  document.querySelectorAll("[data-live-only]").forEach(function (el) { el.hidden = true; });
  var sched = document.getElementById("scheduled-card");
  if (sched) sched.hidden = true;
}

async function renderScheduledCity(c) {
  const health = document.getElementById("feed-health");
  health.className = "scheduled";
  health.textContent = "● Timetable only — this agency publishes no live vehicle tracking";
  const body = document.getElementById("scheduled-body");
  scheduledLayer.clearLayers();
  let data;
  try { data = await getJSON(`/public/scheduled/${c.id}`); }
  catch (e) {
    body.innerHTML = '<p class="muted">A schedule for this city isn\'t available yet.</p>';
    return;
  }
  // Lazy build in progress: the index is being prepared on first view. Show a
  // status + retry (only while this city is still selected).
  if (data && data.status && data.status !== "ready") {
    if (data.status === "unavailable") {
      body.innerHTML = '<p class="muted">No published schedule for this city yet.</p>';
      return;
    }
    body.innerHTML = '<p class="muted">Preparing this city\'s schedule… (first load only)</p>';
    setTimeout(() => { if (current === c.id) renderScheduledCity(c); }, 4000);
    return;
  }
  // Stop dots — zoom-aware clustering (S7): at low zoom show fewer, clustered
  // dots with count labels; at high zoom show every individual stop (the
  // Google-like click-a-stop behavior). This keeps the map readable for cities
  // with thousands of stops (e.g. national feeds with 50k+ stops).
  renderSchedStops(data.stops || [], c.id);
  // Re-cluster on every zoom change while this city is still selected.
  if (!map._schedZoomBound) {
    map._schedZoomBound = true;
    map.on("zoomend", () => {
      if (current && scheduledLayer && window._schedStopsData && window._schedStopsCity === current) {
        renderSchedStops(window._schedStopsData, window._schedStopsCity);
      }
    });
  }
  window._schedStopsData = data.stops || [];
  window._schedStopsCity = c.id;
  // Frame the network on the 5th–95th percentile of stop coordinates so a few
  // outlying stops can't zoom the whole city into a speck (F2-17).
  fitScheduledBounds(data.stops || []);
  renderScheduledBody(data);
  // Lazy route polylines (derived from the schedule on view; drawn UNDER the
  // stop dots, in routeLayer). Best-effort — the page is fine without them.
  loadScheduledShapes(c.id);
}

// Percentile-based fit for scheduled networks: outlier stops must not set the
// frame (F2-17). Falls back silently when there are too few stops to judge.
function fitScheduledBounds(stops) {
  if (!map) return;
  const pts = (stops || []).filter((p) => Array.isArray(p) && p.length >= 2 &&
    typeof p[0] === "number" && typeof p[1] === "number");
  if (pts.length < 8) return;
  const q = (arr, f) => arr[Math.max(0, Math.min(arr.length - 1, Math.round(f * (arr.length - 1))))];
  const lats = pts.map((p) => p[0]).sort((a, b) => a - b);
  const lons = pts.map((p) => p[1]).sort((a, b) => a - b);
  try {
    const b = L.latLngBounds([q(lats, 0.05), q(lons, 0.05)], [q(lats, 0.95), q(lons, 0.95)]);
    if (b.isValid()) map.fitBounds(b.pad(0.12));
  } catch (e) { /* keep the config center/zoom */ }
}

// S7 — zoom-aware stop rendering. At zoom < SCHED_STOP_ZOOM_FULL, subsample
// stops so the canvas layer stays readable. At full zoom show every stop dot.
const SCHED_STOP_ZOOM_FULL = 14;  // individual dots above this zoom
const SCHED_STOP_CLUSTER_SIZE = 0.02; // ~2km grid cell for cluster grouping
function renderSchedStops(stops, cityId) {
  if (!scheduledLayer) return;
  scheduledLayer.clearLayers();
  if (!stops || !stops.length) return;
  const zoom = map ? map.getZoom() : 12;
  if (zoom >= SCHED_STOP_ZOOM_FULL) {
    // High zoom — show every individual stop dot with click-to-popup.
    stops.forEach((pt) => {
      if (!Array.isArray(pt) || pt.length < 2) return;
      const sid = pt[2];
      const m = L.circleMarker([pt[0], pt[1]], {
        renderer: scheduledRenderer, radius: 3.5, weight: 1.2, color: "#ffffff", opacity: 0.9,
        fillColor: "#5c6776", fillOpacity: 0.75, interactive: !!sid,
      });
      if (sid != null && sid !== "") {
        m.on("click", () => openStopPopup(m, cityId, sid));
      }
      scheduledLayer.addLayer(m);
    });
  } else {
    // Low zoom — cluster nearby stops into grid cells and show a count dot.
    // Grid-cell size scales with zoom so clusters dissolve smoothly.
    const cellSz = SCHED_STOP_CLUSTER_SIZE * Math.pow(2, SCHED_STOP_ZOOM_FULL - zoom);
    const cells = {};
    stops.forEach((pt) => {
      if (!Array.isArray(pt) || pt.length < 2) return;
      const gLat = Math.round(pt[0] / cellSz);
      const gLon = Math.round(pt[1] / cellSz);
      const key = gLat + "," + gLon;
      if (!cells[key]) cells[key] = { lat: 0, lon: 0, n: 0, rep: pt[2] };
      cells[key].lat += pt[0]; cells[key].lon += pt[1]; cells[key].n++;
    });
    Object.values(cells).forEach((cell) => {
      const lat = cell.lat / cell.n;
      const lon = cell.lon / cell.n;
      const r = Math.min(4 + Math.log2(cell.n) * 1.5, 14);  // radius grows with count
      const m = L.circleMarker([lat, lon], {
        renderer: scheduledRenderer, radius: r, weight: 1.5, color: "#ffffff", opacity: 0.85,
        fillColor: cell.n === 1 ? "#5c6776" : "#2563eb", fillOpacity: 0.65, interactive: true,
      });
      m.bindTooltip(cell.n === 1 ? "1 stop" : cell.n + " stops — zoom in to see individually",
        { direction: "top", sticky: true });
      // At cluster zoom, clicking zooms in instead of showing stop departures.
      m.on("click", () => { if (map) map.setView([lat, lon], Math.min(map.getZoom() + 3, SCHED_STOP_ZOOM_FULL), { animate: true }); });
      scheduledLayer.addLayer(m);
    });
  }
}

let schedRouteIndex = {};   // route_id -> [polylines]
let schedActiveRoute = null;
async function loadScheduledShapes(city) {
  if (!routeLayer) return;
  let sh;
  try { sh = await getJSON(`/public/scheduled/${city}/shapes`); }
  catch (e) { return; }
  if (current !== city) return; // user switched cities while we were fetching
  routeLayer.clearLayers();
  schedRouteIndex = {};
  schedActiveRoute = null;
  // Scheduled cities have no shapes.txt: these paths are drawn through the stop
  // sequence, so terrain sampled along them is stop-sequence terrain, not road.
  currentGeometrySource = (sh.routes && sh.routes.length) ? "scheduled" : "none";
  currentNoGeometry = !(sh.routes && sh.routes.length);
  (sh.routes || []).forEach((r) => {
    if (!r.path || r.path.length < 2) return;
    const col = MODE_LINE_COLOR[r.mode] || MODE_LINE_COLOR.other;
    // Dashed schematic: these paths are stop-sequence diagrams, not street
    // traces — the dash says so at a glance (F2-17).
    const ln = L.polyline(r.path, { color: col, weight: 2, opacity: 0.5, dashArray: "4 6", interactive: true });
    ln._base = { color: col, weight: 2, opacity: 0.5 };
    ln._rid = r.route_id;
    const tip = document.createElement("span");
    tip.textContent = (r.label || r.route_id) +
      (r.mode ? " · " + (MODE_LABEL[r.mode] || "") : "");
    ln.bindTooltip(tip, { sticky: true });
    ln.on("click", (ev) => { if (ev.originalEvent && L.DomEvent) L.DomEvent.stopPropagation(ev.originalEvent); toggleSchedRoute(r.route_id); });
    (schedRouteIndex[r.route_id] = schedRouteIndex[r.route_id] || []).push(ln);
    routeLayer.addLayer(ln);
  });
  // Clear the focus when clicking empty map.
  if (map && !map._schedClearBound) {
    map._schedClearBound = true;
    map.on("click", () => clearSchedRoute());
  }
}

// Route-focused mode: emphasize one route, dim the rest + the stop dots, so the
// network becomes understandable instead of a flat overlay (stand-in's top ask).
function highlightSchedRoute(rid) {
  if (!schedRouteIndex[rid]) return;
  schedActiveRoute = rid;
  Object.keys(schedRouteIndex).forEach((k) => {
    schedRouteIndex[k].forEach((ln) => {
      if (k === rid) { ln.setStyle({ weight: 6, opacity: 1, color: ln._base.color }); ln.bringToFront(); }
      else ln.setStyle({ opacity: 0.12 });
    });
  });
  if (scheduledLayer) scheduledLayer.eachLayer((m) => { if (m.setStyle) m.setStyle({ fillOpacity: 0.12 }); });
  // Frame the route.
  try {
    const grp = L.featureGroup(schedRouteIndex[rid]);
    if (grp.getBounds && grp.getBounds().isValid()) map.fitBounds(grp.getBounds().pad(0.15));
  } catch (e) { /* ignore */ }
  // Reflect selection in the table.
  document.querySelectorAll("#sched-route-rows tr").forEach((tr) =>
    tr.classList.toggle("rte-active", tr.dataset.rid === String(rid)));
}
function clearSchedRoute() {
  schedActiveRoute = null;
  Object.values(schedRouteIndex).forEach((arr) => arr.forEach((ln) =>
    ln.setStyle({ color: ln._base.color, weight: ln._base.weight, opacity: ln._base.opacity })));
  if (scheduledLayer) scheduledLayer.eachLayer((m) => { if (m.setStyle) m.setStyle({ fillOpacity: 0.6 }); });
  document.querySelectorAll("#sched-route-rows tr.rte-active").forEach((tr) => tr.classList.remove("rte-active"));
}

// S7 — departures detail drawer: click the ▸ button on a scheduled route row
// to expand an inline panel below it with stop sequence + today's schedule info.
// Fetches from /api/route-detail (already handles scheduled cities, returns
// directions + stops + schedule; graceful fallback when absent).
// Both route-detail readers use one disclosure: long directions remain complete
// on narrow cards, and every choice is a native keyboard-operable button.
function routeDirectionsHtml(dirs, stopListHtml) {
  const labels = dirs.map((dir, i) => (dir.headsign ||
    (dir.direction_id != null ? "Direction " + dir.direction_id : "Direction " + i)) +
    " (" + (dir.stops || []).length + " stops)");
  return '<div class="rb-dir-wrap route-directions">' +
    '<details class="route-direction-menu"><summary>Direction: <span class="route-direction-current">' +
    esc(labels[0]) + '</span></summary><div class="route-direction-options" role="group" aria-label="Route directions">' +
    dirs.map((dir, i) => '<button type="button" class="route-direction-option" data-direction-index="' + i +
      '" aria-pressed="' + (i === 0 ? "true" : "false") + '">' + esc(labels[i]) + '</button>').join("") +
    '</div></details>' + dirs.map((dir, i) =>
      '<section class="route-direction-panel" data-direction-index="' + i + '" aria-label="' + esc(labels[i]) + '"' +
      (i === 0 ? '' : ' hidden') + '>' + (stopListHtml(dir.stops) ||
        '<p class="muted">No stops published for this direction.</p>') + '</section>').join("") + '</div>';
}

function wireRouteDirections(host) {
  host.querySelectorAll(".route-directions").forEach((wrap) => {
    const menu = wrap.querySelector(".route-direction-menu");
    const summary = menu.querySelector("summary");
    const options = Array.from(menu.querySelectorAll(".route-direction-option"));
    options.forEach((button) => {
      button.onclick = () => {
        const index = button.getAttribute("data-direction-index");
        options.forEach((option) => option.setAttribute("aria-pressed", option === button ? "true" : "false"));
        wrap.querySelectorAll(".route-direction-panel").forEach((panel) => {
          panel.hidden = panel.getAttribute("data-direction-index") !== index;
        });
        summary.querySelector(".route-direction-current").textContent = button.textContent;
        menu.open = false;
        summary.focus();
      };
    });
    menu.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && menu.open) {
        event.preventDefault(); event.stopPropagation();
        menu.open = false; summary.focus();
      }
    });
  });
}

let _schedDetOpen = null;  // currently-open route_id for the drawer
function _schedDetRow(rid) {
  // Return the paired detail-drawer <tr> for this route_id.
  return document.querySelector("#sched-route-rows tr.sched-det-row[data-rid=\"" + rid.replace(/\\/g, "\\\\").replace(/"/g, "\\\"") + "\"]");
}
function _schedDetBtn(rid) {
  // Return the ▸ detail button in the data row for this route_id.
  const rows = document.querySelectorAll("#sched-route-rows tr:not(.sched-det-row)[data-rid]");
  for (const tr of rows) {
    if (tr.getAttribute("data-rid") === rid) return tr.querySelector(".sched-det-btn");
  }
  return null;
}
async function toggleSchedRouteDetail(rid, cityId) {
  if (!rid || !cityId) return;
  const detRow = _schedDetRow(rid);
  const btn = _schedDetBtn(rid);
  if (!detRow) return;
  // Toggle: if this drawer is already open, close it.
  if (_schedDetOpen === rid && !detRow.hidden) {
    detRow.hidden = true;
    _schedDetOpen = null;
    if (btn) { btn.textContent = "▸"; btn.setAttribute("aria-expanded", "false"); }
    return;
  }
  // Close any previously-open drawer.
  if (_schedDetOpen) {
    const prev = _schedDetRow(_schedDetOpen);
    if (prev) prev.hidden = true;
    const prevBtn = _schedDetBtn(_schedDetOpen);
    if (prevBtn) { prevBtn.textContent = "▸"; prevBtn.setAttribute("aria-expanded", "false"); }
  }
  _schedDetOpen = rid;
  detRow.hidden = false;
  if (btn) { btn.textContent = "▾"; btn.setAttribute("aria-expanded", "true"); }
  const body = detRow.querySelector(".sched-det-body");
  if (body) body.innerHTML = '<p class="muted">Loading…</p>';
  let d;
  try { d = await getJSON(`/route-detail?city=${encodeURIComponent(cityId)}&route_id=${encodeURIComponent(rid)}`); }
  catch (e) {
    if (body) body.innerHTML = '<p class="muted">Schedule detail unavailable for this route.</p>';
    return;
  }
  if (!body) return;
  // Render: schedule summary + directions (or flat stop list).
  let h = "";
  if (d.schedule) {
    const s = d.schedule;
    const days = s.service_days && s.service_days.length === 7 ? "daily"
      : (s.service_days || []).map((x) => x[0].toUpperCase() + x.slice(1)).join(" ");
    h += `<p class="sched-det-sched">`;
    if (days) h += `Runs ${esc(days)}. `;
    if (s.departures_today) {
      h += `${s.departures_today} departure${s.departures_today === 1 ? "" : "s"} today`;
      if (s.first_departure) h += ` (first ${esc(s.first_departure)}, last ${esc(s.last_departure || "—")})`;
      h += ". ";
    } else {
      h += "No departures scheduled today. ";
    }
    if (s.typical_headway_min) h += `Every ~${s.typical_headway_min} min.`;
    h += `</p>`;
  }
  // Multiple directions share the route-browser disclosure; single directions
  // and legacy responses keep the plain stop-list fallback.
  function _stopListHtml(stops) {
    if (!stops || !stops.length) return "";
    return '<ol class="sched-det-stops" tabindex="0" aria-label="Route stops">' + stops.map((s) =>
      `<li>${esc(s.name || s.stop_id || "Stop")}</li>`
    ).join("") + "</ol>";
  }
  const dirs = Array.isArray(d.directions) ? d.directions : [];
  if (dirs.length >= 2) {
    h += routeDirectionsHtml(dirs, _stopListHtml);
  } else if (dirs.length === 1) {
    const stops = dirs[0].stops || [];
    if (stops.length) {
      const label = dirs[0].headsign ? ` towards ${esc(dirs[0].headsign)}` : "";
      h += `<details class="rb-stops"><summary>${stops.length} stops${label}</summary>${_stopListHtml(stops)}</details>`;
    }
  } else if (d.stops && d.stops.length) {
    h += `<details class="rb-stops"><summary>${d.stops.length} stops</summary>${_stopListHtml(d.stops)}</details>`;
  }
  if (!h) h = '<p class="muted">No stop detail available for this route.</p>';
  body.innerHTML = h;
  wireRouteDirections(body);
}
function toggleSchedRoute(rid) {
  if (schedActiveRoute === rid) clearSchedRoute(); else highlightSchedRoute(rid);
}

// Click a scheduled stop → popup of its next scheduled departures (lazy-fetched).
async function openStopPopup(marker, city, stopId) {
  marker.bindPopup('<div class="stop-pop"><p class="muted">Loading schedule…</p></div>',
    { minWidth: 210, maxWidth: 280 }).openPopup();
  let s;
  try { s = await getJSON(`/public/scheduled/${city}/stop/${encodeURIComponent(stopId)}`); }
  catch (e) { marker.setPopupContent('<div class="stop-pop"><p class="muted">No schedule for this stop.</p></div>'); return; }
  marker.setPopupContent(buildStopPopupHtml(s));
}

function buildStopPopupHtml(s) {
  let h = `<div class="stop-pop"><div class="stop-pop-h">${esc(s.stop_name || "Stop")}` +
    `<span class="sched-tag">scheduled</span></div>`;
  if (s.departures && s.departures.length) {
    h += `<p class="stop-pop-sub stop-pop-sched">${uiIcon("clock", 11)} Scheduled departures · no live delay data</p><ul class="sched-next">`;
    s.departures.slice(0, 8).forEach((d) => {
      const col = MODE_LINE_COLOR[d.mode] || MODE_LINE_COLOR.other;
      const head = d.headsign ? `<span class="stop-dest">${esc(d.headsign)}</span>` : "";
      h += `<li><span class="t">${esc(d.time || "")}</span>` +
        `<span class="rte-badge" style="background:${col}">${esc(d.label || "")}</span>${head}</li>`;
    });
    h += `</ul>`;
    // Non-interactive, muted — clearly a count, not a clickable "+N".
    if (s.n_upcoming > 8) h += `<p class="stop-pop-sub">${s.n_upcoming - 8} more scheduled today</p>`;
  } else if (s.n_today > 0) {
    h += `<p class="muted">No more scheduled departures today (${s.n_today} earlier).</p>`;
  } else {
    h += `<p class="muted">No scheduled service at this stop today.</p>`;
  }
  return h + `</div>`;
}

function renderScheduledBody(d) {
  const body = document.getElementById("scheduled-body");
  const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString());
  let html = "";
  // Honest framing first: what this is and isn't. The second sentence is the
  // anti-overtrust line (stand-in's key ask) — never imply the bus is actually coming.
  html += `<p class="sched-note">Routes, stops and timetable from ${esc(d.agency || "the agency")}'s published schedule — ` +
    `the same way most cities appear on global transit maps. ` +
    `<strong>Actual vehicle locations and delays are unknown</strong> — these are timetable times only.</p>`;
  html += `<div class="sched-summary">` +
    `<div class="now"><span class="big">${fmt(d.n_routes)}</span><label>routes</label></div>` +
    `<div class="now"><span class="big">${fmt(d.n_stops)}</span><label>stops</label></div>` +
    (d.calendar_supported
      ? `<div class="now"><span class="big">${fmt(d.n_trips_today)}</span><label>trips today</label></div>`
      : "") +
    `</div>`;
  if (!d.calendar_supported) {
    html += `<p class="sched-note">This feed has no service calendar, so a per-day timetable isn't available — the full route list is shown below.</p>`;
  }
  // Next departures (only when we have a real service day). Labelled as
  // TIMETABLE times, never live predictions (the key trust fix).
  if (d.next_departures && d.next_departures.length) {
    const tzLine = `Timetable times in ${esc(d.name || "local")} local time — not live arrival predictions.`;
    html += `<h4 class="sched-h">Next scheduled departures</h4>` +
      `<p class="sched-sub">${tzLine}</p><ul class="sched-next">`;
    d.next_departures.slice(0, 12).forEach((n) => {
      const col = MODE_LINE_COLOR[n.mode] || MODE_LINE_COLOR.other;
      html += `<li><span class="t">${esc(n.time || "")}</span>` +
        `<span class="rte-badge" style="background:${col}">${esc(n.label || "")}</span>` +
        `<span class="muted">${esc(MODE_LABEL[n.mode] || "")}</span></li>`;
    });
    html += `</ul>`;
  }
  // Route list with service span + typical frequency, mode filter chips (S7
  // parity with route-browser v2), text search, and departures detail drawer.
  if (d.routes && d.routes.length) {
    const spanHdr = d.calendar_supported ? "Scheduled service today" : "Service span";
    // Dead-column pruning (F2-18): when the feed has no calendar (or simply
    // carries no span/frequency data), don't ship columns of "—" — the note
    // above the table already explains why.
    const hasSpan = d.routes.some((r) => r.first_departure && r.last_departure);
    const hasEvery = d.routes.some((r) => r.typical_headway_min);
    // S7 — mode filter chips: auto-built from modes present in this city's
    // route list (parity with route-browser v2). Compose with text search.
    const modesPresent = [...new Set(d.routes.map((r) => r.mode || "other"))];
    const modeChipsHtml = modesPresent.length > 1
      ? `<div class="sched-mode-chips" id="sched-mode-chips" role="group" aria-label="Filter by mode">` +
        `<button type="button" class="mode-chip active" data-m="all" aria-pressed="true">All modes</button>` +
        modesPresent.map((m) => {
          const cnt = d.routes.filter((r) => (r.mode || "other") === m).length;
          return `<button type="button" class="mode-chip" data-m="${esc(m)}" aria-pressed="false">${esc(MODE_LABEL[m] || m)} (${cnt})</button>`;
        }).join("") + `</div>` : "";
    html += `<h4 class="sched-h">Routes</h4>` + modeChipsHtml +
      `<input type="search" id="sched-route-filter" class="sched-filter" placeholder="Search routes…" autocomplete="off" aria-label="Search routes">` +
      `<p class="sched-sub" id="sched-route-count"></p>` +
      `<table class="sched-table"><thead><tr><th>Route</th><th>Type</th>` +
      (hasSpan ? `<th>${spanHdr}</th>` : "") + (hasEvery ? `<th>Every</th>` : "") +
      `<th></th>` +
      `</tr></thead><tbody id="sched-route-rows">`;
    d.routes.forEach((r) => {
      const col = MODE_LINE_COLOR[r.mode] || MODE_LINE_COLOR.other;
      const span = (r.first_departure && r.last_departure)
        ? `${esc(r.first_departure)}–${esc(r.last_departure)}` : "—";
      const every = r.typical_headway_min ? `~${r.typical_headway_min} min` : "—";
      const key = `${(r.label || "")} ${MODE_LABEL[r.mode] || ""}`.toLowerCase();
      const modeKey = r.mode || "other";
      html += `<tr data-k="${esc(key)}" data-rid="${esc(r.route_id || "")}" data-mode="${esc(modeKey)}">` +
        `<td><span class="rte-badge" style="background:${col}">${esc(r.label || "")}</span></td>` +
        `<td>${esc(MODE_LABEL[r.mode] || "Other")}</td>` +
        (hasSpan ? `<td>${span}</td>` : "") + (hasEvery ? `<td>${every}</td>` : "") +
        `<td><button type="button" class="sched-det-btn" aria-label="Details for route ${esc(r.label || r.route_id || "")}">▸</button></td>` +
        `</tr>` +
        // Departures detail drawer row (S7): initially hidden, filled on demand.
        `<tr class="sched-det-row" data-rid="${esc(r.route_id || "")}" hidden><td colspan="${3 + Number(hasSpan) + Number(hasEvery)}"><div class="sched-det-body"><p class="muted">Loading…</p></div></td></tr>`;
    });
    html += `</tbody></table>`;
    if (d.routes_truncated) {
      html += `<p class="sched-note">Showing the first ${d.routes.length} of ${fmt(d.n_routes)} routes.</p>`;
    }
  } else {
    html += `<p class="muted">No routes found in this city's schedule.</p>`;
  }
  // Provenance block — source, service date, schedule freshness, realtime status.
  // This is the credibility layer: it states plainly what the data is and that
  // no realtime feed exists.
  html += `<dl class="sched-prov">` +
    `<div><dt>Source</dt><dd>${esc(d.agency || "Agency")} GTFS schedule</dd></div>` +
    (d.service_date ? `<div><dt>Service date</dt><dd>${esc(prettyYmd(d.service_date))}</dd></div>` : "") +
    (d.schedule_updated ? `<div><dt>Schedule updated</dt><dd>${esc(d.schedule_updated)}</dd></div>` : "") +
    `<div class="rt"><dt>Realtime feed</dt><dd>not published</dd></div>` +
    `</dl>`;
  body.innerHTML = html;
  // --- S7: mode filter chips ---
  let schedModeActive = "all";
  function applySchedFilters() {
    const q = (document.getElementById("sched-route-filter") || {}).value
      ? document.getElementById("sched-route-filter").value.toLowerCase().trim() : "";
    let shown = 0;
    document.querySelectorAll("#sched-route-rows tr[data-rid]:not(.sched-det-row)").forEach((tr) => {
      const modeOk = schedModeActive === "all" || (tr.dataset.mode || "other") === schedModeActive;
      const textOk = !q || (tr.dataset.k || "").includes(q);
      const hit = modeOk && textOk;
      tr.style.display = hit ? "" : "none";
      // Also hide/show the paired detail drawer row.
      const rid = tr.dataset.rid;
      if (rid) {
        const det = document.querySelector(`#sched-route-rows tr.sched-det-row[data-rid="${CSS.escape(rid)}"]`);
        if (det) det.hidden = !hit || det.hidden;
      }
      if (hit) shown++;
    });
    const cnt = document.getElementById("sched-route-count");
    if (cnt) {
      const qLabel = q ? `"${document.getElementById("sched-route-filter").value.trim()}"` : null;
      const mLabel = schedModeActive !== "all" ? (MODE_LABEL[schedModeActive] || schedModeActive) : null;
      if (qLabel || mLabel) {
        const parts = [mLabel, qLabel].filter(Boolean).join(" + ");
        cnt.textContent = `${shown} route${shown === 1 ? "" : "s"} match ${parts}`;
      } else {
        cnt.textContent = "";
      }
    }
  }
  document.querySelectorAll("#sched-mode-chips .mode-chip").forEach((btn) => {
    btn.addEventListener("click", () => {
      schedModeActive = btn.getAttribute("data-m") || "all";
      document.querySelectorAll("#sched-mode-chips .mode-chip").forEach((b) => {
        b.classList.toggle("active", b === btn);
        b.setAttribute("aria-pressed", b === btn ? "true" : "false");
      });
      applySchedFilters();
    });
  });
  // Wire the route text filter (client-side; no request).
  const f = document.getElementById("sched-route-filter");
  if (f) f.addEventListener("input", applySchedFilters);

  // --- Click a route row → highlight on map + toggle departures detail drawer ---
  document.querySelectorAll("#sched-route-rows tr[data-rid]:not(.sched-det-row)").forEach((tr) => {
    tr.style.cursor = "pointer";
    // Map highlight: click anywhere on the data row.
    tr.addEventListener("click", (e) => {
      const rid = tr.dataset.rid;
      if (!rid) return;
      // Detail-btn click: open/close drawer without toggling map highlight.
      if (e.target && e.target.classList.contains("sched-det-btn")) {
        toggleSchedRouteDetail(rid, d.city || (window._schedStopsCity));
        return;
      }
      toggleSchedRoute(rid);
    });
    // Keyboard: Enter/Space on the detail button only.
    const btn = tr.querySelector(".sched-det-btn");
    if (btn) {
      btn.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggleSchedRouteDetail(tr.dataset.rid, d.city || (window._schedStopsCity));
        }
      });
    }
  });
}

// "20260626" -> "Jun 26, 2026" (best-effort; falls back to the raw string).
function prettyYmd(ymd) {
  const m = /^(\d{4})(\d{2})(\d{2})$/.exec(ymd || "");
  if (!m) return ymd || "";
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (isNaN(dt.getTime())) return ymd;
  return dt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// ── "now" stat-card state helpers ──────────────────────────────────────────
// A card is either a live numeric value, a transient "Loading…" state (set on a
// city switch, never on a periodic refresh), or an honest "Not available" when
// the feed publishes no gradeable delay data. Reserving "—"/"Not available" for
// genuinely-absent data keeps an in-flight fetch from reading as broken.
function setNowCard(id, text) {
  var e = document.getElementById(id);
  if (e) {
    e.classList.remove("loading", "na");
    e.textContent = text;
  }
}

function setNowCardNA(id) {
  var e = document.getElementById(id);
  if (e) {
    e.classList.remove("loading");
    e.classList.add("na");
    e.textContent = "Not available";
  }
}

function setNowCardsLoading() {
  const note = document.getElementById("now-grading");
  if (note) note.textContent = "Loading delay-status coverage…";
  var ids = ["now-vehicles", "now-late", "now-ontime"];
  for (var i = 0; i < ids.length; i++) {
    var e = document.getElementById(ids[i]);
    if (e) {
      e.classList.remove("na");
      e.classList.add("loading");
      e.textContent = "Loading…";
    }
  }
}

function renderNowCards(shown, late, ontime, early, total) {
  const fmt = (n) => Number(n).toLocaleString();
  const filtered = !mapMode.has("all") || !mapAgency.has("all");
  setNowCard("now-vehicles", filtered ? `${fmt(shown)} of ${fmt(total)} live` : fmt(total));
  const graded = late + ontime + early;
  const note = document.getElementById("now-grading");
  if (note) note.textContent = `Among shown vehicles: ${fmt(graded)} with delay status · ${fmt(early)} early · ${fmt(Math.max(0, shown - graded))} without delay status.`;
  if (graded === 0) {
    setNowCardNA("now-late");
    setNowCardNA("now-ontime");
  } else {
    setNowCard("now-late", fmt(late));
    setNowCard("now-ontime", fmt(ontime));
  }
  // Tint the late figure red only when lateness is actually notable (≥20% of
  // graded vehicles) — a routine handful of late buses isn't an alarm (F2-14).
  const lateEl = document.getElementById("now-late");
  if (lateEl) lateEl.classList.toggle("bad", graded > 0 && late / graded >= 0.2);
}

/* ── F4: persistent vehicle markers (Google-Maps-style migration) ──
   Markers are diffed by vehicle id every poll instead of clear-and-rebuilt:
   existing markers migrate via setLatLng (a CSS transition in vehicle.css
   animates the move), new vehicles are added, vanished ones removed — so an
   open popup survives refreshes and updates its content in place. The 5,000-
   vehicle cap is the API's (LIMIT 5000 in /api/vehicles); every returned row
   is rendered, same as before. */
let _vehMarkers = new Map();   // vehicle key -> L.marker
let _vehSeq = 0;               // stale-response guard for overlapping polls
// F10 — last successful /api/vehicles payload (city+scope+timestamp), reused
// by loadModes' empty-aggregate fallback instead of a second full fetch.
let _lastVehData = null;

function _vehKey(v, i, seq) {
  const a = v.agency || "";
  if (v.vehicle_id != null && v.vehicle_id !== "") return "v|" + a + "|" + v.vehicle_id;
  if (v.trip_id != null && v.trip_id !== "") return "t|" + a + "|" + v.trip_id;
  // No stable id: the key is poll-unique (seq) so it can NEVER glue an old
  // marker to a different vehicle next poll — id-less rows rebuild per poll.
  return "i|" + seq + "|" + i;
}
function clearVehicleMarkers() {
  if (vehicleLayer) vehicleLayer.clearLayers();
  _vehMarkers.clear();
}

/* ── F5: feed-status line follows the vehicle poll ──
   "● Feed live · updated HH:MM" refreshes on every successful poll; after 3
   consecutive failed polls the line switches to a visible stale state until a
   poll succeeds again. Scheduled cities keep their own status line. */
let _vehFailStreak = 0;
let _lastVehOkTime = null;
let _vehStale = false;   // true while the visible stale warning is shown
function vehiclePollSucceeded(count) {
  _vehFailStreak = 0;
  _lastVehOkTime = new Date();
  const el = document.getElementById("feed-health");
  if (!el || document.body.classList.contains("scheduled-mode")) return;
  // A 200 with an EMPTY vehicle list keeps selectCity's honest "no usable
  // data yet" copy — unless the stale warning is up, which is now wrong.
  if (!count && !_vehStale && el.className !== "ok") return;
  _vehStale = false;
  el.className = "ok";
  el.textContent = "● Feed live · updated " + _lastVehOkTime.toLocaleTimeString();
}
function vehiclePollFailed() {
  _vehFailStreak++;
  if (_vehFailStreak < 3) return;
  const el = document.getElementById("feed-health");
  if (!el || document.body.classList.contains("scheduled-mode")) return;
  _vehStale = true;
  el.className = "down";
  el.textContent = "⚠ data may be stale — " + (_lastVehOkTime
    ? "last update " + _lastVehOkTime.toLocaleTimeString()
    : "no update received yet");
}

async function loadVehicles() {
  if (!current) return;
  const seq = ++_vehSeq;
  const reqCity = current, reqScope = scope;
  let data;
  try { data = await getJSON(`/vehicles?city=${reqCity}&scope=${reqScope}`); }
  catch (e) { vehiclePollFailed(); return; }
  // Drop stale responses: a newer poll started, or the city/scope moved on
  // while this one was in flight (protects the marker diff from races).
  if (seq !== _vehSeq || current !== reqCity || scope !== reqScope) return;
  _lastVehData = { city: reqCity, scope: reqScope, t: Date.now(), data };
  vehiclePollSucceeded(data.count);
  // Cache the city's agency list (multi-agency clusters) for chip building.
  cityAgencies = data.agencies || [];
  // Count by mode for chip badges + 0-count disabling. Mode counts respect the
  // active agency filter and agency counts respect the active mode filter, so
  // each chip row shows what is reachable given the OTHER dimension's selection.
  const modeCounts = {}, agencyCounts = {};
  data.vehicles.forEach((v) => {
    const m = v.mode || "other";
    if (agencyFilterPass(v.agency_id)) modeCounts[m] = (modeCounts[m] || 0) + 1;
    if (v.agency_id && modeFilterPass(m)) {
      agencyCounts[v.agency_id] = (agencyCounts[v.agency_id] || 0) + 1;
    }
  });
  let late = 0, ontime = 0, early = 0, shown = 0;
  const seen = new Set();
  data.vehicles.forEach((v, i) => {
    const vmode = v.mode || "other";
    // Compose agency AND mode: a vehicle shows only if it passes BOTH filters.
    if (!modeFilterPass(vmode)) return;
    if (!agencyFilterPass(v.agency_id)) return;
    shown++;
    if (v.status === "late") late++;
    else if (v.status === "on_time") ontime++;
    else if (v.status === "early") early++;
    const key = _vehKey(v, i, seq);
    seen.add(key);
    let m = _vehMarkers.get(key);
    if (m) {
      // Existing vehicle: migrate the marker in place (CSS animates the move).
      m.setLatLng([v.lat, v.lon]);
      // Replace the icon ONLY when the visual state changed — setIcon rebuilds
      // the DOM node, which would cut the migration animation short.
      const sig = (v.status || "") + "|" + vmode + "|" + (v.route_color || "");
      if (m._tpIconSig !== sig) { m.setIcon(vehicleIcon(v)); m._tpIconSig = sig; }
      m._tpVeh = v;
      m._tpRouteId = v.route_id;
      // An open popup follows its marker; re-render it in place from the lazy
      // content function (never replace that function with a static string, or
      // a later reopen would show this poll's delay/age instead of the latest).
      if (m.isPopupOpen()) m.getPopup().update();
    } else {
      const altText = window.TP_MARKER.vehicleAlt(v);
      // keyboard:false keeps ~1500 vehicle markers OUT of the tab order (they'd
      // otherwise be 1500+ tab stops with no bypass — a keyboard/AT operability
      // wall). The aggregate data is all reachable as text in the side panel.
      m = L.marker([v.lat, v.lon], { icon: vehicleIcon(v), alt: altText, title: altText, keyboard: false });
      m._tpIconSig = (v.status || "") + "|" + vmode + "|" + (v.route_color || "");
      m._tpVeh = v;
      // Popup content is built lazily on open from the marker's LATEST data,
      // so closed popups cost nothing per poll and are never stale on open.
      m.bindPopup((layer) => buildPopup(layer._tpVeh));
      // TASK D: clicking a vehicle highlights its route polyline (match by route_id).
      m._tpRouteId = v.route_id;
      m.on("click", function () { if (m._tpRouteId) highlightRoute(m._tpRouteId); });
      vehicleLayer.addLayer(m);
      _vehMarkers.set(key, m);
    }
    // Preserve route focus across the 20s refresh: dots off the focused route
    // stay/start dimmed so the highlight doesn't flash back to full.
    if (typeof m.setOpacity === "function") {
      m.setOpacity(highlightedRoute && v.route_id !== highlightedRoute ? 0.25 : 1);
    }
  });
  // Remove markers whose vehicle vanished or no longer passes the filters
  // (removing a marker with an open popup closes that popup, as before).
  _vehMarkers.forEach((m, key) => {
    if (!seen.has(key)) {
      vehicleLayer.removeLayer(m);
      _vehMarkers.delete(key);
    }
  });
  // Fix 1/3: thousands-separated "X of Y live" when filtered; "Not available"
  // (not a misleading 0) when the feed publishes no gradeable delay data.
  renderNowCards(shown, late, ontime, early, data.count);
  // Dense-city declutter input for the zoom-class dot sizing (F2-07).
  _vehShown = shown;
  updateMapZoomClass();
  // Refresh the mode + agency chips with current counts.
  buildMapModes(modeCounts);
  buildMapAgencies(cityAgencies, agencyCounts);
}

let trendChart = null;
let chartJsPromise = null;

// Lazy-load Chart.js (208 KB) only when a trend chart with data actually needs
// to render. It's no longer in the HTML <head>, so the global Chart is absent
// until this resolves. Loads exactly once; concurrent callers share the promise.
function ensureChartJs() {
  if (window.Chart) return Promise.resolve(window.Chart);
  if (chartJsPromise) return chartJsPromise;
  chartJsPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    // Self-hosted (same-origin) chart.js — CSP script-src allows 'self', so no
    // head/CSP change is needed, and same-origin avoids the CDN CORS/redirect
    // failures that broke the jsdelivr/unpkg variants. SRI pins the exact bytes.
    script.src = "/chart.umd.min.js?v=4.4.4";
    script.integrity = "sha384-NrKB+u6Ts6AtkIhwPixiKTzgSKNblyhlk0Sohlgar9UHUBzai/sgnNNWWd291xqt";
    script.onload = () => resolve(window.Chart);
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return chartJsPromise;
}

function renderTrend(city, window, mode, scope) {
  const canvas = document.getElementById("trend-chart");
  if (!canvas) return;
  return getJSON(`/trend?city=${city}&window=${window}&mode=${mode}&scope=${scope}`).then((data) => {
    let note = canvas.parentElement && canvas.parentElement.querySelector(".trend-window-note");
    if (!note && canvas.parentElement) {
      note = document.createElement("p");
      note.className = "muted trend-window-note";
      note.style.margin = "0 0 8px";
      canvas.parentElement.insertBefore(note, canvas);
    }
    if (note) {
      // Low-coverage rollup windows get the same honesty note as the
      // reliability card: the trend is real but built on incomplete data.
      note.textContent = effectiveWindowText(data) +
        (data.coverage_basis === "feed_health" && data.low_coverage
          ? " Data coverage " + (data.coverage_pct != null ? data.coverage_pct + "%" : "unknown") +
            " for this window — treat this trend as provisional."
          : "");
    }
    if (!data.points || data.points.length === 0) {
      canvas.style.display = "none";
      const accessible = document.getElementById("trend-accessible");
      if (accessible) accessible.textContent = "No on-time trend data is available for this window.";
      canvas.setAttribute("aria-label", "No on-time trend data is available for this window.");
      return;
    }
    // Sub-day windows: time + agency-zone abbreviation. Keeping the suffix makes
    // the repeated 01:00 hour at a DST fall-back unambiguous (PDT vs PST, etc.).
    const timeOnly = window === "5m" || window === "1h" || window === "1d" || window === "24h";
    const labels = data.points.map(p => {
      const t = String(p.t);
      return (timeOnly && t.length >= 16) ? t.slice(11) : t;
    });
    const values = data.points.map(p => p.on_time_pct);
    const accessible = document.getElementById("trend-accessible");
    const numeric = values.filter(v => Number.isFinite(Number(v))).map(Number);
    if (accessible && numeric.length) {
      const latest = numeric[numeric.length - 1];
      const low = Math.min(...numeric), high = Math.max(...numeric);
      accessible.textContent = "On-time percentage trend: " + numeric.length +
        " observations; latest " + latest + "%; range " + low + "% to " + high + "%.";
      canvas.setAttribute("aria-label", accessible.textContent);
    }
    return ensureChartJs().then((Chart) => {
      if (trendChart) trendChart.destroy();
      trendChart = new Chart(canvas, {
        type: "line",
        data: {
          labels: labels,
          datasets: [
            {
              label: "On-time %",
              data: values,
              borderColor: "#1a9e4b",
              backgroundColor: "rgba(26, 158, 75, 0.1)",
              fill: true,
              tension: 0.3,
              pointRadius: 0,
              pointHoverRadius: 4,
              borderWidth: 2
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: { padding: { top: 4, right: 8, bottom: 4, left: 4 } },
          plugins: { legend: { display: false },
            tooltip: { callbacks: { label: function(c){ return 'On-time ' + c.parsed.y + '%'; } } } },
          scales: {
            y: { beginAtZero: true, max: 100, ticks: { callback: function(v){ return v + '%'; }, stepSize: 25 }, grid: { color: 'rgba(0,0,0,0.06)' } },
            x: { ticks: { maxTicksLimit: 6, maxRotation: 0, autoSkip: true }, grid: { display: false } }
          }
        }
      });
      canvas.style.display = "";
    });
  }).catch(() => { canvas.style.display = "none"; });
}

// Window-aware empty-state copy. Long windows (30d/1y/5y) are empty only because
// the poller hasn't been running that long — reassure the user it auto-fills,
// rather than implying an error. Short windows / all-time get a plain note.
function emptyWindowNote(win, since) {
  const label = (WINDOWS.find(([k]) => k === win) || [, "this"])[1];
  // Any window that needs accumulated history (30d, 3mo … 5y) is empty only
  // because the poller hasn't run that long — reassure, don't imply an error.
  if ((WINDOW_MIN_DAYS[win] || 0) >= 30) {
    return `<p class="empty-note">Not enough history yet for the ` +
      `<strong>${label}</strong> view — this poller has only been collecting since ` +
      `${since}. It will show automatically once enough days of data accumulate.</p>`;
  }
  return `<p class="empty-note">No data yet for this window. Collecting since ${since}.</p>`;
}

// Split a stats object into four punctuality shares that sum to EXACTLY 100%.
// Derives from the raw counts (on_time/late/early) rather than the pre-rounded
// *_pct fields, and adds a "no realtime delay" remainder for observations the
// feed reported with no delay value. Largest-remainder rounding keeps the four
// values summing to 100.0 even after rounding to one decimal.
function computeShares(s) {
  let n = Number(s.n_observations) || 0;
  if (n <= 0) return { onTime: 0, late: 0, early: 0, noData: 0 };
  let ot = Number(s.on_time) || 0, lt = Number(s.late) || 0, er = Number(s.early) || 0;
  let nd = Math.max(0, n - ot - lt - er);
  let rawOnTime = 100 * ot / n, rawLate = 100 * lt / n, rawEarly = 100 * er / n, rawNoData = 100 * nd / n;
  let floors = [rawOnTime, rawLate, rawEarly, rawNoData].map(v => Math.floor(v * 10));
  while (Math.floor(1000 - floors.reduce((a, b) => a + b)) > 0) {
    let i = floors.indexOf(Math.max(...floors));
    floors[i]++;
  }
  let onTime = floors[0] / 10, late = floors[1] / 10, early = floors[2] / 10, noData = floors[3] / 10;
  return { onTime, late, early, noData };
}

function renderStats(s) {
  const box = document.getElementById("stats");
  const windowNote = `<p class="muted" style="margin:8px 0">${esc(effectiveWindowText(s))}</p>`;
  if (!s.n_observations) {
    const since = s.collecting_since
      ? longDate(s.collecting_since) : "launch";
    box.innerHTML = emptyWindowNote(window_, since) + windowNote;
    return;
  }
  const sh = computeShares(s);
  let html = windowNote + `<div class="bar">` +
    `<span style="width:${sh.onTime}%;background:#1a9e4b"></span>` +
    `<span style="width:${sh.late}%;background:#e5484d"></span>` +
    `<span style="width:${sh.early}%;background:#d6921a"></span>` +
    `<span style="width:${sh.noData}%;background:#8b97a8"></span></div>`;
  const rows = [
    ["Observations", s.n_observations.toLocaleString()],
    ["On time", sh.onTime + "%"],
    ["Late", sh.late + "%"],
    ["Early", sh.early + "%"],
    ["No realtime delay", sh.noData + "%"],
    ["Average delay", fmtDelay(s.avg_delay_sec)],
    ["Worst delay", fmtDelay(s.max_delay_sec)],
  ];
  // Coverage-aware annotation (matches the reliability card's honesty rails):
  // rollup windows carry a measured data-completeness %; a window below the
  // ranking bar is badged provisional rather than presented as confident.
  if (s.coverage_basis === "feed_health") {
    rows.splice(1, 0, ["Data coverage",
      (s.coverage_pct != null ? s.coverage_pct + "%" : "unknown") +
      (s.provisional ? ' <span class="rel-prov" title="Data completeness for this window is below the ranking bar — figures are shown but are not a rankable score">PROVISIONAL</span>' : "")]);
  }
  if (s.p50_delay_sec !== null && s.p50_delay_sec !== undefined) {
    rows.push([s.source === "rollup" ? "Median (p50, estimated)" : "Median (p50)", fmtDelay(s.p50_delay_sec)]);
    rows.push([s.source === "rollup" ? "90th percentile (estimated)" : "90th percentile", fmtDelay(s.p90_delay_sec)]);
  }
  rows.forEach(([k, v]) => {
    html += `<div class="stat-row"><span>${k}</span><span class="v">${v}</span></div>`;
  });
  if (s.source === "rollup") {
    // If the selected window is longer than we've actually been collecting,
    // say so — the figures are real but cover only the days we have so far,
    // and the window fills in automatically as history accrues.
    const note = partialHistoryNote(window_, s.collecting_since);
    html += `<p class="muted" style="margin-top:8px">` +
            (note ? note + " " : "") +
            `Long windows use daily rollups; the median and 90th-percentile are estimated from the daily distribution (exact figures need a short window).</p>`;
  } else if (s.partial_history) {
    // Agency filter forced the raw (hot-retention) path onto a long window:
    // the daily rollups carry no agency dimension, so this agency's history is
    // only the last few days, not the full window. Say so rather than letting
    // a 3-day sample read as e.g. all-time.
    const winLabel = (WINDOWS.find(([k]) => k === window_) || [, "this window"])[1];
    html += `<p class="muted" style="margin-top:8px">` +
            `Per-agency history is kept only for the last few days, so this ` +
            `${winLabel} view for the selected agency shows that recent window, ` +
            `not the full period.</p>`;
  }
  // Historical on-time stats come from the delay archive, which is recorded
  // per city + mode but not per agency — so when an agency is isolated, these
  // figures stay network-wide. The live map, route lines and live counts above
  // ARE agency-filtered. Say so honestly rather than implying the agency was
  // applied to the history.
  if (!mapAgency.has("all")) {
    const names = cityAgencies.filter((a) => mapAgency.has(a.id))
      .map((a) => a.label).join(", ");
    html += `<p class="muted" style="margin-top:8px">On-time history below is ` +
            `city-wide; the agency filter (${esc(names || "selected")}) applies to ` +
            `the live map and the live counts above.</p>`;
  }
  box.innerHTML = html;
}

// Returns a sentence when the window spans more time than we've been collecting,
// else "". Auto-disappears once enough history accrues (collected ≥ window).
function partialHistoryNote(win, collectingSince) {
  // Any fixed rollup window (30d, 3mo … 5y) whose span exceeds our collection
  // age gets the note; raw windows (min_days 0) and all-time never do.
  const days = (win === "all") ? 0 : (WINDOW_MIN_DAYS[win] || 0);
  if (!days || days < 30 || !collectingSince) return "";
  const collected = Math.floor((Date.now() - new Date(collectingSince).getTime()) / 86400000);
  if (collected >= days) return "";
  const label = (WINDOWS.find(([k]) => k === win) || [, "this window"])[1];
  return `This poller has only been collecting for ${collected} day${collected === 1 ? "" : "s"} so far, ` +
         `so the ${label} view shows everything available to date and will keep filling in.`;
}

async function loadStats() {
  if (!current) return null;
  const box = document.getElementById("stats");
  box.innerHTML = `<p class="muted">Loading…</p>`;
  const modeStr = mapMode.has("all") ? "all" : Array.from(mapMode).join(",");
  // TASK 1 — historical agency isolation (independent of live vehicles).
  const agencyQS = statsAgency ? `&agency=${encodeURIComponent(statsAgency)}` : "";
  try {
    const s = await getJSON(
      `/stats?city=${current}&window=${window_}&scope=${scope}&mode=${modeStr}${agencyQS}`);
    renderStats(s);
    renderTrend(current, window_, modeStr, scope);
    return s;
  }
  catch (e) { box.innerHTML = `<p class="empty-note">Could not load stats.</p>`; return null; }
}

async function loadModes() {
  if (!current) return { modes: [] };
  const box = document.getElementById("modes");
  box.innerHTML = `<p class="muted">Loading…</p>`;
  let d;
  const agencyQS = statsAgency ? `&agency=${encodeURIComponent(statsAgency)}` : "";
  try { d = await getJSON(
    `/modes?city=${current}&window=${window_}&scope=${scope}${agencyQS}`); }
  catch (e) { box.innerHTML = `<p class="muted">—</p>`; return { modes: [] }; }
  if (!d.modes.length) {
    // Fallback for warm-on-request cities: /api/modes aggregates persisted
    // per-mode delay rows, but these cities resolve mode at READ-TIME (not
    // stored), so the aggregate is empty even though live vehicles ARE
    // classified. Mirror the map mode chips: derive live per-mode counts from
    // /api/vehicles and show them as live counts (not on-time %).
    const EMPTY = `<p class="muted">No vehicle-type data for this city yet.</p>`;
    try {
      // F10 — reuse the vehicles payload loadVehicles already fetched for this
      // city+scope (refreshed every 20s poll) instead of re-fetching the full
      // /api/vehicles payload just to count modes.
      let vd;
      if (_lastVehData && _lastVehData.city === current &&
          _lastVehData.scope === scope && Date.now() - _lastVehData.t < 30000) {
        vd = _lastVehData.data;
      } else {
        vd = await getJSON(`/vehicles?city=${current}&scope=${scope}`);
      }
      const counts = {};
      for (const v of vd.vehicles) {
        // Respect the active agency filter so this panel matches the map.
        if (!agencyFilterPass(v.agency_id)) continue;
        const mk = v.mode || "other";
        counts[mk] = (counts[mk] || 0) + 1;
      }
      const order = MODE_ORDER.concat(["other"]);
      const rows = [];
      for (const mode of order) {
        if (counts[mode] > 0) {
          rows.push(`<div class="stat-row"><span class="mode-cell">${modeIcon(mode)} ${MODE_LABEL[mode]}</span><span class="v">${counts[mode]} live</span></div>`);
        }
      }
      box.innerHTML = rows.length ? rows.join("") : EMPTY;
      return d;
    } catch (e) {
      box.innerHTML = EMPTY;
      return d;
    }
  }
  let html = "";
  d.modes.forEach((s) => {
    if (s.on_time_pct !== null) {
      const sh = computeShares(s);
      html += `<div class="mode-row">` +
        `<div class="mode-row-head"><span class="mode-cell">${modeIcon(s.mode)} ${MODE_LABEL[s.mode]}</span>` +
        `<span class="v">${sh.onTime}% on time</span></div>` +
        `<div class="bar mode-bar">` +
          `<span style="width:${sh.onTime}%;background:#1a9e4b"></span>` +
          `<span style="width:${sh.late}%;background:#e5484d"></span>` +
          `<span style="width:${sh.early}%;background:#d6921a"></span>` +
          `<span style="width:${sh.noData}%;background:#8b97a8"></span></div>` +
        `<div class="mode-row-legend">On-time ${sh.onTime}% · Late ${sh.late}% · Early ${sh.early}% · No realtime delay ${sh.noData}%</div>` +
      `</div>`;
    } else {
      html += `<div class="stat-row"><span class="mode-cell">${modeIcon(s.mode)} ${MODE_LABEL[s.mode]}</span><span class="v">${s.vehicles_now} live</span></div>`;
    }
  });
  box.innerHTML = html;
  return d;
}

async function loadRoutes() {
  if (!current) return;
  const box = document.getElementById("routes");
  box.innerHTML = `<p class="muted">Loading…</p>`;
  let d;
  try { d = await getJSON(
    `/routes?city=${current}&window=${window_}&limit=5&scope=${scope}`); }
  catch (e) { box.innerHTML = `<p class="muted">—</p>`; return 0; }
  if (!d.available) { box.innerHTML = `<p class="muted">${esc(d.note)}</p>`; return 0; }
  if (!d.worst.length) {
    box.innerHTML = `<p class="muted">Not enough route data yet for this window.</p>`;
    return 0;
  }
  // TASK E: each worst-route row is a clickable button that highlights the
  // matching polyline on the map (switching to the map view if needed).
  box.innerHTML = "";
  d.worst.forEach((r) => {
    const row = document.createElement("button");
    row.type = "button";
    row.className = "stat-row route-row";
    row.dataset.routeId = r.route_id;
    row.setAttribute("aria-label", "Highlight route " + r.route_id + " on the map");
    const code = r.route_short_name || r.route_id;
    const longName = r.route_long_name && r.route_long_name !== code ? r.route_long_name : '';
    // Route badge gets the coloured-chip treatment so the row leads with the
    // route, not the stat sentence (F2-23).
    row.innerHTML = `<span class="rte-badge route-badge">${esc(code)}</span>` +
      (longName ? `<span class="route-name">${esc(longName)}</span>` : '') +
      `<span class="v">${fmtDelay(r.avg_delay)} avg, ${r.late_pct}% late, ${r.n.toLocaleString()} obs</span>`;
    row.addEventListener("click", function () {
      if (view !== "map") setView("map");
      highlightRoute(r.route_id);
    });
    box.appendChild(row);
  });
  return d.worst.length;
}

function renderMoreInfo(city, modesData) {
  const host = document.getElementById("more-info-host");
  while (host.firstChild) host.removeChild(host.firstChild);

  const summary = document.createElement("summary");
  summary.className = "more-info-summary";
  summary.textContent = "More info & data sources";
  host.appendChild(summary);

  const body = document.createElement("div");
  body.className = "more-info-body";

  // Helper to add a row
  const addRow = (label, value) => {
    const row = document.createElement("div");
    row.className = "more-info-row";

    const lbl = document.createElement("div");
    lbl.className = "more-info-label";
    lbl.textContent = label;
    row.appendChild(lbl);

    if (typeof value === "string") {
      const val = document.createElement("div");
      val.className = "more-info-value";
      val.textContent = value;
      row.appendChild(val);
    } else {
      row.appendChild(value);
    }
    body.appendChild(row);
  };

  // Network scope
  addRow("Network scope:", city.scope || "");

  // Operator(s)
  addRow("Operator(s):", city.agency || "");

  // Map placement
  let mapModeSentence = city.map_mode;
  if (city.map_mode === "gps") {
    mapModeSentence = "Live GPS positions from the agency feed";
  } else if (city.map_mode === "trip_stop") {
    mapModeSentence = "No GPS feed — vehicles placed at their next scheduled stop";
  } else if (city.map_mode === "hybrid") {
    mapModeSentence = "GPS where available; falls back to next-station placement";
  } else if (city.map_mode === "stop") {
    mapModeSentence = "Vehicles placed at the station they're approaching";
  }
  addRow("Map placement:", mapModeSentence);

  // Polling interval
  addRow("Polling interval:", city.interval + " seconds");

  // Last successful poll
  let lastPollText = "never";
  if (city.last_poll) {
    const pollTime = new Date(city.last_poll);
    const now = new Date();
    const diffMs = now - pollTime;
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) {
      lastPollText = diffSec + " seconds ago";
    } else if (diffSec < 3600) {
      const m = Math.floor(diffSec / 60);
      lastPollText = m + " minutes ago";
    } else {
      const h = Math.floor(diffSec / 3600);
      lastPollText = h + " hours ago";
    }
  }
  addRow("Last successful poll:", lastPollText);

  // Static GTFS source
  if (city.static_gtfs) {
    const val = document.createElement("div");
    val.className = "more-info-value";
    const a = document.createElement("a");
    a.href = city.static_gtfs;
    a.target = "_blank";
    a.rel = "noopener";
    a.textContent = city.static_gtfs;
    val.appendChild(a);
    const row = document.createElement("div");
    row.className = "more-info-row";
    const lbl = document.createElement("div");
    lbl.className = "more-info-label";
    lbl.textContent = "Static GTFS source:";
    row.appendChild(lbl);
    row.appendChild(val);
    body.appendChild(row);
  } else {
    addRow("Static GTFS source:", "none configured");
  }

  // Live feed sources. feeds_summary moved off /api/cities (2026-08-09 API
  // contract) to /api/public/feeds/{city} — fetched LAZILY, only when this
  // "More info" panel is actually opened, so a city switch never pays for it.
  const feedsRow = document.createElement("div");
  feedsRow.className = "more-info-row";
  const feedsLbl = document.createElement("div");
  feedsLbl.className = "more-info-label";
  feedsLbl.textContent = "Live feed sources:";
  feedsRow.appendChild(feedsLbl);
  const feedsVal = document.createElement("div");
  feedsVal.className = "more-info-value";
  const ul = document.createElement("ul");
  ul.className = "feed-list";
  const fillFeeds = (list) => {
    while (ul.firstChild) ul.removeChild(ul.firstChild);
    feedsLbl.textContent = "Live feed sources (" + (list ? list.length : 0) + "):";
    if (list && list.length > 0) {
      list.forEach((f) => {
        const li = document.createElement("li");
        const url = document.createElement("span");
        url.textContent = f.url;
        li.appendChild(url);
        const badge = document.createElement("span");
        badge.className = "badge";
        badge.textContent = f.kind;
        li.appendChild(badge);
        ul.appendChild(li);
      });
    } else {
      const li = document.createElement("li");
      li.textContent = "(no feeds configured)";
      ul.appendChild(li);
    }
  };
  if (city.feeds_summary) {
    // Transitional: an old API payload (or the boot cache) still carries the
    // embedded list — use it directly.
    fillFeeds(city.feeds_summary);
  } else {
    const li = document.createElement("li");
    li.className = "muted";
    li.textContent = "Loads when this panel is opened…";
    ul.appendChild(li);
    let feedsFetched = false;
    const loadFeedList = async () => {
      if (feedsFetched) return;
      feedsFetched = true;
      try {
        const fd = await getJSON("/public/feeds/" + encodeURIComponent(city.id));
        fillFeeds((fd && fd.feeds_summary) || []);
      } catch (e) { fillFeeds([]); }
    };
    // One live toggle handler on the host (replaced per city render, never
    // stacked); if the panel is already open, fetch immediately.
    if (host._feedsToggle) host.removeEventListener("toggle", host._feedsToggle);
    host._feedsToggle = () => { if (host.open) loadFeedList(); };
    host.addEventListener("toggle", host._feedsToggle);
    if (host.open) loadFeedList();
  }
  feedsVal.appendChild(ul);
  feedsRow.appendChild(feedsVal);
  body.appendChild(feedsRow);

  // Modes covered right now
  let modesList = "no live vehicles";
  if (modesData && modesData.modes && modesData.modes.length > 0) {
    modesList = modesData.modes
      .map((m) => m.mode + ": " + m.vehicles_now)
      .join(", ");
  }
  addRow("Modes covered right now:", modesList);

  host.appendChild(body);
}

/* ─────────── compare view: dual-pane transfer list ─────────── */
// Highlighted-but-not-yet-moved rows in each pane (distinct from cmpSelected,
// which is the actual comparison set the API is queried with).
let cmpAvailSel = new Set();
let cmpSelSel = new Set();

// The /api/compare endpoint bounds one REQUEST at 100 cities (URL length + DB
// work per request stay sane); larger selections are fetched in batches and
// merged client-side (loadCompare), so the picker itself has no product cap —
// the old hard 12 was an unrequested ceiling (owner, 2026-07-26). CMP_BATCH
// must not exceed the backend's per-request bound.
const CMP_BATCH = 100;
let _cmpCapTimer = null;
function cmpCapNote(msg) {
  const el = document.getElementById("cmp-cap-note");
  if (!el) return;
  el.textContent = msg || "";
  if (_cmpCapTimer) clearTimeout(_cmpCapTimer);
  if (msg) _cmpCapTimer = setTimeout(() => { el.textContent = ""; }, 4000);
}
// Add ids to the comparison set (no cap). Large sets just take longer to load;
// surface that as a note instead of refusing.
function cmpAddIds(ids) {
  for (const id of ids) cmpSelected.add(id);
  cmpCapNote(cmpSelected.size > CMP_BATCH
    ? `Comparing ${cmpSelected.size} cities — large comparisons load in batches and can take a moment.`
    : "");
  return false;
}

function cmpSortedCities() {
  // Live cities (by live vehicle count, desc) lead; scheduled-only cities
  // follow alphabetically — the same ranking as the city selector (F2-06),
  // so the compare picker's first screen is the data-rich inventory.
  return cities.slice().sort((a, b) => {
    const aLive = a.coverage !== "scheduled" ? 1 : 0;
    const bLive = b.coverage !== "scheduled" ? 1 : 0;
    if (aLive !== bLive) return bLive - aLive;
    if (aLive) return (b.n_vehicles || 0) - (a.n_vehicles || 0) || a.name.localeCompare(b.name);
    return a.name.localeCompare(b.name);
  });
}

function renderCmpPane(listEl, items, highlightSet, side) {
  listEl.innerHTML = "";
  if (!items.length) {
    const li = document.createElement("li");
    li.className = "cmp-empty";
    li.textContent = side === "avail"
      ? "No matching cities"
      : "No cities yet — pick on the left and press →";
    listEl.appendChild(li);
    return;
  }
  items.forEach((c) => {
    const li = document.createElement("li");
    li.className = "cmp-row" + (highlightSet.has(c.id) ? " sel" : "");
    li.setAttribute("role", "option");
    li.setAttribute("aria-selected", highlightSet.has(c.id) ? "true" : "false");
    li.tabIndex = 0;
    const name = document.createElement("span");
    name.className = "cmp-row-name";
    name.textContent = c.name;
    const meta = document.createElement("span");
    meta.className = "cmp-row-meta";
    meta.textContent = c.country || "";
    li.append(name, meta);
    const toggle = () => {
      if (highlightSet.has(c.id)) highlightSet.delete(c.id);
      else highlightSet.add(c.id);
      li.classList.toggle("sel");
      li.setAttribute("aria-selected", highlightSet.has(c.id) ? "true" : "false");
    };
    li.onclick = toggle;
    li.ondblclick = () => {
      highlightSet.add(c.id);
      if (side === "avail") cmpAddSelected(); else cmpRemoveSelected();
    };
    li.onkeydown = (e) => {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); }
    };
    listEl.appendChild(li);
  });
}

function renderCmpPicker() {
  // Prune highlights that no longer belong to their pane.
  [...cmpAvailSel].forEach(id => { if (cmpSelected.has(id)) cmpAvailSel.delete(id); });
  [...cmpSelSel].forEach(id => { if (!cmpSelected.has(id)) cmpSelSel.delete(id); });
  const q = (cmpSearchQuery || "").toLowerCase();
  const all = cmpSortedCities();
  const avail = all.filter(c => !cmpSelected.has(c.id) && c.name.toLowerCase().includes(q));
  const selected = all.filter(c => cmpSelected.has(c.id));
  renderCmpPane(document.getElementById("cmp-available"), avail, cmpAvailSel, "avail");
  renderCmpPane(document.getElementById("cmp-selected-list"), selected, cmpSelSel, "sel");
  const ac = document.getElementById("cmp-avail-count");
  const sc = document.getElementById("cmp-sel-count");
  if (ac) ac.textContent = `(${avail.length})`;
  if (sc) sc.textContent = `(${selected.length})`;
}

function cmpAddSelected() {
  if (!cmpAvailSel.size) return;
  cmpAddIds([...cmpAvailSel]);
  cmpAvailSel.clear();
  renderCmpPicker();
  loadCompare();
}

function cmpRemoveSelected() {
  if (!cmpSelSel.size) return;
  cmpSelSel.forEach(id => cmpSelected.delete(id));
  cmpSelSel.clear();
  renderCmpPicker();
  loadCompare();
}

function buildMapAgencies(agencies, counts) {
  var box = document.getElementById('map-agencies');
  if (!box) return;
  var card = box.closest ? box.closest('.card') : null;
  if (!agencies || agencies.length < 2) {
    if (card) card.style.display = 'none';
    box.innerHTML = '';
    return;
  }
  if (card) card.style.display = '';
  box.innerHTML = '';
  var items = [['all', 'All agencies', null]];
  agencies.forEach(function (a) {
    items.push([a.id, a.label, counts ? (counts[a.id] || 0) : null]);
  });
  items.forEach(function (item) {
    var key = item[0], label = item[1], count = item[2];
    var b = document.createElement('button');
    var cls = 'chip';
    if (mapAgency.has(key)) cls += ' on';
    // TASK 1 — never DISABLE an agency chip just because it has zero LIVE
    // vehicles right now. An agency that is off-hours (no buses running) must
    // stay selectable; selecting it filters the live map (legitimately empty)
    // without trapping the user. The count badge still shows 0 so the live
    // state is honest, but the chip remains clickable.
    b.className = cls;
    b.type = 'button';
    // F24 — toggle chips announce their state (matches the scheduled-city chips).
    b.setAttribute('aria-pressed', mapAgency.has(key) ? 'true' : 'false');
    b.textContent = (count == null) ? label : (label + ' ' + count);
    b.onclick = function () {
      if (key === 'all') {
        mapAgency = new Set(['all']);
      } else {
        if (mapAgency.has('all')) mapAgency.delete('all');
        if (mapAgency.has(key)) mapAgency.delete(key);
        else mapAgency.add(key);
        if (mapAgency.size === 0) mapAgency = new Set(['all']);
      }
      buildMapAgencies(agencies, counts);
      loadVehicles();
      loadShapes();
      loadStats();
    };
    box.appendChild(b);
  });
}

// TASK 1 — historical-stats agency selector. Options come from /api/agencies
// (distinct stored agency strings seen in delay_obs), NOT from live vehicles,
// so an agency is selectable from history regardless of whether it has any
// vehicles reporting right now. Only shown when a city actually has >=2 stored
// agencies to choose between (otherwise the filter is a no-op).
async function loadStatsAgencies() {
  const box = document.getElementById("stats-agencies");
  const card = document.getElementById("stats-agencies-card");
  if (!current) return;
  // Capture the city at call start: a rapid city switch can resolve this fetch
  // after `current` has already moved on, which would bind one city's agency
  // chips to another. Re-check after the await and ignore a stale response.
  const reqCity = current;
  let d;
  try { d = await getJSON(`/agencies?city=${reqCity}`); }
  catch (e) { d = { agencies: [] }; }
  if (current !== reqCity) return;
  statsAgencyOptions = d.agencies || [];
  if (!card || !box) return;
  if (statsAgencyOptions.length < 2) {
    card.style.display = "none";
    box.innerHTML = "";
    return;
  }
  card.style.display = "";
  buildStatsAgencies();
}

function buildStatsAgencies() {
  const box = document.getElementById("stats-agencies");
  if (!box) return;
  box.innerHTML = "";
  const items = [[null, "All agencies"]].concat(statsAgencyOptions.map((a) => [a, a]));
  items.forEach(([key, label]) => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "chip" + (statsAgency === key ? " on" : "");
    b.textContent = label;
    b.onclick = () => {
      statsAgency = key;
      buildStatsAgencies();
      loadStats();
      loadModes();
    };
    box.appendChild(b);
  });
}

function buildMapModes(counts) {
  // `counts` is an optional { bus: 24, metro: 755, other: 4025, ... } map.
  // When provided, render the count on each chip and gray-disable 0-count modes
  // so users can see at a glance what's available in the current city's feed.
  const box = document.getElementById("map-modes");
  if (!box) return;
  box.innerHTML = "";
  const totalCount = counts ? Object.values(counts).reduce((a, b) => a + b, 0) : null;
  const items = [["all", "All combined", totalCount, null]].concat(
    MODE_ORDER.map((m) => [m, MODE_LABEL[m], counts ? (counts[m] || 0) : null, m])
  );
  items.forEach(([key, label, count, mode]) => {
    const b = document.createElement("button");
    let cls = "chip mode-chip" + (mapMode.has(key) ? " on" : "");
    // Don't disable a selected mode chip (composed agency filter can zero its
    // count) — keep it clickable so the user can always deselect.
    b.className = cls;
    b.type = "button";
    // F24 — toggle chips announce their state (matches the scheduled-city chips).
    b.setAttribute("aria-pressed", mapMode.has(key) ? "true" : "false");
    b.innerHTML = (mode ? modeIcon(mode, 13) + " " : "") + esc(label) +
      (count == null ? "" : ` ${Number(count).toLocaleString()}`);
    b.onclick = () => {
      if (key === "all") {
        mapMode = new Set(["all"]);
      } else {
        if (mapMode.has("all")) mapMode.delete("all");
        if (mapMode.has(key)) mapMode.delete(key);
        else mapMode.add(key);
        if (mapMode.size === 0) mapMode = new Set(["all"]);
      }
      buildMapModes(counts);
      loadVehicles();
      loadStats();
    };
    box.appendChild(b);
  });
}

function initCmpModeOverflow(box) {
  if (box._modeOverflow) return box._modeOverflow;
  const more = document.createElement("button"), panel = document.createElement("div");
  more.type = "button";
  more.className = "chip mode-more";
  more.textContent = "More";
  more.setAttribute("aria-label", "More transit types");
  more.setAttribute("aria-controls", "cmp-mode-overflow");
  panel.id = "cmp-mode-overflow";
  panel.className = "mode-overflow-panel";
  panel.setAttribute("role", "group");
  panel.setAttribute("aria-label", "More transit types");
  const close = (focus) => {
    panel.hidden = true;
    more.setAttribute("aria-expanded", "false");
    if (focus && !more.hidden) more.focus();
  };
  close(false);
  let buttons = [], pending = false;
  function layout() {
    if (!box.clientWidth) return; // Compare can be built while another view is open.
    const focused = document.activeElement, open = !panel.hidden;
    buttons.forEach(b => box.insertBefore(b, more));
    more.hidden = true;
    const gap = parseFloat(getComputedStyle(box).columnGap) || 0;
    const widths = buttons.map(b => b.getBoundingClientRect().width);
    const total = widths.reduce((sum, w) => sum + w, 0) + gap * (buttons.length - 1);
    if (total > box.clientWidth) {
      more.hidden = false;
      let room = box.clientWidth - more.getBoundingClientRect().width;
      const keep = new Set();
      // Keep the selected type visible; move the original buttons, with their handlers.
      const priority = buttons.map((b, i) => i).sort((a, b) => Number(buttons[b].classList.contains("on")) - Number(buttons[a].classList.contains("on")));
      priority.forEach(i => { if (widths[i] + gap <= room) { keep.add(i); room -= widths[i] + gap; } });
      buttons.forEach((b, i) => { if (!keep.has(i)) panel.appendChild(b); });
    }
    if (more.hidden || !open) close(false);
    else { panel.hidden = false; more.setAttribute("aria-expanded", "true"); }
    if (buttons.includes(focused)) {
      if (panel.contains(focused) && panel.hidden) more.focus();
      else focused.focus();
    } else if (focused === more) {
      if (more.hidden) buttons.find(b => b.classList.contains("on"))?.focus();
      else more.focus();
    }
  }
  function schedule() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => { pending = false; layout(); });
  }
  more.onclick = () => { const open = panel.hidden; panel.hidden = !open; more.setAttribute("aria-expanded", String(open)); };
  box.addEventListener("keydown", e => { if (e.key === "Escape" && !panel.hidden) { e.preventDefault(); close(true); } });
  document.addEventListener("click", e => { if (!box.contains(e.target)) close(false); });
  box.addEventListener("focusout", e => { if (!box.contains(e.relatedTarget)) close(false); });
  window.addEventListener("resize", schedule);
  if (window.ResizeObserver) new ResizeObserver(schedule).observe(box);
  if (document.fonts) document.fonts.ready.then(schedule);
  box._modeOverflow = { update(items) { buttons = items; panel.replaceChildren(); box.append(more, panel); close(false); layout(); } };
  return box._modeOverflow;
}

function buildCmpModes() {
  const box = document.getElementById("cmp-modes");
  const overflow = initCmpModeOverflow(box);
  const focusedMode = box.contains(document.activeElement) ? document.activeElement.dataset.mode : null;
  box.innerHTML = "";
  [["all", "All combined", null]].concat(MODE_ORDER.map((m) =>
    [m, MODE_LABEL[m], m])).forEach(([key, label, mode]) => {
    const b = document.createElement("button");
    b.type = "button";
    b.dataset.mode = key;
    b.setAttribute("aria-pressed", String(key === cmpMode));
    b.className = "chip mode-chip" + (key === cmpMode ? " on" : "");
    b.innerHTML = (mode ? modeIcon(mode, 13) + " " : "") + esc(label);
    b.onclick = () => { cmpMode = key; buildCmpModes(); loadCompare(); };
    box.appendChild(b);
  });
  overflow.update([...box.querySelectorAll(".mode-chip")]);
  if (focusedMode) box.querySelector(`[data-mode="${focusedMode}"]`)?.focus();
}

function buildCmpWindows() {
  const w = document.getElementById("cmp-windows");
  w.innerHTML = "";
  // History-gate the compare windows like the live map does. Compare is
  // multi-city, so gate on the global collecting span -- don't offer
  // "1 year"/"5 years" when only weeks of data exist (they render empty).
  const have = collectingSince
    ? Math.floor((Date.now() - new Date(collectingSince).getTime()) / 86400000)
    : 0;
  if (cmpWindow !== "all" && (WINDOW_MIN_DAYS[cmpWindow] || 0) > have) cmpWindow = "1h";
  WINDOWS.forEach(([key, label]) => {
    if (key !== "all" && (WINDOW_MIN_DAYS[key] || 0) > have) return;
    const b = document.createElement("button");
    b.className = "win-btn" + (key === cmpWindow ? " active" : "");
    b.textContent = label;
    b.onclick = () => { cmpWindow = key; buildCmpWindows(); loadCompare(); };
    w.appendChild(b);
  });
}

function renderHighlights(list) {
  const box = document.getElementById("cmp-highlights");
  const withData = list.filter((s) => s.n_observations > 0);
  if (withData.length < 1) { box.innerHTML = ""; return; }
  // Punctuality superlatives never go to a provisional (low-coverage) city —
  // partial data must not win "most punctual" (falls back if all provisional).
  const eligible = withData.filter((s) => !s.provisional);
  const punct = eligible.length ? eligible : withData;
  const best = punct.reduce((a, b) =>
    (b.on_time_pct ?? -1) > (a.on_time_pct ?? -1) ? b : a);
  const worst = punct.reduce((a, b) =>
    (b.on_time_pct ?? 999) < (a.on_time_pct ?? 999) ? b : a);
  const biggest = withData.reduce((a, b) =>
    (b.max_delay_sec ?? -1) > (a.max_delay_sec ?? -1) ? b : a);
  const busiest = list.reduce((a, b) =>
    (b.vehicles_now ?? -1) > (a.vehicles_now ?? -1) ? b : a);
  const cards = [
    ["Most punctual", best.city_name, pct(best.on_time_pct)],
    ["Least punctual", worst.city_name, pct(worst.on_time_pct)],
    ["Biggest single delay", biggest.city_name, fmtDelay(biggest.max_delay_sec)],
    ["Most vehicles live now", busiest.city_name, busiest.vehicles_now],
  ];
  box.innerHTML = cards.map(([k, c, v]) =>
    `<div class="hl"><label>${esc(k)}</label><span class="hl-v">${esc(v)}</span>` +
    `<span class="hl-c">${esc(c)}</span></div>`).join("");
}

var GRADE_BAR_COLOR = { A: "#1a9e4b", B: "#0a7ea4", C: "#b45309", D: "#d6921a", F: "#e5484d" };
// At-a-glance comparison: sorted horizontal on-time bars so the "who's most
// punctual" answer reads instantly, above the detailed table (ChatGPT ask).
function compareBarChart(withData) {
  var scored = withData.map(function (s) {
    var sh = computeShares(s);
    return { name: s.city_name, onTime: sh.onTime, live: s.vehicles_now, grade: pctGrade(sh.onTime) };
  }).sort(function (a, b) { return b.onTime - a.onTime; });
  if (scored.length < 2) { return ""; }
  var h = '<div class="cmp-chart"><div class="cmp-chart-h">On-time performance — ' + scored.length + ' cities</div>';
  scored.forEach(function (c) {
    h += '<div class="cmp-chart-row">' +
      '<div class="cmp-chart-name">' + esc(c.name) + '</div>' +
      '<div class="cmp-chart-track"><span class="cmp-chart-bar" style="width:' + c.onTime + '%;background:' + (GRADE_BAR_COLOR[c.grade] || "#8b97a8") + '"></span></div>' +
      '<div class="cmp-chart-val">' + c.onTime + '%<span class="cmp-chart-live">' + c.live + ' live</span></div></div>';
  });
  h += '</div>';
  return h;
}

// compare v2: column definitions for sortable headers
// key = field on the city object (or synthetic); getValue = extract numeric for sort
const CMP_COLS = [
  { key: "rank",           label: "#",            getValue: (s) => s.rank == null ? Infinity : s.rank },
  { key: "city_name",      label: "City",         getValue: (s) => s.city_name || "" },
  { key: "on_time_pct",    label: "On-time",      getValue: (s) => s.on_time_pct == null ? -Infinity : s.on_time_pct },
  { key: "_bar",           label: "",             getValue: null },   // mini-bar — not sortable
  { key: "late_pct",       label: "Late",         getValue: (s) => s.late_pct == null ? -Infinity : s.late_pct },
  { key: "early_pct",      label: "Early",        getValue: (s) => s.early_pct == null ? -Infinity : s.early_pct },
  { key: "unclassified_pct", label: "No delay data", title: "Vehicles observed without realtime delay data",
                             getValue: (s) => s.unclassified_pct == null ? -Infinity : s.unclassified_pct },
  { key: "avg_delay_sec",  label: "Avg delay",    getValue: (s) => s.avg_delay_sec == null ? -Infinity : s.avg_delay_sec },
  // p50/p90 inserted conditionally
  { key: "max_delay_sec",  label: "Worst",        getValue: (s) => s.max_delay_sec == null ? -Infinity : s.max_delay_sec },
  { key: "n_observations", label: "Observations", getValue: (s) => s.n_observations || 0 },
  { key: "vehicles_now",   label: "Live now",     getValue: (s) => s.vehicles_now || 0 },
  { key: "late_now",       label: "Late now",     getValue: (s) => s.late_now || 0 },
];
const CMP_P50 = { key: "p50_delay_sec", label: "Median",    getValue: (s) => s.p50_delay_sec == null ? -Infinity : s.p50_delay_sec };
const CMP_P90 = { key: "p90_delay_sec", label: "90th %ile", getValue: (s) => s.p90_delay_sec == null ? -Infinity : s.p90_delay_sec };

function _cmpSortRows(rows) {
  if (!cmpSortCol) return rows;
  const allCols = CMP_COLS.concat([CMP_P50, CMP_P90]);
  const col = allCols.find((c) => c.key === cmpSortCol);
  if (!col || !col.getValue) return rows;
  return rows.slice().sort((a, b) => {
    const va = col.getValue(a), vb = col.getValue(b);
    if (typeof va === "string") return cmpSortDir * va.localeCompare(vb);
    return cmpSortDir * (va - vb);
  });
}

// Comparability caption (Task 5): when the API reports comparability:"mixed"
// (all-time, where city histories differ in length), surface the API's own note
// so it reads as informative context, not an error. Fixed windows are directly
// comparable and get no caption. Text comes straight from the API (drift-proof).
function comparabilityCaption(data) {
  if (!data || (data.comparability || 'comparable') !== 'mixed') return '';
  const note = data.comparability_note ||
    'All-time mixes cities with different history lengths; a fixed window is a more accurate comparison.';
  return '<div class="cmp-comparability" role="note">' +
    '<span class="cmp-comparability-icon" aria-hidden="true">ⓘ</span>' +
    '<span>' + esc(note) + '</span></div>';
}

// History-ineligible cities (Task 4): the API excludes a requested city from a
// fixed window when it lacks the required history, returning it in `excluded`
// with the windows it IS available in. Render that cleanly — the city is not
// dropped silently; the user sees why and where it CAN be compared — instead of
// a blank/zero row.
function excludedCitiesSection(data) {
  const ex = (data && data.excluded) || [];
  if (!ex.length) return '';
  const winLabel = (WINDOWS.find(([k]) => k === cmpWindow) || [, cmpWindow])[1];
  const rows = ex.map(function (c) {
    const name = c.city_name || c.city || '';
    const avail = (c.available_windows || [])
      .filter(function (k) { return k !== '1d' && WINDOW_META[k]; })
      .map(function (k) { return (WINDOW_META[k] && WINDOW_META[k].label) || k; });
    const since = c.data_since ? (' · data since ' + esc(longDate(c.data_since))) : '';
    const availTxt = avail.length
      ? ('Available in: ' + esc(avail.join(', ')))
      : 'No comparable history yet';
    return '<li class="cmp-ex-row"><span class="cmp-ex-name">' + esc(name) + '</span>' +
      '<span class="cmp-ex-avail">' + availTxt + since + '</span></li>';
  }).join('');
  return '<details class="cmp-excluded" open><summary>' +
    ex.length + ' cit' + (ex.length === 1 ? 'y is' : 'ies are') +
    ' not eligible for the ' + esc(winLabel) + ' window' +
    '</summary><p class="cmp-ex-lead">These cities don’t yet have enough history for this window. ' +
    'Pick a shorter window (or All time) to include them.</p>' +
    '<ul class="cmp-ex-list">' + rows + '</ul></details>';
}

function renderCompare(data) {
  _lastCmpData = data;
  const box = document.getElementById('cmp-output');
  const list = data.ranked || [];
  const caption = comparabilityCaption(data);
  const excluded = excludedCitiesSection(data);
  if (!list.length || list.every(function(s){ return !s.n_observations; })) {
    document.getElementById('cmp-highlights').innerHTML = '';
    // If cities exist but were all excluded for THIS window, that's the story to
    // tell (not "no data") — lead with the excluded section + caption.
    if (!list.length && excluded) {
      box.innerHTML = caption + excluded;
      return;
    }
    const since = (list[0] && list[0].collecting_since) ? longDate(list[0].collecting_since) : 'launch';
    box.innerHTML = caption + emptyWindowNote(cmpWindow, since) + excluded;
    return;
  }
  renderHighlights(list);
  // Lead the results with the at-a-glance on-time chart, above the summary cards.
  const _withDataForChart = list.filter(function (s) { return s.n_observations > 0; });
  const _lead = compareBarChart(_withDataForChart);
  if (_lead) {
    const hb = document.getElementById('cmp-highlights');
    if (hb) { hb.innerHTML = '<div class="cmp-chart-lead">' + _lead + '</div>' + hb.innerHTML; }
  }
  const rollup = list[0] && list[0].source === 'rollup';
  const showPct = list.some(function(s){ return s.p50_delay_sec !== null && s.p50_delay_sec !== undefined; });
  const withData = list.filter(function(s){ return s.n_observations > 0; });
  const zero = list.filter(function(s){ return !s.n_observations; });
  // Apply user sort (v2) — no-data rows always trail ranked rows.
  const sortedWith = _cmpSortRows(withData);
  const rows = sortedWith.concat(zero);
  const span = showPct ? 12 : 10;

  // Build sortable column header list (v2)
  const activeCols = CMP_COLS.slice();
  if (showPct) {
    // Insert p50/p90 after avg_delay_sec
    const avgIdx = activeCols.findIndex((c) => c.key === "avg_delay_sec");
    activeCols.splice(avgIdx + 1, 0, CMP_P50, CMP_P90);
  }
  const thHtml = activeCols.map((col) => {
    if (!col.getValue) return '<th></th>'; // mini-bar — unsortable
    const isSorted = cmpSortCol === col.key;
    const arrow = isSorted ? (cmpSortDir === 1 ? ' ↑' : ' ↓') : '';
    const title = col.title ? (' title="' + esc(col.title) + '"') : '';
    return '<th class="cmp-th-sort' + (isSorted ? ' cmp-th-active' : '') + '"' +
      title + ' data-col="' + col.key + '" tabindex="0" role="columnheader" aria-sort="' +
      (isSorted ? (cmpSortDir === 1 ? 'ascending' : 'descending') : 'none') + '">' +
      esc(col.label) + '<span class="cmp-sort-arrow">' + arrow + '</span></th>';
  }).join('');

  let html = '<div class="cmp-scroll"><table class="cmp"><thead><tr>' + thHtml + '</tr></thead><tbody>';
  rows.forEach(function(s){
    if (!s.n_observations) {
      html += '<tr><td class="cmp-td-sticky">—</td><td class="city cmp-td-city-sticky">' + esc(s.city_name) +
        '</td><td class="cmp-nodata" colspan="' + span + '">No on-time data in this window — ' +
        s.vehicles_now + ' live now</td></tr>';
      return;
    }
    const sh = computeShares(s);
    const barcell = '<td class="barcell"><span class="minibar4">' +
      '<i style="width:' + sh.onTime + '%;background:#1a9e4b"></i>' +
      '<i style="width:' + sh.late + '%;background:#e5484d"></i>' +
      '<i style="width:' + sh.early + '%;background:#d6921a"></i>' +
      '<i style="width:' + sh.noData + '%;background:#8b97a8"></i>' +
    '</span></td>';
    const lateNowClass = s.late_now ? 'bad' : '';
    const pctCells = showPct ? ('<td>' + fmtDelay(s.p50_delay_sec) + '</td><td>' + fmtDelay(s.p90_delay_sec) + '</td>') : '';
    html += '<tr><td class="cmp-td-sticky">' + (s.rank == null ? '—' : s.rank) + '</td>' +
      '<td class="city cmp-td-city-sticky">' + esc(s.city_name) +
      (s.provisional ? ' <span class="rel-prov" title="Data coverage ' +
        (s.coverage_pct != null ? s.coverage_pct + '%' : 'unknown') +
        ' — below the ranking bar for this window; listed after ranked cities, not ranked">PROVISIONAL</span>' : '') +
      '</td>' +
      '<td class="v">' + sh.onTime + '%</td>' +
      barcell +
      '<td>' + sh.late + '%</td>' +
      '<td>' + sh.early + '%</td>' +
      '<td>' + sh.noData + '%</td>' +
      '<td>' + fmtDelay(s.avg_delay_sec) + '</td>' + pctCells +
      '<td>' + fmtDelay(s.max_delay_sec) + '</td>' +
      '<td>' + s.n_observations.toLocaleString() + '</td>' +
      '<td>' + s.vehicles_now + '</td>' +
      '<td class="' + lateNowClass + '">' + s.late_now + '</td></tr>';
  });
  html += '</tbody></table></div>';
  const label = cmpMode === 'all' ? 'all transit types' : MODE_LABEL[cmpMode].toLowerCase();
  // CSV export + copy-link toolbar (v2)
  html += '<div class="cmp-v2-toolbar">' +
    '<button type="button" id="cmp-csv-btn" class="cmp-v2-btn">&#8595; Download CSV</button>' +
    '<button type="button" id="cmp-copy-link-btn" class="cmp-v2-btn">&#128279; Copy link</button>' +
    '</div>';
  let foot = '<p class="muted" style="margin-top:8px">' + esc(effectiveWindowText(data)) + ' Showing ' + label + '.';
  if (rows.some(function (s) { return s.provisional; })) {
    foot += ' Cities marked PROVISIONAL have data completeness below the ranking bar for this window — they are listed after ranked cities and given no rank, never silently dropped.';
  }
  if (rollup) foot += ' Long windows use daily rollups; median and 90th-percentile are estimated from daily rollups for long windows.';
  foot += '</p>';
  // Comparability caption leads the results (Task 5); the ineligible-cities
  // section (Task 4) trails them so users see who was filtered out and why.
  box.innerHTML = caption + html + foot + excluded;

  // Wire sortable header clicks (v2)
  box.querySelectorAll('.cmp-th-sort').forEach((th) => {
    const onClick = () => {
      const col = th.getAttribute('data-col');
      if (cmpSortCol === col) { cmpSortDir = -cmpSortDir; }
      else { cmpSortCol = col; cmpSortDir = col === 'rank' || col === 'city_name' ? 1 : -1; }
      if (_lastCmpData) renderCompare(_lastCmpData);
    };
    th.addEventListener('click', onClick);
    th.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } });
  });

  // CSV export (v2)
  const csvBtn = document.getElementById('cmp-csv-btn');
  if (csvBtn) csvBtn.addEventListener('click', cmpExportCsv);

  // Copy-link button (v2)
  const linkBtn = document.getElementById('cmp-copy-link-btn');
  if (linkBtn) linkBtn.addEventListener('click', () => {
    const url = cmpShareUrl();
    navigator.clipboard.writeText(url).then(() => {
      linkBtn.textContent = '✓ Copied!';
      setTimeout(() => { linkBtn.textContent = '\u{1F517} Copy link'; }, 2000);
    }).catch(() => {
      // Fallback: select a temporary input
      const inp = document.createElement('input');
      inp.value = url;
      document.body.appendChild(inp);
      inp.select();
      document.execCommand('copy');
      document.body.removeChild(inp);
      linkBtn.textContent = '✓ Copied!';
      setTimeout(() => { linkBtn.textContent = '\u{1F517} Copy link'; }, 2000);
    });
  });
}

/* ─────────── compare v2: URL hash, CSV export ─────────── */
// Build a shareable URL encoding the current compare selection + window.
// Uses hash so it doesn't require a server route change.
function cmpShareUrl() {
  const cities = [...cmpSelected].join(',');
  const hash = '#compare?cities=' + encodeURIComponent(cities) + '&window=' + encodeURIComponent(cmpWindow);
  return location.origin + '/compare' + hash;
}

// Update the URL hash silently (no pushState navigation event needed).
function cmpUpdateHash() {
  if (!cmpSelected.size) return;
  const hash = '#compare?cities=' + encodeURIComponent([...cmpSelected].join(',')) + '&window=' + encodeURIComponent(cmpWindow);
  if (location.hash !== hash) history.replaceState(null, '', location.pathname + hash);
}

// Hydrate cmpSelected + cmpWindow from the URL hash (called on boot at /compare).
function cmpApplyHash() {
  const h = location.hash;
  if (!h || !h.startsWith('#compare?')) return false;
  try {
    const params = new URLSearchParams(h.slice('#compare?'.length));
    const rawCities = params.get('cities');
    const rawWindow = params.get('window');
    if (rawCities) {
      const ids = rawCities.split(',').map((s) => s.trim()).filter(Boolean);
      if (ids.length) {
        cmpSelected.clear();
        ids.forEach((id) => cmpSelected.add(id));
        renderCmpPicker();
      }
    }
    if (rawWindow) cmpWindow = rawWindow;
    return true;
  } catch (e) { return false; }
}

// Export the last-fetched compare data as a CSV file (client-side, no re-fetch).
function cmpExportCsv() {
  if (!_lastCmpData || !_lastCmpData.ranked) return;
  const rows = _lastCmpData.ranked;
  const showPct = rows.some((s) => s.p50_delay_sec != null);
  // F25 — coverage_pct + provisional included so exported scores carry the
  // same honesty rails the table shows.
  const header = ['rank','city','on_time_pct','late_pct','early_pct','no_delay_data_pct',
    'avg_delay_sec', ...(showPct ? ['p50_delay_sec','p90_delay_sec'] : []),
    'max_delay_sec','n_observations','vehicles_now','late_now',
    'coverage_pct','provisional'].join(',');
  const csvRows = [header];
  rows.forEach((s) => {
    const sh = s.n_observations ? computeShares(s) : null;
    const line = [
      s.rank == null ? '' : s.rank,
      '"' + (s.city_name || s.city || '').replace(/"/g, '""') + '"',
      s.on_time_pct == null ? '' : s.on_time_pct,
      s.late_pct == null ? '' : s.late_pct,
      s.early_pct == null ? '' : s.early_pct,
      sh ? sh.noData : '',
      s.avg_delay_sec == null ? '' : s.avg_delay_sec,
      ...(showPct ? [s.p50_delay_sec == null ? '' : s.p50_delay_sec,
                     s.p90_delay_sec == null ? '' : s.p90_delay_sec] : []),
      s.max_delay_sec == null ? '' : s.max_delay_sec,
      s.n_observations || 0,
      s.vehicles_now || 0,
      s.late_now || 0,
      s.coverage_pct == null ? '' : s.coverage_pct,
      s.provisional == null ? '' : (s.provisional ? 'yes' : 'no'),
    ].join(',');
    csvRows.push(line);
  });
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'gts-compare-' + cmpWindow + '-' + new Date().toISOString().slice(0, 10) + '.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// F6 — in-flight guard: the 20s auto-refresh must never stack a new batched
// load on top of one that hasn't finished (large selections take > 20s).
let _cmpInFlight = false;

async function loadCompare(silent) {
  const box = document.getElementById("cmp-output");
  if (silent && _cmpInFlight) return;
  if (!cmpSelected.size) {
    document.getElementById("cmp-highlights").innerHTML = "";
    // §E coverage empty-state: if the user arrived from a map city, hint them
    // towards that city so the cross-page transition isn't bewildering.
    if (current) {
      const cityObj = cities.find((c) => c.id === current);
      const cityName = (cityObj && cityObj.name) || current;
      box.innerHTML = `<div class="coverage-empty-note" role="status">
        You're currently viewing <strong>${esc(cityName)}</strong>. Add it (or any other cities) to the pane above to compare on-time performance.
      </div><p class="muted cmp-empty">Select cities above to compare.</p>`;
    } else {
      box.innerHTML = `<p class="muted">Pick one or more cities above.</p>`;
    }
    return;
  }
  if (!silent) box.innerHTML = `<p class="muted">Loading…</p>`;
  _cmpInFlight = true;
  try {
    // Batch the selection across requests (backend bounds one request at
    // CMP_BATCH cities) and merge: concat per-chunk results, then re-rank the
    // union with the SAME rule the backend uses — best on-time first, no-data
    // cities last, provisional cities after ranked ones and never numbered.
    const all = [...cmpSelected];
    const chunks = [];
    for (let i = 0; i < all.length; i += CMP_BATCH) chunks.push(all.slice(i, i + CMP_BATCH));
    let data = null;
    for (let i = 0; i < chunks.length; i++) {
      if (!silent && chunks.length > 1) {
        box.innerHTML = `<p class="muted">Loading… (batch ${i + 1} of ${chunks.length})</p>`;
      }
      const part = await getJSON(
        `/compare?cities=${chunks[i].join(",")}&window=${cmpWindow}` +
        `&mode=${cmpMode}&scope=${cmpScope}`);
      if (!data) { data = part; }
      else {
        data.cities = (data.cities || []).concat(part.cities || []);
        // History-ineligible cities come back per-chunk too — merge them so the
        // "not eligible for this window" section is complete across batches.
        data.excluded = (data.excluded || []).concat(part.excluded || []);
        data.partial_day = data.partial_day || part.partial_day;
      }
    }
    if (chunks.length > 1) {
      // F16 — rank merged batches by the SAME rule the leaderboard documents:
      // the conservative 95% Wilson lower bound, not the point estimate, so a
      // lucky-but-thin sample can't out-rank a well-measured one. Compare rows
      // don't carry the bound, so it's computed from the raw counts they DO
      // carry (on_time / n_observations) with the methodology's z (1.96).
      const rankBound = (s) => {
        if (s.on_time_pct == null || !s.n_observations) return -1;
        const k = (s.on_time != null) ? s.on_time
          : Math.round((s.on_time_pct / 100) * s.n_observations);
        return wilsonLower(k, s.n_observations);
      };
      const ranked = data.cities.slice().sort((a, b) =>
        (a.on_time_pct === null) - (b.on_time_pct === null) ||
        !!a.provisional - !!b.provisional ||
        rankBound(b) - rankBound(a));
      let n = 0;
      ranked.forEach((s) => {
        s.rank = (s.on_time_pct !== null && !s.provisional) ? ++n : null;
      });
      data.ranked = ranked;
    }
    renderCompare(data);
    cmpUpdateHash(); // v2: keep URL in sync so current view is shareable
  } catch (e) {
    // On a background refresh, keep the last good table rather than blanking it.
    if (!silent) box.innerHTML = `<p class="empty-note">Could not load comparison — please retry (fewer cities load faster).</p>`;
  } finally {
    _cmpInFlight = false;
  }
}

/* ─────────── boot ─────────── */
/* ─────────── data-quality + feed-health pages (Phase 4B) ─────────── */
function esc(s) {
  return String(s == null ? "" : s).replace(/[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
}
function gradeClass(g) {
  const safe = /^(?:A|B|C|D|F)$/.test(String(g || "")) ? String(g) : "NA";
  return "dq-grade dq-" + safe;
}
function _pctTxt(v) { return v == null ? "—" : Math.round(v) + "%"; }
// Humanize raw issue codes so the table doesn't surface machine strings like "feed_down".
const _DQ_ISSUE_LABELS = {
  feed_down: "Low observation availability", feed_stale: "Stale data", stale: "Stale data",
  low_confidence: "Low confidence", no_grade: "Not gradeable", partial: "Partial data",
  no_realtime: "No realtime feed", no_delay: "No delay data", low_uptime: "Low feed uptime",
};
function _dqIssue(s) {
  return _DQ_ISSUE_LABELS[s] || String(s).replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

async function loadDataQuality() {
  const host = document.getElementById("dq-output");
  if (!host) return;
  host.innerHTML = '<p class="muted">Loading…</p>';
  let data;
  try { data = await getJSON("/public/data-quality"); }
  catch (e) {
    if (isNotFound(e)) { host.innerHTML = '<p class="muted">Data-quality grades are not available yet.</p>'; return; }
    host.innerHTML = serviceErrorNote("Data-quality grades", e, "dq-retry");
    wireRetry("dq-retry", () => loadDataQuality());
    return;
  }
  const list = (data && data.cities) || [];
  if (!list.length) {
    host.innerHTML = '<p class="muted">No grades yet — they populate daily as feed-health history accrues.</p>';
    return;
  }
  // §E coverage empty-state: if the user arrived from a specific city and it
  // is absent from data-quality (needs a monitored realtime feed), tell them
  // clearly — don't let it silently disappear into the directory.
  let _dqCityNote = "";
  if (current) {
    const inList = list.some((c) => c.city === current);
    if (!inList) {
      const cityObj = cities.find((c) => c.id === current);
      const cityName = (cityObj && cityObj.name) || current;
      _dqCityNote = `<div class="coverage-empty-note" role="status">
        No data-quality data for <strong>${esc(cityName)}</strong> yet — it needs a monitored realtime feed.
        Once a realtime feed is active and polled, grades appear here after the first daily rollup.
        <a href="/methodology" class="view-link" style="padding:2px 8px">Methodology</a>
      </div>`;
    }
  }
  // KPI header — four mutually-exclusive health buckets; counts sum to total.
  // Buckets determined by the same rowStatus logic used in the directory rows.
  // Retired feeds (no data for > retired_after_days) are excluded from the KPI strip.
  const actList = list.filter((c) => c.retired !== true);
  const retiredDays = (data && data.retired_after_days) || 2;
  const total = actList.length;
  const isOff = (c) => (c.issues || []).some((i) => /offline|down|stale/.test(String(i)));
  const cntBad = actList.filter(isOff).length;
  const cntNofeed = actList.filter((c) => !isOff(c) && (!c.has_observations || c.dq_grade == null)).length;
  const cntWarn = actList.filter((c) => !isOff(c) && c.has_observations && c.dq_grade != null &&
    ((c.confidence != null && c.confidence < 0.25) || (c.feed_uptime_pct != null && c.feed_uptime_pct < 60))).length;
  const cntOk = total - cntBad - cntNofeed - cntWarn;
  const gradeA = actList.filter((c) => c.dq_grade === "A").length;
  const header = civicHeaderStrip([
    { label: "Healthy", value: cntOk, tone: "good" },
    { label: "Degraded", value: cntWarn, tone: cntWarn ? "warn" : "" },
    { label: "Availability / freshness issues", value: cntBad, tone: cntBad ? "bad" : "" },
    { label: "Insufficient grading data", value: cntNofeed },
    { label: "Grade A (best)", value: gradeA, tone: "good" },
    { label: "Total monitored", value: total },
  ]);
  const uptBar = (u) => {
    if (u == null) return "—";
    const p = Math.max(0, Math.min(100, Math.round(u)));
    const cls = p >= 90 ? "up-good" : p >= 60 ? "up-mid" : "up-bad";
    return `<span class="dq-bar ${cls}"><i style="width:${p}%"></i></span><span class="dq-bar-n">${p}%</span>`;
  };
  const confLamp = (c) => {
    if (c == null) return "—";
    const p = Math.round(c * 100);
    const cls = p >= 60 ? "cf-good" : p >= 25 ? "cf-mid" : "cf-low";
    return `<span class="cf-lamp ${cls}"></span>${p}%`;
  };
  // Per-row status capsule + tint. Four mutually-exclusive buckets, severity-ordered:
  // bad (feed offline/stale issues) > warn (low confidence or low uptime) >
  // nofeed (no observations — no RT feed yet) > ok (healthy).
  const rowStatus = (c) => {
    if (c.retired === true) return { cls: "nofeed", label: "Retired · last data " + c.day };
    if ((c.issues || []).some((i) => /stale/.test(String(i)))) return { cls: "bad", label: "Stale observations" };
    if ((c.issues || []).some((i) => /offline|down/.test(String(i)))) return { cls: "bad", label: "Low observation availability" };
    if (!c.has_observations || c.dq_grade == null) return { cls: "nofeed", label: "Insufficient grading data" };
    if (c.confidence != null && c.confidence < 0.25) return { cls: "warn", label: "Low confidence" };
    if (c.feed_uptime_pct != null && c.feed_uptime_pct < 60) return { cls: "warn", label: "Low uptime" };
    return { cls: "ok", label: "Healthy" };
  };
  // Severity rank: bad=0 (worst, shown first), warn=1, nofeed=2, ok=3 (best, shown last).
  // This puts actionable problem cities at the top; "no feed yet" cities after issues.
  const SEV_RANK = { bad: 0, warn: 1, nofeed: 2, ok: 3 };
  const enriched = list.map((c) => ({ c, st: rowStatus(c) }));
  // Count per bucket for header KPI strip and filter chips.
  const nBad = enriched.filter((e) => e.st.cls === "bad").length;
  const nWarn = enriched.filter((e) => e.st.cls === "warn").length;
  const nNofeed = enriched.filter((e) => e.st.cls === "nofeed").length;
  const nOk = enriched.filter((e) => e.st.cls === "ok").length;
  const FILTERS = [
    ["all", "All", (e) => e.c.retired !== true],
    ["healthy", "Healthy", (e) => e.st.cls === "ok"],
    ["offline", "Availability / freshness issues", (e) => e.st.cls === "bad"],
    ["lowconf", "Degraded", (e) => e.st.cls === "warn"],
    ["nofeed", "Insufficient grading data", (e) => e.st.cls === "nofeed" && e.c.retired !== true],
    ["retired", "Retired feeds", (e) => e.c.retired === true],
  ];
  const state = { q: "", filter: "all", showAll: false, openCity: null };
  const TOP = 50;
  // Diagnostic panel: fetch DQ+feed data for a city and render inline.
  const _diagCache = {};
  const _loadDiagPanel = async (city) => {
    if (_diagCache[city]) return _diagCache[city];
    const [dqData, feedData] = await Promise.allSettled([
      getJSON("/public/data-quality/" + encodeURIComponent(city)),
      getJSON("/public/feeds/" + encodeURIComponent(city)),
    ]);
    const dq = dqData.status === "fulfilled" ? dqData.value : null;
    const fd = feedData.status === "fulfilled" ? feedData.value : null;
    _diagCache[city] = { dq, fd };
    return _diagCache[city];
  };
  const _renderDiagPanel = (entry) => {
    const { dq, fd } = entry;
    const cur = dq && dq.current;
    const fcur = fd && fd.current;
    const hist = (dq && dq.history || []).slice().sort((a, b) => String(a.day) < String(b.day) ? -1 : 1).slice(-7);
    const trendBars = hist.length ? hist.map((h) => {
      const g = h.dq_grade || "NA";
      const sc = h.dq_score == null ? null : Math.max(0, Math.min(100, Math.round(h.dq_score)));
      const fill = sc == null ? 8 : Math.max(8, sc);
      const tip = `${h.day} · grade ${g === "NA" ? "—" : g}${sc != null ? " · score " + sc : ""}`;
      return `<span class="dq-tl-cell" title="${esc(tip)}">` +
        `<span class="dq-tl-track"><i class="dq-tl-fill dq-${esc(g)}" style="height:${fill}%"></i></span>` +
        `<b class="${gradeClass(h.dq_grade)}">${esc(h.dq_grade || "—")}</b></span>`;
    }).join("") : "";
    const trend = trendBars ? `<div class="dq-timeline diag-trend" role="img" aria-label="7-day grade trend">${trendBars}</div>` : "";
    const stats = [];
    if (cur) {
      stats.push(`<div class="diag-kpi"><div class="diag-kv">${esc(cur.dq_grade || "—")}</div><div class="diag-kl">Grade</div></div>`);
      stats.push(`<div class="diag-kpi"><div class="diag-kv">${cur.dq_score != null ? esc(cur.dq_score) : "—"}</div><div class="diag-kl">Score</div></div>`);
      stats.push(`<div class="diag-kpi"><div class="diag-kv">${cur.confidence != null ? Math.round(cur.confidence * 100) + "%" : "—"}</div><div class="diag-kl">Confidence</div></div>`);
      if (cur.feed_uptime_pct != null) stats.push(`<div class="diag-kpi"><div class="diag-kv">${Math.round(cur.feed_uptime_pct)}%</div><div class="diag-kl">Feed uptime</div></div>`);
      if (cur.p50_staleness_sec != null) stats.push(`<div class="diag-kpi"><div class="diag-kv">${esc(cur.p50_staleness_sec)}s</div><div class="diag-kl">Median staleness</div></div>`);
    }
    if (fcur) {
      const pollTime = fcur.last_poll ? new Date(fcur.last_poll).toLocaleString() : "time unknown";
      stats.push(`<div class="diag-kpi"><div class="diag-kv">${fcur.ok === true ? "Observations received" : fcur.ok === false ? "No usable observations" : "Outcome unknown"}</div><div class="diag-kl">Last poll · ${esc(pollTime)}</div></div>`);
      if (fcur.n_vehicles != null) stats.push(`<div class="diag-kpi"><div class="diag-kv">${esc(fcur.n_vehicles)}</div><div class="diag-kl">Vehicle positions at last poll</div></div>`);
    }
    const issues = cur && (cur.issues || []).length
      ? cur.issues.map((i) => `<span class="dq-chip">${esc(_dqIssue(i))}</span>`).join(" ")
      : "";
    const agency = fd && fd.agency ? `<span class="muted" style="font-size:11px">${esc(fd.agency)}${fd.country ? " · " + esc(fd.country) : ""}</span>` : "";
    return `<div class="diag-panel">
      <div class="diag-header">${agency}${issues ? ' <span class="diag-issues">' + issues + "</span>" : ""}</div>
      ${stats.length ? '<div class="diag-kpis">' + stats.join("") + "</div>" : ""}
      ${trend}
      <div class="diag-actions">
        <a href="/data-quality/${encodeURIComponent(dq && dq.city || "")}/" class="view-link" style="padding:2px 8px">Full DQ history</a>
        <a href="/feeds/${encodeURIComponent(fd && fd.city || dq && dq.city || "")}/" class="view-link" style="padding:2px 8px">Feed health</a>
      </div>
    </div>`;
  };
  // F1c — each city's own tracked era beside its grade: cities were added at
  // different times, so grades cover different spans. data_since comes from
  // the /api/cities catalogue already loaded at boot.
  const citySince = (slug) => {
    const cat = cities.find((x) => x.id === slug);
    const iso = cat && (cat.data_since || cat.since);
    if (!iso) return "";
    const dt = new Date(iso);
    if (isNaN(dt.getTime())) return "";
    return dt.toLocaleDateString(undefined, { year: "numeric", month: "short" });
  };
  const rowHtml = (e) => {
    const c = e.c, st = e.st;
    const gradeMuted = (st.cls === "bad" || st.cls === "nofeed") ? ' style="opacity:.45"' : "";
    const isOpen = state.openCity === c.city;
    const since = citySince(c.city);
    return `<tr class="sev-row sev-${st.cls} dq-dir-row${isOpen ? " dq-dir-open" : ""}" data-city="${esc(c.city)}" tabindex="0" role="button" aria-expanded="${isOpen ? "true" : "false"}">
      <td><span class="status-cap cap-${st.cls}">${esc(st.label)}</span></td>
      <td class="dq-city-name">${esc(c.name || c.city)}</td>
      <td><span class="${gradeClass(c.dq_grade)}"${gradeMuted}>${esc(c.dq_grade || "—")}</span>${since ? `<div class="muted" style="font-size:10.5px;white-space:nowrap">data since ${esc(since)}</div>` : ""}</td>
      <td>${c.dq_score == null ? "—" : esc(c.dq_score)}</td>
      <td class="dq-upt">${uptBar(c.feed_uptime_pct)}</td>
      <td class="dq-cf">${confLamp(c.confidence)}</td>
      <td>${(c.issues || []).length ? (c.issues || []).map((i) => `<span class="dq-chip">${esc(_dqIssue(i))}</span>`).join("") : '<span class="muted">—</span>'}</td>
    </tr>`;
  };
  const panelRowHtml = (e) => {
    return `<tr class="dq-panel-row" data-city="${esc(e.c.city)}" id="dq-panel-${esc(e.c.city)}"><td colspan="7"><div class="diag-panel-wrap"><p class="muted" style="padding:4px 0">Loading…</p></div></td></tr>`;
  };
  host.innerHTML = _dqCityNote + header + `<p class="muted" style="margin:0 0 6px;font-size:12px;line-height:1.5">Feeds with no data for more than ${esc(retiredDays)} days are listed under Retired.</p><p class="muted" style="margin:0 0 10px;font-size:12px;line-height:1.5">
      <strong>Uptime</strong> is separately how often the feed was reachable (the grade itself is
      explained above). Click any row to open the diagnostic panel.</p>
    <div class="dq-controls">
      <input type="search" id="dq-search" class="sched-filter dq-search" placeholder="Search cities…" autocomplete="off" aria-label="Search cities">
      <div class="cmp-modes" id="dq-filters"></div>
    </div>
    <div class="dq-scroll"><table class="dq-table" id="dq-main-table"><thead><tr>
      <th>Status</th><th>City</th><th>Data grade</th><th>Score</th><th>Uptime</th><th>Confidence</th><th>Issues</th>
    </tr></thead><tbody id="dq-rows"></tbody></table></div>
    <div id="dq-more"></div>`;
  const rowsEl = host.querySelector("#dq-rows");
  const moreEl = host.querySelector("#dq-more");
  const filtersEl = host.querySelector("#dq-filters");
  const searchEl = host.querySelector("#dq-search");
  const _openPanel = async (citySlug, panelTr) => {
    const wrap = panelTr.querySelector(".diag-panel-wrap");
    if (!wrap) return;
    const entry = await _loadDiagPanel(citySlug);
    wrap.innerHTML = _renderDiagPanel(entry);
  };
  const _toggleRow = (citySlug) => {
    if (state.openCity === citySlug) {
      state.openCity = null;
    } else {
      state.openCity = citySlug;
    }
    render();
    if (state.openCity) {
      const panelTr = rowsEl.querySelector(`tr.dq-panel-row[data-city="${CSS.escape(state.openCity)}"]`);
      if (panelTr) _openPanel(state.openCity, panelTr);
    }
  };
  const render = () => {
    const q = state.q.toLowerCase().trim();
    const pass = (FILTERS.find(([k]) => k === state.filter) || FILTERS[0])[2];
    let items = enriched.filter((e) => pass(e) &&
      (!q || String(e.c.name || e.c.city).toLowerCase().includes(q)));
    items = items.slice().sort((a, b) =>
      (SEV_RANK[a.st.cls] - SEV_RANK[b.st.cls]) ||
      ((b.c.dq_score == null ? -1 : b.c.dq_score) - (a.c.dq_score == null ? -1 : a.c.dq_score)) ||
      String(a.c.name || a.c.city).localeCompare(String(b.c.name || b.c.city)));
    const shown = state.showAll ? items : items.slice(0, TOP);
    // Interleave panel row after open city's data row.
    const rows = [];
    shown.forEach((e) => {
      rows.push(rowHtml(e));
      if (state.openCity === e.c.city) rows.push(panelRowHtml(e));
    });
    rowsEl.innerHTML = rows.length ? rows.join("")
      : '<tr><td colspan="7"><p class="muted" style="padding:12px 0">No cities match.</p></td></tr>';
    // Wire click/keyboard on data rows.
    rowsEl.querySelectorAll("tr.dq-dir-row").forEach((tr) => {
      const city = tr.dataset.city;
      const activate = () => _toggleRow(city);
      tr.addEventListener("click", activate);
      tr.addEventListener("keydown", (ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); activate(); } });
    });
    moreEl.innerHTML = "";
    if (!state.showAll && items.length > TOP) {
      const btn = document.createElement("button");
      btn.className = "view-link";
      btn.style.marginTop = "10px";
      btn.textContent = `Show all ${items.length.toLocaleString()} cities`;
      btn.onclick = () => { state.showAll = true; render(); };
      moreEl.appendChild(btn);
    } else if (state.showAll && items.length > TOP) {
      const note = document.createElement("p");
      note.className = "muted";
      note.style.marginTop = "8px";
      note.textContent = `Showing all ${items.length.toLocaleString()} cities.`;
      moreEl.appendChild(note);
    }
    filtersEl.querySelectorAll(".chip").forEach((ch) =>
      ch.classList.toggle("on", ch.dataset.k === state.filter));
  };
  FILTERS.forEach(([k, label]) => {
    const n = enriched.filter((e) => (FILTERS.find(([kk]) => kk === k))[2](e)).length;
    const b = document.createElement("button");
    b.type = "button";
    b.className = "chip";
    b.dataset.k = k;
    b.textContent = `${label} ${n.toLocaleString()}`;
    b.onclick = () => { state.filter = k; state.showAll = false; state.openCity = null; render(); };
    filtersEl.appendChild(b);
  });
  searchEl.addEventListener("input", () => { state.q = searchEl.value; state.showAll = false; state.openCity = null; render(); });
  render();
}

async function loadDataQualityCity(city) {
  const host = document.getElementById("dq-output");
  if (!host) return;
  host.innerHTML = '<p class="muted">Loading…</p>';
  const back = '<p><a href="/data-quality" class="view-link" style="padding:2px 8px">← All cities</a></p>';
  let data;
  try { data = await getJSON("/public/data-quality/" + encodeURIComponent(city)); }
  catch (e) { host.innerHTML = back + '<p class="muted">No data-quality history for this city.</p>'; return; }
  const cur = data.current;
  if (!cur) { host.innerHTML = back + '<p class="muted">No grade yet for ' + esc(data.name || city) + '.</p>'; return; }
  const histArr = data.history || [];
  const uptBar = (u) => {
    if (u == null) return "—";
    const p = Math.max(0, Math.min(100, Math.round(u)));
    const cls = p >= 90 ? "up-good" : p >= 60 ? "up-mid" : "up-bad";
    return `<span class="dq-bar ${cls}"><i style="width:${p}%"></i></span><span class="dq-bar-n">${p}%</span>`;
  };
  const gradeTone = (g) => (g === "A" || g === "B") ? "good" : (g === "C" || g === "D") ? "warn" : g === "F" ? "bad" : "";
  const sevByGrade = (g) => (g === "A" || g === "B") ? "sev-ok" : (g === "C" || g === "D") ? "sev-warn" : g === "F" ? "sev-bad" : "";
  const hupts = histArr.map((h) => h.feed_uptime_pct).filter((u) => u != null).sort((a, b) => a - b);
  const medUpt = hupts.length ? hupts[Math.floor(hupts.length / 2)] : null;
  // Civic header — the city's report opening frame, matching the list page.
  // F1c — this city's own tracked era next to its grade: grades cover each
  // city's own data era, and eras differ between cities.
  const catCity = cities.find((x) => x.id === city);
  const sinceIso = catCity && (catCity.data_since || catCity.since);
  const sinceTxt = sinceIso ? longDate(sinceIso) : null;
  const header = civicHeaderStrip([
    { label: "Current grade", value: esc(cur.dq_grade || "—"), tone: gradeTone(cur.dq_grade),
      sub: sinceTxt ? "data since " + sinceTxt : undefined },
    { label: "Data-quality score", value: cur.dq_score == null ? "—" : esc(cur.dq_score) },
    { label: "Confidence", value: cur.confidence == null ? "—" : Math.round(cur.confidence * 100) + "%" },
    { label: "Days tracked", value: histArr.length },
    { label: "Median uptime", value: medUpt == null ? "—" : Math.round(medUpt) + "%" },
  ]);
  // Compact recent-history band: the last 10 days as grade-tinted bars whose
  // height reads the DAILY SCORE (0–100) — uptime often pins at 100% and told
  // no story as a height (F2-11); colour stays the daily grade.
  const tl = histArr.slice().sort((a, b) => (String(a.day) < String(b.day) ? -1 : 1)).slice(-10);
  const tlBand = tl.length ? `<div class="dq-timeline" role="img" aria-label="Recent ${tl.length}-day grade and daily-score trend, oldest to newest">` +
      tl.map((h) => {
        const g = h.dq_grade || "NA";
        const sc = h.dq_score == null ? null : Math.max(0, Math.min(100, Math.round(h.dq_score)));
        const fill = sc == null ? 8 : Math.max(8, sc);
        const up = h.feed_uptime_pct == null ? null : Math.round(h.feed_uptime_pct);
        const tip = `${h.day} · grade ${g === "NA" ? "—" : g}` +
          (sc == null ? "" : ` · score ${sc}`) + (up == null ? "" : ` · uptime ${up}%`);
        return `<span class="dq-tl-cell" title="${esc(tip)}">` +
          `<span class="dq-tl-track"><i class="dq-tl-fill dq-${esc(g)}" style="height:${fill}%"></i></span>` +
          `<b class="${gradeClass(h.dq_grade)}">${esc(h.dq_grade || "—")}</b></span>`;
      }).join("") +
    `</div><p class="dq-tl-cap muted">Last ${tl.length} days · bar height = daily score (0–100), colour = daily grade</p>` : "";
  const hist = histArr.map((h) => `<tr class="sev-row ${sevByGrade(h.dq_grade)}">
      <td>${esc(h.day)}</td><td><span class="${gradeClass(h.dq_grade)}">${esc(h.dq_grade || "—")}</span></td>
      <td>${h.dq_score == null ? "—" : esc(h.dq_score)}</td><td class="dq-upt">${uptBar(h.feed_uptime_pct)}</td>
    </tr>`).join("");
  host.innerHTML = back +
    `<h2 style="margin:6px 0 12px">${esc(data.name || city)} — feed-health report</h2>` +
    header + tlBand +
    `<p class="muted" style="margin:0 0 10px">Daily data-quality grade + feed uptime history. Issues in the latest daily report: ${esc((cur.issues || []).map(_dqIssue).join(", ") || "none recorded")}.
     <a href="/feeds/${encodeURIComponent(city)}" class="view-link" style="padding:2px 8px">Feed health →</a> <a href="/methodology" class="view-link" style="padding:2px 8px">Methodology</a></p>
     <table class="dq-table dq-hist"><thead><tr><th>Day</th><th>Grade</th><th>Score</th><th>Uptime</th></tr></thead><tbody>${hist}</tbody></table>`;
}

async function loadFeedCity(city) {
  const host = document.getElementById("feeds-output");
  const title = document.getElementById("feeds-title");
  if (!host) return;
  host.innerHTML = '<p class="muted">Loading…</p>';
  let data;
  try { data = await getJSON("/public/feeds/" + encodeURIComponent(city)); }
  catch (e) {
    if (isNotFound(e)) { host.innerHTML = '<p class="muted">No feed-health data for this city.</p>'; return; }
    host.innerHTML = serviceErrorNote("Feed health for " + city, e, "feed-city-retry");
    wireRetry("feed-city-retry", () => loadFeedCity(city));
    return;
  }
  if (title) title.textContent = "Feed health — " + (data.name || city);
  const cur = data.current;
  const configured = data.realtime_configured;
  const monitoringNote = configured === false
    ? '<p><strong>No realtime feed configured here.</strong> This entry is not counted as a monitored feed outage. Recorded snapshots and daily percentages remain below for transparency.</p>'
    : configured !== true ? '<p class="muted">Realtime monitoring configuration is unknown in this response. Recorded history does not verify current service.</p>'
    : data.poll_within_reporting_window === false ? '<p><strong>Historical poll record.</strong> No poll is recorded within the daily reporting window. A newly generated daily row does not make the old poll current.</p>'
    : data.poll_within_reporting_window !== true ? '<p class="muted">No verified recent poll time is available.</p>' : '';
  const curHtml = cur
    ? `<p>${cur.ok === true ? '<span class="ok">● observations received at last poll</span>' : cur.ok === false ? '<span class="muted">● no usable observations recorded at last poll</span>' : '<span class="muted">● last poll outcome unknown</span>'}${cur.last_poll ? " · polled " + esc(new Date(cur.last_poll).toLocaleString()) : " · poll time unknown"} · ${cur.n_vehicles == null ? "—" : esc(cur.n_vehicles)} vehicle positions · ${cur.n_delay == null ? "—" : esc(cur.n_delay)} delay observations</p><p class="muted">An empty poll alone does not establish an outage or scheduled non-service. This snapshot does not verify current service.</p>`
    : '<p class="muted">No recorded poll status.</p>';
  const hist = (data.history || []).map((h) => `<tr>
      <td>${esc(h.day)}</td><td>${_pctTxt(h.uptime_pct)}</td>
      <td>${h.p50_staleness_sec == null ? "—" : esc(h.p50_staleness_sec) + "s"}</td>
      <td>${h.avg_vehicles == null ? "—" : esc(Math.round(h.avg_vehicles))}</td>
    </tr>`).join("") || '<tr><td colspan="4">No daily history recorded.</td></tr>';
  host.innerHTML = monitoringNote + '<h2>Last recorded poll</h2>' + curHtml +
    `<p><a href="/data-quality/${encodeURIComponent(city)}" class="view-link" style="padding:2px 8px">← Data quality</a></p>
     <h2>Recorded daily history</h2><p class="muted">Snapshot success percentages describe the recorded samples on each date, not current feed availability. Samples can carry an older poll status forward; read the last-poll date above.</p>
     <div class="dq-scroll" tabindex="0" role="region" aria-label="Recorded daily feed history"><table class="dq-table"><thead><tr><th>Day</th><th>Snapshot success</th><th>Median staleness</th><th>Avg vehicles</th></tr></thead><tbody>${hist}</tbody></table></div>`;
}

async function loadFeedDirectory() {
  const host = document.getElementById("feeds-output");
  const title = document.getElementById("feeds-title");
  if (!host) return;
  if (title) title.textContent = "Feed health — all cities";
  host.innerHTML = '<p class="muted">Loading…</p>';
  let data;
  try { data = await getJSON("/public/feed-health"); }
  catch (e) {
    host.innerHTML = serviceErrorNote("Feed-health data", e, "feed-dir-retry");
    wireRetry("feed-dir-retry", () => loadFeedDirectory());
    return;
  }
  const list = (data && data.cities) || [];
  if (!list.length) { host.innerHTML = '<p class="muted">No feed-health data yet.</p>'; return; }
  // Summary buckets from real fields: stale (p50 staleness >> 1 hour), low-uptime, healthy.
  const hasEvidence = (c) => c.realtime_configured === true && c.poll_within_reporting_window === true && c.retired !== true;
  const isUnknown = (c) => !hasEvidence(c) || c.p50_staleness_sec == null || c.uptime_pct == null;
  const isStale = (c) => !isUnknown(c) && c.stale === true;
  const isDown = (c) => !isUnknown(c) && !isStale(c) && c.uptime_pct < 60;
  const isHealthy = (c) => !isUnknown(c) && !isStale(c) && c.uptime_pct >= 90;
  const isMid = (c) => !isUnknown(c) && !isStale(c) && !isDown(c) && !isHealthy(c);
  // Retired feeds (no data for > retired_after_days) are excluded from the KPI strip.
  const actList = list.filter(hasEvidence);
  const retiredDays = (data && data.retired_after_days) || 3;
  const nTotal = actList.length;
  const nHealthy = actList.filter(isHealthy).length;
  const nMid = actList.filter(isMid).length;
  const nDown = actList.filter(isDown).length;
  const nStale = actList.filter(isStale).length;
  const avgVehs = actList.map((c) => c.avg_vehicles).filter((v) => typeof v === "number" && Number.isFinite(v) && v >= 0).sort((a, b) => a - b);
  const midVeh = Math.floor(avgVehs.length / 2);
  const medVeh = avgVehs.length ? Math.round(avgVehs.length % 2 ? avgVehs[midVeh] : (avgVehs[midVeh - 1] + avgVehs[midVeh]) / 2) : null;
  const header = civicHeaderStrip([
    { label: "Daily success ≥90%", value: nHealthy, tone: "good" },
    { label: "Degraded (60–90%)", value: nMid, tone: nMid ? "warn" : "" },
    { label: "Daily success <60%", value: nDown, tone: nDown ? "bad" : "" },
    { label: "Stale data", value: nStale, tone: nStale ? "bad" : "" },
    { label: "Insufficient evidence", value: list.filter((c) => c.realtime_configured !== false && isUnknown(c)).length },
    { label: "Median vehicles/poll", value: medVeh != null ? medVeh : "—" },
    { label: "Recent monitored entries", value: nTotal },
    { label: "No realtime feed configured", value: list.filter((c) => c.realtime_configured === false).length },
  ]);
  const fhStatus = (c) => {
    if (c.realtime_configured === false) return { cls: "nofeed", label: "Not monitored here" };
    if (c.retired === true || c.poll_within_reporting_window === false) return { cls: "nofeed", label: "Historical evidence" };
    if (isUnknown(c)) return { cls: "nofeed", label: "Insufficient evidence" };
    if (isStale(c)) return { cls: "bad", label: "Stale" };
    if (isDown(c)) return { cls: "bad", label: "Low uptime" };
    if (isMid(c)) return { cls: "warn", label: "Degraded" };
    return { cls: "ok", label: "Healthy" };
  };
  const FH_SEV = { bad: 0, warn: 1, nofeed: 2, ok: 3 };
  const FH_FILTERS = [
    ["all", "All recorded entries", () => true],
    ["healthy", "Healthy", (e) => e.st.cls === "ok"],
    ["degraded", "Degraded", (e) => e.st.cls === "warn"],
    ["down", "Low uptime / stale", (e) => e.st.cls === "bad"],
    ["unknown", "Insufficient evidence", (e) => e.c.realtime_configured !== false && isUnknown(e.c)],
    ["unmonitored", "No realtime feed configured", (e) => e.c.realtime_configured === false],
    ["retired", "Historical evidence", (e) => e.c.retired === true || e.c.poll_within_reporting_window === false],
  ];
  const enriched = list.map((c) => ({ c, st: fhStatus(c) }));
  const fhState = { q: "", filter: "all", showAll: false };
  const TOP = 50;
  const uptBar2 = (u) => {
    if (u == null) return "—";
    const p = Math.max(0, Math.min(100, Math.round(u)));
    const cls = p >= 90 ? "up-good" : p >= 60 ? "up-mid" : "up-bad";
    return `<span class="dq-bar ${cls}"><i style="width:${p}%"></i></span><span class="dq-bar-n">${p}%</span>`;
  };
  const staleFmt = (s) => {
    if (s == null) return "—";
    if (s > 3600) return `<span style="color:#b3261e">${Math.round(s / 3600)}h</span>`;
    return esc(s) + "s";
  };
  const fhRowHtml = (e) => {
    const c = e.c, st = e.st;
    return `<tr class="sev-row sev-${st.cls}">
      <td><span class="status-cap cap-${st.cls}">${esc(st.label)}</span><div class="muted">${c.last_poll ? 'Last poll ' + esc(String(c.last_poll).slice(0, 10)) : 'Poll time unknown'}</div></td>
      <td><a href="/feeds/${encodeURIComponent(c.city)}">${esc(c.name || c.city)}</a></td>
      <td>${uptBar2(c.uptime_pct)}</td>
      <td>${staleFmt(c.p50_staleness_sec)}</td>
      <td>${staleFmt(c.p95_staleness_sec)}</td>
      <td>${c.avg_vehicles != null ? esc(Math.round(c.avg_vehicles)) : "—"}</td>
      <td class="muted" style="font-size:11px">${esc(c.day || "")}</td>
    </tr>`;
  };
  host.innerHTML = header +
    `<p class="muted">Daily categories require a configured realtime feed and a poll recorded within the ${esc(retiredDays)}-day reporting window. Insufficient evidence also includes historical entries. All recorded entries, including unmonitored and historical entries, remain available below.</p><p class="muted">Snapshot success percentages and labels describe each row's reported date. Samples can carry an older poll status forward. They do not establish current feed availability, an outage or scheduled non-service.</p><div class="dq-controls fh-controls">
      <input type="search" id="fh-search" class="sched-filter dq-search" placeholder="Search cities…" autocomplete="off" aria-label="Search cities">
      <select id="fh-filter" class="sched-filter" aria-label="Filter feed history"></select>
    </div>
    <div class="dq-scroll" tabindex="0" role="region" aria-label="Feed history entries"><table class="dq-table"><thead><tr>
      <th>Evidence</th><th>City</th><th>Snapshot success</th><th>Median staleness</th><th>P95 staleness</th><th>Avg vehicles</th><th>Reported day</th>
    </tr></thead><tbody id="fh-rows"></tbody></table></div>
    <div id="fh-more"></div>`;
  const rowsEl = host.querySelector("#fh-rows");
  const moreEl = host.querySelector("#fh-more");
  const filtersEl = host.querySelector("#fh-filter");
  const searchEl = host.querySelector("#fh-search");
  const fhRender = () => {
    const q = fhState.q.toLowerCase().trim();
    const pass = (FH_FILTERS.find(([k]) => k === fhState.filter) || FH_FILTERS[0])[2];
    let items = enriched.filter((e) => pass(e) &&
      (!q || String(e.c.name || e.c.city).toLowerCase().includes(q)));
    items = items.slice().sort((a, b) =>
      (FH_SEV[a.st.cls] - FH_SEV[b.st.cls]) ||
      ((a.c.uptime_pct == null ? 100 : a.c.uptime_pct) - (b.c.uptime_pct == null ? 100 : b.c.uptime_pct)) ||
      String(a.c.name || a.c.city).localeCompare(String(b.c.name || b.c.city)));
    const shown = fhState.showAll ? items : items.slice(0, TOP);
    rowsEl.innerHTML = shown.length ? shown.map(fhRowHtml).join("")
      : '<tr><td colspan="7"><p class="muted" style="padding:12px 0">No cities match.</p></td></tr>';
    moreEl.innerHTML = "";
    if (!fhState.showAll && items.length > TOP) {
      const btn = document.createElement("button");
      btn.className = "view-link";
      btn.style.marginTop = "10px";
      btn.textContent = `Show all ${items.length.toLocaleString()} cities`;
      btn.onclick = () => { fhState.showAll = true; fhRender(); };
      moreEl.appendChild(btn);
    } else if (fhState.showAll && items.length > TOP) {
      const note = document.createElement("p");
      note.className = "muted";
      note.style.marginTop = "8px";
      note.textContent = `Showing all ${items.length.toLocaleString()} cities.`;
      moreEl.appendChild(note);
    }
    filtersEl.value = fhState.filter;
  };
  FH_FILTERS.forEach(([k, label]) => {
    const n = enriched.filter((e) => (FH_FILTERS.find(([kk]) => kk === k))[2](e)).length;
    const b = document.createElement("option");
    b.value = k;
    b.textContent = `${label} ${n.toLocaleString()}`;
    filtersEl.appendChild(b);
  });
  filtersEl.addEventListener("change", () => { fhState.filter = filtersEl.value; fhState.showAll = false; fhRender(); });
  searchEl.addEventListener("input", () => { fhState.q = searchEl.value; fhState.showAll = false; fhRender(); });
  fhRender();
}

/* ─────────── favorites (localStorage; flag: favorites) ─────────── */
const FAV_KEY = "gts_favorites";
function loadFavs() { try { return JSON.parse(localStorage.getItem(FAV_KEY) || "[]"); } catch (e) { return []; } }
function saveFavs(a) { try { localStorage.setItem(FAV_KEY, JSON.stringify(a)); } catch (e) { /* ignore */ } }
function isFav(id) { return loadFavs().indexOf(id) >= 0; }
function toggleFav(id) {
  if (!id) return;
  const a = loadFavs();
  const i = a.indexOf(id);
  if (i >= 0) a.splice(i, 1); else a.push(id);
  saveFavs(a);
  renderFavBar(); updateFavStar();
}
function updateFavStar() {
  const b = document.getElementById("fav-star");
  if (!b) return;
  if (!flag("favorites") || !current) { b.hidden = true; return; }
  b.hidden = false;
  const saved = isFav(current);
  b.classList.toggle("saved", saved);
  b.innerHTML = uiIcon(saved ? "starFill" : "star", 13) + (saved ? " Saved" : " Save");
  b.setAttribute("aria-pressed", saved ? "true" : "false");
}
function renderFavBar() {
  const bar = document.getElementById("fav-bar");
  if (!bar || !flag("favorites")) return;
  const favs = loadFavs().map((id) => cities.find((c) => c.id === id)).filter(Boolean);
  if (!favs.length) { bar.hidden = true; bar.innerHTML = ""; return; }
  bar.hidden = false;
  bar.innerHTML = '<span class="fav-lbl">Saved:</span> ' +
    favs.map((c) => `<a href="/city/${encodeURIComponent(c.id)}" class="fav-chip">${esc(c.name)}</a>`).join("");
}

async function renderHeadwayDirectory(host, note) {
  host.innerHTML = '<p class="muted">Loading available headway cities…</p>';
  var d;
  try { d = await getJSON("/public/headway/directory"); }
  catch (e) {
    host.innerHTML = serviceErrorNote("Headway data", e, "hw-dir-retry");
    wireRetry("hw-dir-retry", () => renderHeadwayDirectory(host, note));
    return;
  }
  var cities = (d && d.cities) || [];
  if (!cities.length) { host.innerHTML = '<p class="muted">No headway data available yet.</p>'; return; }
  // note may be raw HTML (coverage-empty-note div) or plain text — render separately.
  var noteHtml = note || "";
  var h = noteHtml +
    '<p class="muted rel-lb-intro">Headway measures how evenly vehicles are spaced (and how often they bunch). It\'s derived for the networks listed here — select one for its route-level breakdown:</p>' +
    '<table class="rel-lb-tbl hw-dir-tbl"><thead><tr><th>City</th><th class="hw-num">Routes</th><th class="hw-num" title="Average of the per-route median gaps — low- and high-frequency routes mixed, not a network headway">Typical route gap</th><th>Evenness</th></tr></thead><tbody>';
  cities.forEach(function (c) {
    var gap = c.med_headway_sec != null ? Math.round(c.med_headway_sec / 60) + " min" : "—";
    var cv = c.avg_cv;
    var even = cv == null ? "—" : (cv < 0.5 ? "Even" : cv < 0.75 ? "Some bunching" : "Bunchy");
    h += '<tr class="rel-lb-row" data-city="' + esc(c.city) + '" tabindex="0" role="button">' +
      '<td class="rel-lb-name">' + esc(c.name) + (c.country ? ' <span class="rel-lb-cc">' + esc(c.country) + '</span>' : '') + '</td>' +
      '<td class="hw-num">' + c.n_routes + '</td><td class="hw-num">' + gap + '</td><td>' + even + '</td></tr>';
  });
  h += '</tbody></table>';
  host.innerHTML = h;
  host.querySelectorAll(".rel-lb-row").forEach(function (row) {
    var go = function () { navigate("/headway/" + row.getAttribute("data-city")); };
    row.addEventListener("click", go);
    row.addEventListener("keydown", function (ev) { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); go(); } });
  });
}

async function loadHeadway(city) {
  const host = document.getElementById("headway-output");
  const title = document.getElementById("headway-title");
  if (!host) return;
  if (!city) {
    if (title) title.textContent = "Headway & bunching";
    renderHeadwayDirectory(host);
    return;
  }
  host.innerHTML = '<p class="muted">Loading…</p>';
  let data;
  try { data = await getJSON("/public/headway/" + encodeURIComponent(city)); }
  catch (e) {
    // §E honest empty-state: name the city and explain the gap.
    const cityObj = cities.find((c) => c.id === city);
    const cityName = (cityObj && cityObj.name) || city;
    if (!isNotFound(e)) {
      host.innerHTML = serviceErrorNote("Headway data for " + cityName, e, "hw-city-retry");
      wireRetry("hw-city-retry", () => loadHeadway(city));
      return;
    }
    const note = `<div class="coverage-empty-note" role="status">
      No headway data for <strong>${esc(cityName)}</strong> — headway is captured only for high-frequency networks where vehicle-spacing can be reliably derived from live positions. Select a city from the list below.
    </div>`;
    renderHeadwayDirectory(host, note);
    return;
  }
  if (title) title.textContent = "Headway & bunching — " + (data.name || city);
  const rows = data.routes || [];
  if (!rows.length) {
    if (title) title.textContent = "Headway & bunching";
    const cityName = data.name || city;
    const note = `<div class="coverage-empty-note" role="status">
      <strong>${esc(cityName)}</strong> isn't in the headway set yet — it needs a high-frequency network with live position data. Select a tracked city from the list below.
    </div>`;
    renderHeadwayDirectory(host, note);
    return;
  }
  const mins = (s) => s == null ? "—" : Math.round(s / 60) + " min";
  const speed = (v) => v == null ? "—" : v + " km/h";
  const sevLabel = (cv) => cv == null ? "—" : (cv < 0.5 ? "Even" : cv < 0.75 ? "Some bunching" : "Bunchy");
  // Diagnostic summary: how this network is actually running, right now.
  const _med = (arr) => { const a = arr.filter((x) => x != null).sort((x, y) => x - y); return a.length ? a[Math.floor(a.length / 2)] : null; };
  const medWait = _med(rows.map((r) => r.p50_observed_headway_sec));
  const medCv = _med(rows.map((r) => r.cv_headway));
  const totalArr = rows.reduce((a, r) => a + (r.n_arrivals || 0), 0);
  const bunchy = rows.filter((r) => r.cv_headway != null && r.cv_headway >= 0.75).length;
  const header = civicHeaderStrip([
    { label: "Routes tracked", value: rows.length },
    { label: "Median wait", value: mins(medWait) },
    { label: "Typical evenness", value: sevLabel(medCv), tone: medCv == null ? "" : (medCv >= 0.75 ? "bad" : medCv >= 0.5 ? "warn" : "good") },
    { label: "Bunchy routes", value: bunchy, tone: bunchy ? "bad" : "" },
    { label: "Observed arrivals", value: totalArr.toLocaleString() },
  ]);
  // Worst-bunched first — the diagnostic reads as "what needs attention".
  const sorted = rows.slice().sort((a, b) => (b.cv_headway == null ? -1 : b.cv_headway) - (a.cv_headway == null ? -1 : a.cv_headway));
  const sevRow = (cv) => cv == null ? "" : (cv < 0.5 ? "sev-ok" : cv < 0.75 ? "sev-warn" : "sev-bad");
  // Evenness renders as a labelled meter, not a wall of identical capsules —
  // the gap CV is named in the header and each row's value is comparable at a
  // glance (F2-12). Meter tone matches the severity bands.
  const cvMeter = (cv) => {
    if (cv == null) return "—";
    const p = Math.max(4, Math.min(100, Math.round((cv / 1.5) * 100)));
    const tone = cv < 0.5 ? "#0a7a3f" : cv < 0.75 ? "#b45309" : "#b3261e";
    return `<span class="hw-meter"><i style="width:${p}%;background:${tone}"></i></span>` +
      `<span class="hw-cv">${cv}</span> <span class="muted">${sevLabel(cv)}</span>`;
  };
  // Coverage is a dead column when every route reads 100% — hide it then.
  const covPct = (c) => c == null ? null : Math.round(c * 100);
  const showCov = sorted.some((r) => covPct(r.confidence) != null && covPct(r.confidence) < 100);
  const hwDayRow = rows.find((r) => r.day);
  const hwDay = hwDayRow ? esc(hwDayRow.day) : "";
  const showSched = rows.some((r) => r.pct_bunched != null);
  const schedCell = (r) => r.pct_bunched == null || r.pct_gapped == null ? "—" : esc(r.pct_bunched) + "% / " + esc(r.pct_gapped) + "%";
  const body = sorted.map((r) => `<tr class="sev-row ${sevRow(r.cv_headway)}">
      <td>${esc(r.route_id)}</td>
      <td>${mins(r.p50_observed_headway_sec)}</td>
      <td class="hw-even">${cvMeter(r.cv_headway)}</td>` +
      (showSched ? `<td>${schedCell(r)}</td>` : "") +
      `<td>${speed(r.avg_speed_kmh)}</td>
      <td>${r.n_arrivals}</td>` +
      (showCov ? `<td>${covPct(r.confidence) == null ? "—" : covPct(r.confidence) + "%"}</td>` : "") +
    `</tr>`).join("");
  host.innerHTML = header +
    '<p class="muted" style="margin-bottom:8px">' + (hwDay ? "Data for " + hwDay + " (agency-local day), both directions combined, " : "Data for the latest derived day, both directions combined, ") + '<strong>worst-bunched routes first</strong>. <strong>Typical wait</strong> = median time between vehicles; <strong>evenness</strong> = the gap CV (spacing variability — lower is steadier; ≥0.75 reads as bunching + gaps); <strong>avg speed</strong> = median vehicle speed across tracked segments. Approximate, from live vehicle positions.' +
    (showCov ? "" : " Every route here has at least 6 observed arrivals (the confidence floor).") +
    (showSched ? "" : " Schedule-based bunching (% of gaps under half the scheduled headway) is not available for this day: the derivation had no scheduled headway for these routes; evenness below is from observed spacing only.") +
    ' <a href="/methodology" class="view-link" style="padding:2px 8px">Methodology</a></p>' +
    `<div class="dq-scroll"><table class="dq-table dq-hist hw-detail-tbl" tabindex="-1"><thead><tr><th>Route</th><th>Typical wait</th><th>Evenness <span class="muted" style="font-weight:400">(gap CV — lower is steadier)</span></th>${showSched ? "<th>Bunched / gapped</th>" : ""}<th>Avg speed</th><th>Observed arrivals</th>${showCov ? '<th title="min(1, arrivals/6): a sample-size floor, not data coverage">Sample confidence</th>' : ""}</tr></thead><tbody>${body}</tbody></table></div>`;
}

let reliabilityWindow = "30d";
let lastReliability = null;
function hour12(h) {
  h = ((h % 24) + 24) % 24;
  if (h === 0) return "12 AM";
  if (h === 12) return "12 PM";
  return (h < 12 ? h + " AM" : (h - 12) + " PM");
}
function shareReliability() {
  const d = lastReliability;
  if (!d || !current) return;
  const o = d.overall || {};
  const url = location.origin + "/reliability/" + encodeURIComponent(current);
  const rank = o.rank ? `, ranks #${o.rank} of ${o.n_cities} cities` : "";
  const text = `${d.name}: transit reliability ${o.grade} — ${o.on_time_pct}% on-time over ${d.window}${rank}. ${url}`;
  const done = () => showToast("Report link + summary copied", "info");
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).then(done).catch(() => done());
  } else { done(); }
}

// §S9 Advocacy CTA: builds a plain-English letter a rider can send to their
// transit agency or paste into a public comment. Every figure comes from the
// live reliability data already on the page — no invented facts or fake URLs.
function buildAdvocacyText() {
  const d = lastReliability;
  if (!d || !current) return "";
  const o = d.overall || {};
  const city = d.name || current;
  const pct  = o.on_time_pct != null ? o.on_time_pct + "%" : "(unknown)";
  const grade = o.grade || "(unknown)";
  const win  = d.window || "30d";
  const obs  = o.n_obs ? Number(o.n_obs).toLocaleString() : "";
  const rankLine = o.rank
    ? `\n- Ranks #${o.rank} of ${o.n_cities} cities tracked globally`
    : "";
  const ci = (o.on_time_ci && o.on_time_ci.length === 2)
    ? `\n- 95% confidence interval: ${o.on_time_ci[0]}%–${o.on_time_ci[1]}%`
    : "";
  const obsLine = obs ? `\n- Based on ${obs} vehicle-position observations` : "";
  const url = location.origin + "/reliability/" + encodeURIComponent(current);
  const reportNote = `\n- Full methodology: ${location.origin}/methodology`;
  return (
`To: [your transit agency contact, city councillor, or transit board representative]
Subject: ${city} transit reliability — independent data for your review

Dear [name / transit board],

I am writing to share independent, publicly-available data on ${city}'s transit reliability, collected by Global Transit Stats using live GTFS-Realtime feeds published by the agency.

Key figures (last ${win}, as of ${new Date().toLocaleDateString(undefined, {month:"long", year:"numeric"})}):
- On-time rate: ${pct} (grade: ${grade})${rankLine}${ci}${obsLine}${reportNote}

These numbers are drawn entirely from the agency's own published real-time data — not from passenger complaints or estimates. The full dataset, methodology and historical trend are public at:
${url}

I hope this data is useful context for planning and investment decisions. I am happy to discuss further.

[Your name]

---
Data source: public GTFS-Realtime feeds. Global Transit Stats is an independent monitoring service — not affiliated with any transit agency.`
  );
}

// Prefilled advocacy draft for a TIMETABLE-ONLY city (no realtime feed to grade).
// Auto-references the city, the coverage limitation, its published routes/lines
// (from the scheduled schedule, when present), the period, and the data source —
// still clipboard-only, with no fabricated agency destination (ChatGPT #1 + #5).
function buildAdvocacyTextNoRealtime(cityName, sched) {
  const period = new Date().toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const agency = (sched && sched.agency) || "the transit agency";
  const nRoutes = sched && sched.n_routes != null ? Number(sched.n_routes).toLocaleString() : null;
  const nStops = sched && sched.n_stops != null ? Number(sched.n_stops).toLocaleString() : null;
  // Name a handful of specific lines so the ask is concrete, not generic.
  const routeLabels = (sched && Array.isArray(sched.routes) ? sched.routes : [])
    .map((r) => r && r.label).filter(Boolean).slice(0, 8);
  const routesLine = routeLabels.length
    ? `\n- Example routes affected: ${routeLabels.join(", ")}${(nRoutes && sched.routes.length < Number(sched.n_routes)) ? " (and others)" : ""}`
    : "";
  const scopeLine = nRoutes
    ? `\n- Scope published today: ${nRoutes} routes${nStops ? ` across ${nStops} stops` : ""} (static timetable only)`
    : "";
  const url = location.origin + "/reliability/" + encodeURIComponent(current || "");
  return (
`To: [your transit agency contact, city councillor, or transit board representative]
Subject: ${cityName} — please publish a public GTFS-Realtime feed

Dear [name / transit board],

I am writing to ask ${agency} to publish a public GTFS-Realtime feed for ${cityName}'s transit service.

${cityName} currently publishes only a static GTFS timetable — riders and independent researchers can see the scheduled service, but there is no way to measure whether vehicles actually run on time, because no live vehicle-position or trip-update feed is published.

The coverage limitation, as of ${period}:
- No GTFS-Realtime feed is available for ${cityName}, so on-time performance cannot be measured${scopeLine}${routesLine}

A public GTFS-Realtime feed (the same open standard Google Maps and most transit apps consume) would let riders, planners and independent monitors track real-time reliability — at no cost to riders and using data the agency's vehicles already generate.

Global Transit Stats already lists ${cityName}'s scheduled service and will begin publishing independent reliability grades automatically the moment a realtime feed is available:
${url}

I would be glad to discuss what publishing this feed involves.

[Your name]

---
Global Transit Stats is an independent public-transit monitoring service — not affiliated with any transit agency. We track only publicly-published GTFS and GTFS-Realtime feeds.`
  );
}

// ─────────── Advocate draft modal (ChatGPT #3) ───────────
// Shows the generated letter (built from live city data) in an accessible,
// focus-trapped dialog with a Copy button — so the feature is visible + testable
// instead of a silent clipboard write. Escape / backdrop / Close all dismiss it;
// still clipboard-only (no fabricated destinations).
let _draftLastFocus = null;
let _draftKeyHandler = null;
function openDraftModal(text) {
  const modal = document.getElementById("draft-modal");
  const ta = document.getElementById("draft-text");
  if (!modal || !ta) {
    // Graceful fallback: if the modal markup is absent, copy as before.
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text);
    showToast("Draft letter copied", "info");
    return;
  }
  ta.value = text;
  _draftLastFocus = document.activeElement;
  modal.hidden = false;
  const focusables = () => Array.prototype.slice.call(
    modal.querySelectorAll('button, textarea, [href], [tabindex]:not([tabindex="-1"])'))
    .filter((el) => !el.disabled && el.offsetParent !== null);
  const closeBtn = document.getElementById("draft-close");
  if (closeBtn) closeBtn.focus();
  _draftKeyHandler = function (ev) {
    if (ev.key === "Escape") { ev.preventDefault(); closeDraftModal(); return; }
    if (ev.key === "Tab") {
      const f = focusables();
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (ev.shiftKey && document.activeElement === first) { ev.preventDefault(); last.focus(); }
      else if (!ev.shiftKey && document.activeElement === last) { ev.preventDefault(); first.focus(); }
    }
  };
  document.addEventListener("keydown", _draftKeyHandler, true);
}
function closeDraftModal() {
  const modal = document.getElementById("draft-modal");
  if (!modal || modal.hidden) return;
  modal.hidden = true;
  if (_draftKeyHandler) { document.removeEventListener("keydown", _draftKeyHandler, true); _draftKeyHandler = null; }
  if (_draftLastFocus && _draftLastFocus.focus) { try { _draftLastFocus.focus(); } catch (e) {} }
  _draftLastFocus = null;
}
function initDraftModal() {
  const modal = document.getElementById("draft-modal");
  if (!modal || modal._wired) return;
  modal._wired = true;
  modal.querySelectorAll("[data-draft-close]").forEach((el) => el.addEventListener("click", closeDraftModal));
  const x = document.getElementById("draft-close");
  if (x) x.addEventListener("click", closeDraftModal);
  const copy = document.getElementById("draft-copy");
  if (copy) copy.addEventListener("click", () => {
    const ta = document.getElementById("draft-text");
    const text = ta ? ta.value : "";
    const done = () => showToast("Draft copied — paste into your email or message", "info");
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(() => { if (ta) { ta.focus(); ta.select(); } done(); });
    } else if (ta) { ta.focus(); ta.select(); document.execCommand && document.execCommand("copy"); done(); }
  });
}
function advocacyCopy() {
  const text = buildAdvocacyText();
  if (!text) return;
  openDraftModal(text);
}
// Windows offered by the report card (F17): the reliability API declares its
// OWN accepted subset via /api/config `reliability_windows` (served from the
// exact dict the endpoints validate against) — render precisely that list.
// Against an older API without the field, fall back to the previous derivation:
// the canonical served list filtered to the known reliability keys, with the
// documented trio appended so the picker still works during transition.
// Labels come from the served /api/windows metadata — no hardcoded copies.
function reliabilityWindowChoices() {
  let keys = (CONFIG && Array.isArray(CONFIG.reliability_windows))
    ? CONFIG.reliability_windows.filter((k) => typeof k === "string")
    : [];
  if (!keys.length) {
    const served = servedWindowKeys();
    keys = served.filter((k) => k === "7d" || k === "30d" || k === "90d");
    if (keys.indexOf("7d") === -1) keys.unshift("7d");
    if (keys.indexOf("30d") === -1) keys.splice(1, 0, "30d");
    if (keys.indexOf("90d") === -1) keys.push("90d");
  }
  const FALLBACK_LBL = { "7d": "7 days", "30d": "30 days", "90d": "90 days" };
  return keys.map((k) => [k,
    (WINDOW_META[k] && WINDOW_META[k].label) || FALLBACK_LBL[k] || k]);
}
function buildReliabilityWindows() {
  const w = document.getElementById("reliability-windows");
  if (!w) return;
  w.innerHTML = "";
  const choices = reliabilityWindowChoices();
  // If the server no longer offers the selected window, snap to a served one
  // (prefer 30d) so the picker never renders with no active button and the
  // next fetch never sends a key the API would 400.
  if (!choices.some(([k]) => k === reliabilityWindow)) {
    reliabilityWindow = choices.some(([k]) => k === "30d")
      ? "30d" : (choices[0] ? choices[0][0] : "30d");
  }
  choices.forEach(([k, lbl]) => {
    const b = document.createElement("button");
    b.className = "rel-win" + (k === reliabilityWindow ? " active" : "");
    b.textContent = lbl;
    b.onclick = () => { reliabilityWindow = k; buildReliabilityWindows(); loadReliability(current); };
    w.appendChild(b);
  });
}
function relTrend(t) {
  if (t == null) return '<span class="muted">—</span>';
  if (t > 0) return `<span class="rel-trend-up">▲&nbsp;+${t}&nbsp;pp</span>`;
  if (t < 0) return `<span class="rel-trend-down">▼&nbsp;${t}&nbsp;pp</span>`;
  return '<span class="muted">flat</span>';
}
// Signed delay → unambiguous "Nm late" / "Nm early" / "on time". Negative is
// EARLY (also a reliability failure) — never let it read as "good".
function relDeviation(s) {
  if (s == null) return "—";
  const m = Math.round(Math.abs(s) / 60);
  if (m === 0) return "on time";
  return m + "m " + (s > 0 ? "late" : "early");
}
// 95% Wilson interval [lo,hi] (percentage points) → a "±h" chip. Thin data
// reads visibly wide instead of hiding behind a confident point estimate.
function relCi(ci, unit) {
  if (!ci || ci.length !== 2) return "";
  const half = ((ci[1] - ci[0]) / 2).toFixed(1);
  return ' <span class="rel-ci" title="95% confidence interval ' + ci[0] + '–' + ci[1] + '%">±'
    + half + (unit || "") + '</span>';
}
function relRouteRows(rows) {
  return (rows || []).map((r) => `<tr>
      <td><span class="${gradeClass(r.grade)}">${esc(r.grade)}</span></td>
      <td>${esc(r.label)}</td>
      <td>${r.on_time_pct}%${relCi(r.on_time_ci)}</td>
      <td>${esc(relDeviation(r.avg_delay_sec))}</td>
      <td>${relTrend(r.trend_pct)}</td>
    </tr>`).join("");
}
// Letter grade for an on-time % — driven by the SERVED grade bands (F17),
// never a hardcoded 90/80/70/60 copy of quality-config.json.
var pctGrade = function (pct) {
  for (const b of GRADE_BANDS) { if (pct >= b.min_pct) return b.letter; }
  return GRADE_BANDS[GRADE_BANDS.length - 1].letter;
}

// Reusable "Civic Report Header": a dense evidence strip (big value + label +
// optional sub) shared across Leaderboard / Data Quality / Compare / Headway so
// the secondary pages read like one publication system, not raw admin tables.
// items: [{label, value, sub?, tone?}]  (value/tone are pre-built HTML-safe).
function civicHeaderStrip(items) {
  var h = '<div class="civic-strip">';
  items.forEach(function (it) {
    if (!it) return;
    h += '<div class="civic-stat' + (it.tone ? ' civic-' + it.tone : '') + '">' +
      '<div class="civic-val">' + it.value + '</div>' +
      '<div class="civic-lbl">' + esc(it.label) + '</div>' +
      (it.sub ? '<div class="civic-sub">' + esc(it.sub) + '</div>' : '') +
      '</div>';
  });
  h += '</div>';
  return h;
}

async function renderReliabilityLeaderboard(host) {
  host.innerHTML = '<p class="muted">Loading the global on-time leaderboard…</p>';
  var d;
  // A failed or empty API response is a SERVICE problem and is shown as one.
  // It must never read as "no cities have data" (2026-10-23 QC #2).
  var fail = function(why) {
    host.innerHTML = '<div class="coverage-empty-note" role="alert">'
      + '<strong>The global leaderboard is temporarily unavailable.</strong> ' + esc(why)
      + ' The underlying on-time history is intact; this is a service error, not missing data. '
      + '<button type="button" class="view-btn" id="rel-lb-retry">Retry</button> '
      + '<a href="/methodology" class="view-link" style="padding:2px 8px">Methodology</a></div>';
    var rb = document.getElementById("rel-lb-retry");
    if (rb) rb.addEventListener("click", function () { renderReliabilityLeaderboard(host); });
  };
  try {
    d = await getJSON("/public/reliability/leaderboard?window=" + reliabilityWindow);
  } catch (e) {
    fail("The server did not return the ranking (" + String((e && e.message) || e) + ").");
    return;
  }
  if (d.ranking_status === "unavailable") {
    fail("The hourly ranking has not been computed yet, so there is nothing to rank against.");
    return;
  }
  // Provenance line: when the scores were computed (hourly precompute) and
  // whether that is current. A stale board says so instead of posing as fresh.
  var asOf = "";
  if (d.ranking_computed_at) {
    var _at = new Date(d.ranking_computed_at);
    asOf = '<p class="muted rel-lb-asof">Scores computed ' + esc(isNaN(_at) ? d.ranking_computed_at : _at.toLocaleString())
      + ' from each city\'s completed local days; refreshed hourly.'
      + (d.ranking_status === "stale" ? ' <strong>Stale: the hourly refresh has not run for over 3 hours; the ranking shown is the last one computed.</strong>' : '')
      + '</p>';
  }

  var col = function(title, rows) {
    var _col = '<div class="rel-lb-col"><h3>' + title + '</h3><table class="rel-lb-tbl"><tbody>';
    rows.forEach(function(r) {
      _col += '<tr class="rel-lb-row" data-city="' + esc(r.city) + '" tabindex="0" role="button">'
      + '<td class="rel-lb-rank">#' + r.rank + '</td>'
      + '<td class="rel-lb-name">' + esc(r.name) + (r.country ? ' <span class="rel-lb-cc">' + esc(r.country) + '</span>' : '')
      // Same place name, different agency (e.g. three New York operators): the API names the agency only then.
      + (r.agency ? '<div class="rel-lb-agency">' + esc(r.agency) + '</div>' : '') + '</td>'
      + '<td class="rel-lb-pct"><span class="' + gradeClass(pctGrade(r.on_time)) + '">' + r.on_time + '%</span>'
      + (r.on_time_ci ? '<div class="rel-lb-ci" title="95% confidence interval ' + r.on_time_ci[0] + '–' + r.on_time_ci[1] + '%">±' + ((r.on_time_ci[1] - r.on_time_ci[0]) / 2).toFixed(1) + (r.coverage_pct != null ? ' · ' + Math.round(r.coverage_pct) + '% cov' : '') + '</div>' : '')
      + '</td></tr>';
    });
    _col += '</tbody></table></div>';
    return _col;
  };

  var s = d.snapshot;
  var header = s ? civicHeaderStrip([
    { label: "Ranked cities", value: s.total },
    { label: "Median on-time", value: s.median_on_time + "%" },
    { label: "Cities at " + GRADE_BANDS[0].letter + " (" + GRADE_BANDS[0].min_pct + "%+)", value: s.n_grade_a },
    { label: "Most reliable", value: esc(s.best.name), sub: s.best.on_time + "%" + (s.best.agency ? " · " + s.best.agency : "") + (s.best.country ? " · " + s.best.country : ""), tone: "good" },
    { label: "Least reliable", value: esc(s.worst.name), sub: s.worst.on_time + "%" + (s.worst.agency ? " · " + s.worst.agency : "") + (s.worst.country ? " · " + s.worst.country : ""), tone: "bad" },
  ]) : "";
  host.innerHTML = header
  + '<p class="muted rel-lb-intro">On-time performance across all <strong>' + d.total + '</strong> cities with enough on-time history (last ' + esc(reliabilityWindow) + ', min ' + (s ? s.min_obs : 500) + ' observations). Ranked by the conservative 95% Wilson lower bound — a thin sample can\'t out-rank a well-measured one.' + (s && s.n_provisional ? ' <strong>' + s.n_provisional + '</strong> more cities had data but under ' + (s.coverage_min_pct || 50) + '% data coverage this window — shown as provisional on their own pages, never ranked.' : '') + (s && s.n_quality_hold ? ' <strong>' + s.n_quality_hold + '</strong> more were held off the board because their feeds report implausible delays (for example, every vehicle at the same delay).' : '') + ' Select any city for its full route-level report.</p>'
  + '<p class="muted">' + esc(effectiveWindowText(d)) + '</p>'
  + asOf
  + '<div class="rel-lb-grid">' + col("Most reliable", d.top) + col("Least reliable", d.bottom) + '</div>';

  host.querySelectorAll(".rel-lb-row").forEach(function(row) {
    var go = function(){ navigate("/reliability/" + row.getAttribute("data-city")); };
    row.addEventListener("click", go);
    row.addEventListener("keydown", function(ev) {
      if (ev.key === "Enter" || ev.key === " ") {
        ev.preventDefault();
        go();
      }
    });
  });
}

async function loadReliability(city) {
  const host = document.getElementById("reliability-output");
  const title = document.getElementById("reliability-title");
  if (!host) return;
  buildReliabilityWindows();
  const _sb = document.getElementById("reliability-share");
  if (_sb) _sb.hidden = true;   // shown only once a real report renders
  const _ab = document.getElementById("reliability-advocate");
  if (_ab) _ab.hidden = true;
  if (!city) {
    if (title) title.textContent = "Reliability — global leaderboard";
    renderReliabilityLeaderboard(host);
    return;
  }
  host.innerHTML = '<p class="muted">Loading…</p>';
  let d;
  try { d = await getJSON("/public/reliability/" + encodeURIComponent(city) + "?window=" + reliabilityWindow); }
  catch (e) {
    // §E honest empty-state: tell the user what's missing and why (not a silent gap).
    const cityObj = cities.find((c) => c.id === city);
    const cityName = (cityObj && cityObj.name) || city;
    if (!isNotFound(e)) {
      host.innerHTML = serviceErrorNote("The reliability report for " + cityName, e, "rel-city-retry");
      wireRetry("rel-city-retry", () => loadReliability(city));
      return;
    }
    host.innerHTML = `<div class="coverage-empty-note" role="status">
      No on-time history for <strong>${esc(cityName)}</strong> yet — it needs a monitored realtime feed with enough accrued observations.
      Reliability data populates automatically once the feed has been polled for several days.
      <a href="/methodology" class="view-link" style="padding:2px 8px">Methodology</a>
    </div>`;
    return;
  }
  if (title) title.textContent = "Reliability Report Card — " + (d.name || city);
  lastReliability = d;
  // Keep the URL a shareable permalink for this city's report — but only while
  // a TOP-LEVEL reliability view for this load still owns the URL. The city-hub
  // alias (/city/{slug}/reliability) is equally canonical and must never be
  // rewritten, and a stale async completion must never clobber a URL the user
  // has already left (e.g. back/forward moved on mid-fetch).
  if (!_routing) {
    const r0 = currentRoute();
    if (r0.view === "reliability" && (!r0.city || r0.city === city)) {
      const want = "/reliability/" + city;
      if (location.pathname !== want) history.replaceState({}, "", want);
      updatePageSeo();
    }
  }
  const shareBtn = document.getElementById("reliability-share");
  if (shareBtn) {
    shareBtn.hidden = false;
    shareBtn.innerHTML = uiIcon("share", 12) + " Share";
    shareBtn.onclick = shareReliability;
  }
  const advBtn = document.getElementById("reliability-advocate");
  if (advBtn) {
    advBtn.hidden = false;
    advBtn.onclick = advocacyCopy;
  }
  const o = d.overall || {};
  if (o.on_time_pct == null) {
    await renderReliabilityUnavailable(host, city, d);
    // Wire the Advocate button to the timetable-only prefilled draft.
    const advBtn2 = document.getElementById("reliability-advocate");
    if (advBtn2) { advBtn2.hidden = false; advBtn2.onclick = () => advocacyCopyNoRealtime(city, host._schedContext); }
    const shareBtn2 = document.getElementById("reliability-share");
    if (shareBtn2) shareBtn2.hidden = true;   // nothing measured to share
    return;
  }
  // Coverage-aware honesty rails: ± interval, data completeness, provisional
  // badge when the window is too incomplete to rank, stratified estimate, and
  // explicit monitoring-outage annotations (ours vs the agency's feed).
  const st = o.stratified;
  const stratLine = st
    ? `<div class="rel-rank">Coverage-adjusted (${st.method === "scheduled" ? "weighted by scheduled service" : "weighted by this city's usual hourly profile"}): <strong>${st.on_time_pct}% on time</strong>${st.missing_service_pct >= 0.5 ? ` · ${st.missing_service_pct}% of scheduled service unobserved${(st.missing_ours_pct >= 0.5 || st.missing_source_pct >= 0.5) ? ` (${st.missing_ours_pct}% our gaps · ${st.missing_source_pct}% feed gaps)` : ""}` : ""}</div>`
    : "";
  const hero = `<div class="rel-hero">
      <span class="rel-bigGrade ${gradeClass(o.grade).replace('dq-grade ', '')}">${esc(o.grade)}</span>
      <div>
        <div class="rel-pct">${o.on_time_pct}%${relCi(o.on_time_ci, " pp")} on time <span style="font-size:14px">· ${relTrend(o.trend_pct)} vs prior ${esc(reliabilityWindow)}</span>${o.data_quality_hold ? ' <span class="rel-prov" title="This feed&#39;s delays are implausible (for example, almost every vehicle reports the same delay), so they are not measured punctuality">DATA-QUALITY HOLD · NOT GRADED OR RANKED</span>' : (o.provisional ? ' <span class="rel-prov" title="Data completeness for this window is below the ranking bar — the score is shown but kept off leaderboards">PROVISIONAL · NOT RANKED</span>' : "")}</div>
        <div class="rel-sub">${Number(o.n_obs).toLocaleString()} observations · ${esc(o.confidence)} confidence${o.coverage_pct != null ? ` · ${o.coverage_pct}% data coverage` : ""} · ${d.n_routes_ranked} routes ranked</div>
        ${o.rank ? `<div class="rel-rank">Ranks <strong>#${o.rank} of ${o.n_cities}</strong> tracked cities${o.percentile != null ? ` · more reliable than ${o.percentile}% of the others` : ""}</div>` : ""}
        ${o.worst_hour ? `<div class="rel-rank">Toughest time of day: <strong>~${hour12(o.worst_hour.hour)}</strong> (${o.worst_hour.on_time_pct}% on time, recent)</div>` : ""}
        ${stratLine}
      </div>
    </div>`;
  // Known monitoring gaps: collapse the text-heavy callout behind a concise
  // one-line summary with a native <details> disclosure (ChatGPT #2). Accessible
  // by default (summary is a real button; native keyboard + aria-expanded).
  var gapNote = "";
  if (d.outages && d.outages.length) {
    var n = d.outages.length;
    var oursN = d.outages.filter(function (g) { return g.scope === "ours"; }).length;
    // Concise scope tag for the summary line: which side the gaps came from.
    var scopeTag = oursN === n ? "pipeline, not agency"
      : (oursN === 0 ? "agency feed outage" : "mixed pipeline / agency");
    var rows = d.outages.map(function (g) {
      var when = new Date(g.started_at).toLocaleDateString(undefined, { month: "short", day: "numeric" });
      return "<li>" + esc(when) + " — " + esc(String(g.minutes)) + " min ("
        + (g.scope === "ours" ? "our pipeline, not the agency" : "agency feed outage") + ")</li>";
    }).join("");
    gapNote = '<details class="rel-gap">'
      + '<summary class="rel-note-gap"><span class="rel-gap-sum">⚠ ' + n + ' monitoring gap' + (n > 1 ? "s" : "")
      + ' this window — ' + scopeTag + '</span><span class="rel-gap-toggle" aria-hidden="true"></span></summary>'
      + '<div class="rel-gap-detail"><ul class="rel-gap-list">' + rows + '</ul>'
      + '<p class="rel-gap-foot">Gap periods count against data coverage — they are never silently averaged into the score.</p></div>'
      + '</details>';
  }
  const tbl = (label, rows) => `<div><h4>${label}</h4>` +
    (rows && rows.length
      ? `<table class="rel-table"><thead><tr><th>Grade</th><th>Route</th><th>On&nbsp;time</th><th>Avg&nbsp;deviation</th><th>Trend</th></tr></thead><tbody>${relRouteRows(rows)}</tbody></table>`
      : '<p class="muted">—</p>') + `</div>`;
  host.innerHTML = hero + gapNote +
    `<p class="muted" style="margin:8px 0">${esc(effectiveWindowText(d))}</p>` +
    `<div class="rel-cols">` +
      tbl("Least reliable routes", d.worst_routes) +
      tbl("Most reliable routes", d.best_routes) +
      tbl("Most improved", d.most_improved) +
      tbl("Most declined", d.most_declined) +
    `</div>` +
    `<p class="rel-conf" style="margin-top:10px">${esc(d.note || "")}</p>`;
}

// Intentional "reliability unavailable" state for a timetable-only city (no
// GTFS-Realtime feed to grade). Explains WHAT is unavailable and WHY, shows what
// IS available (scheduled routes/stops), names the agency/missing feed when known,
// and drives a prefilled specific Advocate draft (ChatGPT #1). Replaces the old
// tiny "No gradeable on-time history" line floating on a blank canvas.
async function renderReliabilityUnavailable(host, city, d) {
  const cityObj = cities.find((c) => c.id === city);
  const cityName = (d && d.name) || (cityObj && cityObj.name) || city;
  const isScheduled = cityObj && cityObj.coverage === "scheduled";
  // Pull the published schedule so we can show what IS available + name routes.
  let sched = null;
  if (isScheduled) {
    try {
      const sd = await getJSON(`/public/scheduled/${encodeURIComponent(city)}`);
      if (sd && (!sd.status || sd.status === "ready")) sched = sd;
    } catch (e) { /* best-effort — the empty-state still renders without it */ }
  }
  host._schedContext = sched;   // stashed for the Advocate button
  const agency = (sched && sched.agency) || (cityObj && cityObj.agency) || "";
  const fmt = (n) => (n == null ? null : Number(n).toLocaleString());
  const nRoutes = sched ? fmt(sched.n_routes) : null;
  const nStops = sched ? fmt(sched.n_stops) : null;

  // Reason line — name the specific missing feed/agency when we know it.
  const why = isScheduled
    ? `${esc(cityName)} publishes a static GTFS <strong>timetable</strong>, but ${agency ? esc(agency) + " has" : "the agency has"} not published a <strong>GTFS-Realtime</strong> feed (live vehicle positions / trip updates). On-time performance can only be measured from a realtime feed, so there is no reliability grade yet.`
    : `We have not yet accrued enough graded on-time observations for <strong>${esc(cityName)}</strong>. Reliability grades populate automatically once its realtime feed has been polled for several days.`;

  // "What IS available" evidence strip — reuses the standard trust-strip.
  const availItems = [];
  if (isScheduled) {
    availItems.push({ label: "Coverage tier", value: "Timetable only" });
    if (nRoutes) availItems.push({ label: "Published routes", value: nRoutes });
    if (nStops) availItems.push({ label: "Published stops", value: nStops });
    availItems.push({ label: "On-time grade", value: "Unavailable", tone: "warn", sub: "no realtime feed" });
  }
  const availStrip = availItems.length ? trustStrip(availItems) : "";

  // Sample of named lines (concrete, not generic) when the schedule is present.
  const routeLabels = (sched && Array.isArray(sched.routes) ? sched.routes : [])
    .map((r) => r && r.label).filter(Boolean).slice(0, 12);
  const routeChips = routeLabels.length
    ? `<div class="rel-unavail-routes"><div class="rel-unavail-h">Routes in the published timetable</div><div class="rel-unavail-chips">`
      + routeLabels.map((l) => `<span class="rte-badge rel-route-chip">${esc(l)}</span>`).join("")
      + (sched && sched.n_routes && sched.routes.length < Number(sched.n_routes)
          ? `<span class="rel-route-more">+${(Number(sched.n_routes) - sched.routes.length).toLocaleString()} more</span>` : "")
      + `</div></div>`
    : "";

  const scheduledLink = isScheduled
    ? `<a class="rel-win" href="/city/${encodeURIComponent(city)}">View scheduled service &amp; map</a>`
    : "";

  host.innerHTML =
    `<div class="rel-unavail">
       <div class="rel-unavail-badge">Reliability unavailable</div>
       <h3 class="rel-unavail-title">No on-time grade for ${esc(cityName)} yet</h3>
       <p class="rel-unavail-why">${why}</p>
       ${availStrip}
       ${routeChips}
       <div class="rel-unavail-cta">
         <p class="rel-unavail-cta-lead">Want live reliability tracking here? Ask ${agency ? esc(agency) : "the agency"} to publish a realtime feed — we've drafted a specific request for you.</p>
         <div class="rel-unavail-actions">
           ${scheduledLink}
           <a class="rel-win" href="/methodology">How grading works</a>
         </div>
       </div>
     </div>`;
}

function advocacyCopyNoRealtime(city, sched) {
  const cityObj = cities.find((c) => c.id === city);
  const cityName = (cityObj && cityObj.name) || city;
  const text = buildAdvocacyTextNoRealtime(cityName, sched);
  openDraftModal(text);
}

// Standardized trust-indicator strip (coverage / confidence / freshness / scope /
// data gaps) — ONE visual treatment reused across Reliability / Reports / Data
// Quality (ChatGPT #4). items: [{label, value, sub?, tone?('good'|'bad'|'warn')}].
// Values are treated as trusted HTML (callers pass pre-escaped/plain strings).
function trustStrip(items) {
  var h = '<div class="trust-strip">';
  (items || []).forEach(function (it) {
    if (!it) return;
    h += '<div class="trust-cell' + (it.tone ? ' trust-' + it.tone : '') + '">' +
      '<div class="trust-val">' + it.value + '</div>' +
      '<div class="trust-lbl">' + esc(it.label) + '</div>' +
      (it.sub ? '<div class="trust-sub">' + esc(it.sub) + '</div>' : '') +
      '</div>';
  });
  return h + '</div>';
}

async function boot() {
  await loadConfig();
  // Served grade bands + canonical windows (F17) — consumed before anything
  // that grades or builds a window picker.
  applyServedConfigVocab();
  // Canonical window set (single source of truth = /api/windows). Loaded before
  // any window picker is built so the set/order/labels/gates never drift.
  await loadWindows();
  initMap();
  initMobileSheet();
  initDraftModal();
  buildWindows();
  buildCmpWindows();
  buildCmpModes();
  buildMapModes();
  const VIEW_PATHS = { compare: "/compare", dataquality: "/data-quality", feeds: "/feeds", headway: "/headway", reliability: "/reliability", methodology: "/methodology" };
  document.querySelectorAll(".view-btn").forEach((b) =>
    b.onclick = () => navigate(b.dataset.view === "cityroutes" && current ? "/city/" + encodeURIComponent(current) + "/routes" : VIEW_PATHS[b.dataset.view] || (current ? "/city/" + encodeURIComponent(current) : "/")));
  // Reveal flag-gated nav items once /api/config flags are known.
  document.querySelectorAll(".view-btn[data-flag]").forEach((b) => {
    if (flag(b.dataset.flag)) b.hidden = false;
  });
  initPrimaryNav();
  // Favorites (flag: favorites) — star toggle + saved-cities bar.
  if (flag("favorites")) {
    const star = document.getElementById("fav-star");
    if (star) star.onclick = () => toggleFav(current);
  }
  // Export buttons (flag: exports_csv) — build the download URL from current state.
  if (flag("exports_csv")) {
    const csvBtn = document.getElementById("dl-csv");
    if (csvBtn) {
      csvBtn.hidden = false;
      csvBtn.onclick = () => {
        if (!current) return;
        window.open(API + "/public/export/stats.csv?city=" + encodeURIComponent(current) +
          "&window=" + encodeURIComponent(window_) + "&scope=" + encodeURIComponent(scope), "_blank");
      };
    }
    const pngBtn = document.getElementById("dl-png");
    if (pngBtn) {
      pngBtn.hidden = false;
      pngBtn.onclick = () => {
        if (!trendChart || typeof trendChart.toBase64Image !== "function") return;
        const a = document.createElement("a");
        a.href = trendChart.toBase64Image();
        a.download = (current || "chart") + "-trend.png";
        a.click();
      };
    }
  }
  if (flag("exports_pdf")) {
    const pdfBtn = document.getElementById("dl-pdf");
    if (pdfBtn) {
      pdfBtn.hidden = false;
      pdfBtn.onclick = () => {
        if (!current) return;
        const w = (window_ === "5m" || window_ === "1h" || window_ === "1d" || window_ === "24h") ? "7d" : window_;
        window.open(API + "/public/report/" + encodeURIComponent(current) + ".pdf?window=" +
          encodeURIComponent(w) + "&scope=" + encodeURIComponent(scope), "_blank");
      };
    }
  }
  document.getElementById("scope-toggle").onchange = (e) => {
    scope = e.target.checked ? "network" : "city";
    if (current) selectCity(current);
  };
  // Presentation-only: the Trend card is a collapsed <details>. A Chart.js canvas
  // created inside a display:none container can render at 0px; resize it once the
  // user opens the card so it fills the now-visible width. No data/router change.
  const trendCard = document.getElementById("trend-chart");
  const trendDetails = trendCard && trendCard.closest("details.collapse-card");
  if (trendDetails) {
    trendDetails.addEventListener("toggle", () => {
      if (trendDetails.open && trendChart && typeof trendChart.resize === "function") {
        trendChart.resize();
      }
    });
  }
  document.getElementById("cmp-scope-toggle").onchange = (e) => {
    cmpScope = e.target.checked ? "network" : "city";
    loadCompare();
  };
  document.getElementById("cmp-add").onclick = cmpAddSelected;
  document.getElementById("cmp-remove").onclick = cmpRemoveSelected;
  document.getElementById("cmp-add-all").onclick = () => {
    // F14 — "Add all live": timetable-only (coverage:"scheduled") cities can
    // never have on-time data, so bulk-add only the live-tracked ones.
    const q = (cmpSearchQuery || "").toLowerCase();
    const avail = cities
      .filter(c => c.coverage !== "scheduled" &&
                   !cmpSelected.has(c.id) && c.name.toLowerCase().includes(q))
      .map(c => c.id);
    cmpAddIds(avail);
    cmpAvailSel.clear(); renderCmpPicker(); loadCompare();
  };
  document.getElementById("cmp-remove-all").onclick = () => {
    cmpSelected.clear(); cmpSelSel.clear(); renderCmpPicker(); loadCompare();
  };
  document.getElementById("cmp-search").oninput = (e) => {
    cmpSearchQuery = e.target.value;
    renderCmpPicker();
  };
  // v2: preset buttons — wire dynamically because cities may not be loaded yet
  // at boot. We wire here since this runs after bootData().
  // The buttons are placed in index.html; they guard against unknown city slugs.
  const _wirePreset = (id, slugs) => {
    const btn = document.getElementById(id);
    if (!btn) return;
    btn.addEventListener('click', () => {
      const valid = slugs.filter((s) => cities.some((c) => c.id === s));
      if (!valid.length) return;
      cmpSelected.clear();
      valid.forEach((s) => cmpSelected.add(s));
      cmpAvailSel.clear(); cmpSelSel.clear();
      renderCmpPicker();
      loadCompare();
    });
  };
  _wirePreset('cmp-preset-flagship', ['amsterdam','oslo','prague','sf','rome']);
  // Watchdog (F2-01): if nothing has populated the city list within 8s —
  // a hung fetch resolves neither way — surface the visible error card
  // instead of leaving skeleton dashes forever.
  const watchdog = setTimeout(() => {
    if (!cities.length) showBootError();
  }, 8000);
  await bootData();
  clearTimeout(watchdog);
}

/* ── Data boot: retried, cache-backed, and loudly recoverable (F2-01) ──
   The /api/cities chain is the only fetch whose failure kills the whole app,
   so it gets three retries with backoff, a last-good localStorage fallback
   (warm boot + auto-revalidation), and — only when both fail — a visible
   "Couldn't load — Retry" card instead of a silently dead skeleton. */
const CITIES_CACHE_KEY = "gts_cities_v2";
// F18 — cache only the fields the boot render actually needs (picker, default-
// city choice, city header, window gating), not the full /api/cities payload.
const CITY_CACHE_FIELDS = [
  "id", "name", "country", "agency", "scope", "center", "zoom", "coverage",
  "note", "last_poll", "ok", "n_vehicles", "since", "data_since",
  "available_windows", "map_mode", "interval", "static_gtfs", "agencies",
];
// Set while the app is running on a cached catalogue: the cache-write time
// (ms epoch). Cleared as soon as a fresh /api/cities lands, so the count line
// and toast can state the cache age instead of asserting "now".
let citiesCachedAt = null;
function cacheAgeMin(t) { return Math.max(1, Math.round((Date.now() - t) / 60000)); }
function readCitiesCache() {
  try {
    const c = JSON.parse(localStorage.getItem(CITIES_CACHE_KEY) || "null");
    return (c && Array.isArray(c.cities) && c.cities.length) ? c : null;
  } catch (e) { return null; }
}
function writeCitiesCache(data) {
  try {
    const slim = (data.cities || []).map((c) => {
      const o = {};
      CITY_CACHE_FIELDS.forEach((k) => { if (c[k] !== undefined) o[k] = c[k]; });
      return o;
    });
    localStorage.setItem(CITIES_CACHE_KEY, JSON.stringify(
      { cachedAt: Date.now(), cities: slim, collecting_since: data.collecting_since || null }));
    localStorage.removeItem("gts_cities_v1");   // retire the old fat-payload cache
  } catch (e) { /* storage full/blocked — cache is best-effort */ }
}

async function bootData() {
  hideBootError();
  // Show the URL's target view IMMEDIATELY (synchronous, no data needed) so a
  // full-page navigation or deep-link to a non-map view (e.g. /data-quality,
  // /reliability) doesn't flash the default live-map for a beat before
  // applyRoute() — which runs at the END of boot, after the /cities fetch and
  // geolocation — switches to it. setView only toggles container visibility and
  // is guarded against the not-yet-initialised map; the heavy per-view data load
  // still happens in applyRoute() below.
  try { setView(currentRoute().view); } catch (e) { /* non-fatal — applyRoute() still runs */ }
  // Footer provenance date — long-form, locale-safe (F2-16); the footer span
  // stays empty (never a "since launch" placeholder) until this resolves.
  getJSONRetry("/health", 2).then((h) => {
    if (h && h.collecting_since) {
      document.getElementById("footer-since").textContent =
        "Collecting data since " + longDate(h.collecting_since) + " · ";
    }
  }).catch(() => { /* non-fatal */ });

  let data = null, fromCache = false;
  try {
    data = await getJSONRetry("/cities", 3);
    writeCitiesCache(data);
    citiesCachedAt = null;
  } catch (e) {
    const cached = readCitiesCache();
    if (cached) {
      data = cached;
      fromCache = true;
      citiesCachedAt = cached.cachedAt || null;
    } else {
      showBootError();
      return;
    }
  }
  cities = data.cities;
  collectingSince = data.collecting_since || null;
  // The window pickers were first built at boot() before collectingSince was
  // known (so only raw windows passed the history gate). Now that the global
  // collection span is known, rebuild them so fixed windows the data actually
  // supports (7d/30d/…) are offered on Compare + the map. (Task 3/4.)
  buildCmpWindows();
  buildWindows();
  // Self-heal: the 8s boot watchdog may have shown the error card while a
  // slow-but-alive fetch (cold API can take 10-25s) was still in flight.
  hideBootError();
  if (fromCache) {
    // Warm fallback: the app is navigable from the cached catalogue while we
    // keep revalidating in the background until the API answers again.
    // F18 — the toast states the cache AGE, never implying the list is current.
    showToast("Data service unreachable — showing the city list as of " +
      (citiesCachedAt ? cacheAgeMin(citiesCachedAt) + " min ago" : "an earlier visit") +
      ". Retrying…", "error");
    const revalidate = setInterval(async () => {
      try {
        const fresh = await getJSON("/cities");
        clearInterval(revalidate);
        writeCitiesCache(fresh);
        citiesCachedAt = null;
        cities = fresh.cities;
        collectingSince = fresh.collecting_since || null;
        buildCmpWindows();
        buildWindows();
        hideToast();
        buildCityBar();
        renderFavBar();
        if (current) selectCity(current);
      } catch (e) { /* still down — keep trying */ }
    }, 30000);
  }
  // Try to figure out where the user is, so we can default to their city
  // and sort the dropdown closest-first. Best effort — never blocks boot.
  userLocation = await detectLocation();
  buildCityBar();
  renderFavBar();
  // Seed the comparison with the 5 cities that have the MOST live vehicles right
  // now BEFORE routing, so a deep-link to /compare opens with real, data-rich,
  // DISTINCT rows (population_rank is mostly null, so the old sort collapsed to
  // alphabetically-first cities that often have no on-time data — making
  // "most punctual" and "least punctual" resolve to the same city). The user
  // adds/removes the rest via the transfer panes.
  // v2: if a share-link hash is present, hydrate from it instead of the default seed.
  const hadHashSeed = cmpApplyHash();
  if (!hadHashSeed) {
    cmpSelected.clear();
    cities.slice()
      .filter((c) => (c.n_vehicles || 0) > 0)
      .sort((a, b) => (b.n_vehicles || 0) - (a.n_vehicles || 0))
      .slice(0, 5)
      .forEach((c) => cmpSelected.add(c.id));
    renderCmpPicker();
  }
  // Honor the incoming URL (/city/{id} or /compare); falls back to default city.
  await applyRoute();
}

// Visible, page-level failure card (works on every route — map, headway,
// reliability, … — unlike a sidebar-only message). Retry re-runs the data
// boot without a full reload.
function showBootError() {
  if (document.getElementById("boot-error")) return;
  const card = document.createElement("div");
  card.id = "boot-error";
  card.setAttribute("role", "alert");
  card.innerHTML =
    '<div class="be-head">' + uiIcon("warn", 16) + " Couldn't reach the data service</div>" +
    '<p class="be-body">The city list and live data didn\'t load — usually a brief network blip. ' +
    "The basemap still works; live data returns as soon as the service answers.</p>" +
    '<button type="button" class="be-retry">' + uiIcon("retry", 13) + " Retry</button>";
  document.body.appendChild(card);
  card.querySelector(".be-retry").addEventListener("click", () => {
    const btn = card.querySelector(".be-retry");
    btn.disabled = true;
    btn.textContent = "Retrying…";
    bootData();
  });
}
function hideBootError() {
  const card = document.getElementById("boot-error");
  if (card) card.remove();
}

/* ── Mobile snap bottom sheet for the stats panel (F2-02) ──
   ≤720px the panel becomes a draggable sheet over a full-height map with
   three snap states — peek (~96px: grab handle + city header), half, full.
   Pure pointer events + CSS transforms; no dependencies. Desktop is untouched
   (the CSS media query is the single switch). */
function initMobileSheet() {
  const panel = document.getElementById("panel");
  const handle = document.getElementById("sheet-handle");
  const viewMap = document.getElementById("view-map");
  if (!panel || !handle || !window.matchMedia) return;
  const mq = window.matchMedia("(max-width: 720px)");
  const SNAPS = ["peek", "half", "full"];
  let state = "peek";

  function apply(s) {
    state = s;
    SNAPS.forEach((k) => panel.classList.remove("sheet-" + k));
    panel.classList.add("sheet-" + s);
    panel.style.transform = "";   // release any drag-time inline transform
    if (viewMap) viewMap.classList.toggle("sheet-peeked", s === "peek");
  }
  function teardown() {
    SNAPS.forEach((k) => panel.classList.remove("sheet-" + k));
    panel.style.transform = "";
    if (viewMap) viewMap.classList.remove("sheet-peeked");
  }
  function sync() { if (mq.matches) apply(state); else teardown(); }

  // Visible sheet height (px) for each snap state.
  function snapHeights() {
    const h = panel.getBoundingClientRect().height || (window.innerHeight * 0.78);
    return { peek: 96, half: Math.round(h * 0.52), full: h };
  }

  let dragging = false, startY = 0, startVisible = 0, moved = false;
  handle.addEventListener("pointerdown", (e) => {
    if (!mq.matches) return;
    dragging = true; moved = false;
    startY = e.clientY;
    startVisible = snapHeights()[state];
    handle.setPointerCapture(e.pointerId);
    panel.classList.add("sheet-dragging");
    e.preventDefault();
  });
  handle.addEventListener("pointermove", (e) => {
    if (!dragging) return;
    const dy = startY - e.clientY;               // up = positive = taller sheet
    if (Math.abs(dy) > 4) moved = true;
    const hts = snapHeights();
    const visible = Math.max(hts.peek, Math.min(hts.full, startVisible + dy));
    panel.style.transform = "translateY(" + Math.max(0, hts.full - visible) + "px)";
  });
  function endDrag(e) {
    if (!dragging) return;
    dragging = false;
    panel.classList.remove("sheet-dragging");
    const hts = snapHeights();
    if (!moved) {
      // Tap on the handle cycles peek → half → full → peek.
      apply(state === "peek" ? "half" : state === "half" ? "full" : "peek");
      return;
    }
    const dy = startY - e.clientY;
    const visible = Math.max(hts.peek, Math.min(hts.full, startVisible + dy));
    let best = "peek", bestDist = Infinity;
    SNAPS.forEach((k) => {
      const d = Math.abs(hts[k] - visible);
      if (d < bestDist) { bestDist = d; best = k; }
    });
    apply(best);
  }
  handle.addEventListener("pointerup", endDrag);
  handle.addEventListener("pointercancel", () => {
    if (dragging) { dragging = false; panel.classList.remove("sheet-dragging"); apply(state); }
  });
  handle.addEventListener("keydown", (e) => {
    if (!mq.matches) return;
    if (e.key === "ArrowUp") { e.preventDefault(); apply(state === "peek" ? "half" : "full"); }
    if (e.key === "ArrowDown") { e.preventDefault(); apply(state === "full" ? "half" : "peek"); }
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); apply(state === "peek" ? "half" : state === "half" ? "full" : "peek"); }
  });
  if (mq.addEventListener) mq.addEventListener("change", sync);
  else if (mq.addListener) mq.addListener(sync);
  sync();
}

/* ─────────── geolocation ─────────── */
function haversineKm(a, b) {
  const R = 6371, toRad = (x) => x * Math.PI / 180;
  const dLat = toRad(b[0] - a[0]), dLon = toRad(b[1] - a[1]);
  const s = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

async function detectLocation() {
  // 1) Browser geolocation (asks permission, most accurate).
  if (navigator.geolocation) {
    try {
      const pos = await new Promise((res, rej) =>
        navigator.geolocation.getCurrentPosition(res, rej,
          { timeout: 4000, maximumAge: 600000 }));
      return { lat: pos.coords.latitude, lon: pos.coords.longitude,
               source: "gps" };
    } catch (e) { /* fall through to IP */ }
  }
  // 2) Server-side IP geolocation via /api/locate. The token-free upstream
  //    (ipapi.co) is called by our backend, never the browser, so no CORS
  //    or rate-limit headaches client-side.
  try {
    const d = await getJSON("/locate");
    if (d.available) return { lat: d.lat, lon: d.lon, source: "ip" };
  } catch (e) { /* ignore */ }
  return null;
}

function pickDefaultCity() {
  if (!userLocation || !cities.length) {
    return cities.find((c) => c.n_vehicles > 0) || cities[0];
  }
  const here = [userLocation.lat, userLocation.lon];
  // Closest city by haversine distance — prefer ones with vehicles right now.
  const ranked = cities.slice().sort((a, b) =>
    haversineKm(here, a.center) - haversineKm(here, b.center));
  return ranked.find((c) => c.n_vehicles > 0) || ranked[0];
}

boot();

/* ─────────── Route browser (2026-07-27, owner ask) ───────────
   Browse/select ANY route in the selected city — running now, idle, or
   RETIRED (in our history but gone from the current timetable) — and see its
   geometry, stops, and when it runs. Both coverage tiers. Lazy: the catalog
   is fetched on first expand and kept as a SINGLE-ENTRY cache (latest city
   only — a revisited city refetches); never fetched during city switch. */
let rbCity = null;          // city the catalog belongs to
let rbCatalog = null;       // /api/route-catalog response
let rbFilter = "all";       // all | running | idle | retired
let rbModeFilter = "all";   // all | bus | tram | metro | rail | ferry | cable
let rbSort = "smart";       // smart | name | live | mode (v2 sort control)
let rbLayer = null;         // Leaflet layer group for the selected route
let rbSelected = null;      // selected route_id
let rbRequestId = 0;        // monotonic detail-request token (race guard)

function rbCityChanged() {
  rbCity = null; rbCatalog = null; rbSelected = null;
  // Reset filter + search too (review 2026-07-27: a sticky "retired" filter
  // in a city with no retired routes strands the list on "No routes match"
  // with no visible chip to clear it).
  rbFilter = "all"; rbModeFilter = "all"; rbSort = "smart";
  const rbs = document.getElementById("rb-search");
  if (rbs) rbs.value = "";
  for (const id of ["rb-search-field", "rb-filters", "rb-list"]) {
    const el = document.getElementById(id);
    if (el) el.style.display = "";
  }
  if (rbLayer) rbLayer.clearLayers();
  const list = document.getElementById("rb-list");
  const det = document.getElementById("rb-detail");
  const cnt = document.getElementById("rb-count");
  if (list) list.innerHTML = "";
  if (det) det.innerHTML = "";
  if (cnt) cnt.textContent = "";
  const card = document.getElementById("route-browser-card");
  if (card) card.open = false;
}

async function rbLoadCatalog() {
  if (!current) return;
  if (rbCity === current && rbCatalog) return;
  const list = document.getElementById("rb-list");
  list.innerHTML = '<li class="muted">Loading routes…</li>';
  // Capture the request's city: a stale response must never install itself
  // for a different city (review 2026-07-27, race A).
  const requestCity = current;
  let d;
  try { d = await getJSON("/route-catalog?city=" + encodeURIComponent(requestCity)); }
  catch (e) { list.innerHTML = '<li class="muted">Could not load routes — retry by reopening this panel.</li>'; return; }
  if (current !== requestCity) return;
  if (d.status !== "ready") {
    list.innerHTML = '<li class="muted">Preparing this city\'s timetable… reopen this panel in a moment.</li>';
    return;
  }
  rbCity = requestCity; rbCatalog = d;
  const cnt = document.getElementById("rb-count");
  if (cnt) cnt.textContent =
    d.routes.length + " current" + (d.retired.length ? " · " + d.retired.length + " retired · " + (d.routes.length + d.retired.length) + " total" : " routes");
  rbRenderFilters();
  rbRenderList();
}

function rbRenderFilters() {
  const host = document.getElementById("rb-filters");
  const d = rbCatalog;
  const counts = {
    all: d.routes.length + d.retired.length,
    running: d.routes.filter((r) => r.status === "running").length,
    idle: d.routes.filter((r) => r.status === "idle").length,
    retired: d.retired.length,
  };
  const labels = { all: "All", running: "Running now", idle: "Not running now", retired: "Retired" };
  host.innerHTML = "";
  // Re-render only the list on change: native controls keep keyboard focus.
  function field(id, label, options, value, change) {
    const wrap = document.createElement("div");
    wrap.className = "rb-field";
    const lbl = document.createElement("label");
    lbl.htmlFor = id;
    lbl.textContent = label;
    const select = document.createElement("select");
    select.id = id;
    for (const [key, text] of options) {
      const option = document.createElement("option");
      option.value = key; option.textContent = text;
      select.appendChild(option);
    }
    select.value = value;
    select.onchange = () => { change(select.value); rbRenderList(); };
    wrap.append(lbl, select);
    host.appendChild(wrap);
  }
  field("rb-status", "Route status", ["all", "running", "idle", "retired"]
    .filter((key) => key === "all" || counts[key])
    .map((key) => [key, labels[key] + " (" + counts[key] + ")"]),
    rbFilter, (value) => { rbFilter = value; });

  const allRows = d.routes.concat(d.retired.map((r) => Object.assign({}, r, { mode: r.mode || "other" })));
  const modesPresent = MODE_ORDER.filter((mode) => allRows.some((r) => (r.mode || "other") === mode));
  if (modesPresent.length > 1) {
    field("rb-mode", "Transit type", [["all", "All modes"]].concat(modesPresent.map((mode) =>
      [mode, (MODE_LABEL[mode] || mode) + " (" + allRows.filter((r) => (r.mode || "other") === mode).length + ")"])),
      rbModeFilter, (value) => { rbModeFilter = value; });
  }
  field("rb-sort", "Sort routes", [["smart", "Running, live, name"], ["name", "Name A→Z"],
    ["live", "Most live vehicles"], ["mode", "Mode then name"]], rbSort, (value) => { rbSort = value; });
}

function rbRows() {
  const d = rbCatalog;
  const q = (document.getElementById("rb-search").value || "").trim().toLowerCase();
  let rows = [];
  if (rbFilter !== "retired") {
    rows = d.routes
      .filter((r) => rbFilter === "all" || r.status === rbFilter)
      .map((r) => Object.assign({}, r, { retiredRow: false }));
  }
  if (rbFilter === "all" || rbFilter === "retired") {
    rows = rows.concat(d.retired.map((r) => Object.assign({}, r, {
      short_name: r.short_name || "", mode: r.mode || "other",
      live_now: 0, status: "retired", retiredRow: true,
    })));
  }
  // Mode filter (v2): apply after status filter, before text search.
  if (rbModeFilter !== "all") {
    rows = rows.filter((r) => (r.mode || "other") === rbModeFilter);
  }
  if (q) {
    rows = rows.filter((r) =>
      (r.short_name || "").toLowerCase().includes(q) ||
      (r.route_id || "").toLowerCase().includes(q));
  }
  // Sort (v2): user-selectable via rbSort.
  const nameKey = (r) => String(r.short_name || r.route_id);
  if (rbSort === "name") {
    rows.sort((a, b) => nameKey(a).localeCompare(nameKey(b), undefined, { numeric: true }));
  } else if (rbSort === "live") {
    rows.sort((a, b) => (b.live_now - a.live_now) ||
      nameKey(a).localeCompare(nameKey(b), undefined, { numeric: true }));
  } else if (rbSort === "mode") {
    const modeRank = (r) => MODE_ORDER.indexOf(r.mode || "other") + (r.retiredRow ? 100 : 0);
    rows.sort((a, b) => (modeRank(a) - modeRank(b)) ||
      nameKey(a).localeCompare(nameKey(b), undefined, { numeric: true }));
  } else {
    // "smart" (default): running first by live_now, then idle alphabetically, retired last.
    const rank = { running: 0, idle: 1, retired: 2 };
    rows.sort((a, b) => (rank[a.status] - rank[b.status]) ||
      (b.live_now - a.live_now) ||
      nameKey(a).localeCompare(nameKey(b), undefined, { numeric: true }));
  }
  return rows;
}

function rbRenderList() {
  const list = document.getElementById("rb-list");
  const rows = rbRows();
  if (!rows.length) { list.innerHTML = '<li class="muted">No routes match.</li>'; return; }
  list.innerHTML = rows.map((r) => {
    const name = esc(r.short_name || r.route_id);
    const sub = r.retiredRow
      ? "retired · last seen " + esc(r.last_seen || "?")
      : esc(MODE_LABEL[r.mode] || r.mode) +
        (r.live_now ? " · " + r.live_now + " live now" : "") +
        (r.active_today === false ? " · not scheduled today" : "");
    return '<li><button type="button" class="rb-row' + (rbSelected === r.route_id ? " active" : "") +
      (r.retiredRow ? " rb-retired" : "") + '" data-rid="' + esc(r.route_id) + '">' +
      '<span class="rb-name">' + name + '</span><span class="rb-sub">' + sub + '</span></button></li>';
  }).join("");
  list.querySelectorAll(".rb-row").forEach((li) => {
    li.onclick = () => rbSelect(li.getAttribute("data-rid"));
  });
}

function rbShowCatalog() {
  const selected = rbSelected;
  rbSelected = null;
  if (rbLayer) rbLayer.clearLayers();
  for (const id of ["rb-search-field", "rb-filters", "rb-list"]) {
    const el = document.getElementById(id);
    if (el) el.style.display = "";
  }
  const det = document.getElementById("rb-detail");
  if (det) det.innerHTML = "";
  if (rbCatalog) rbRenderList();
  const row = Array.from(document.querySelectorAll("#rb-list .rb-row"))
    .find((el) => el.getAttribute("data-rid") === selected);
  if (row) row.focus({ preventScroll: true });
}

async function rbSelect(routeId) {
  rbSelected = routeId;
  // Two-state workflow (2026-07-27 review): the detail view REPLACES the
  // catalog — never stacked below the full route list.
  for (const id of ["rb-search-field", "rb-filters", "rb-list"]) {
    const el = document.getElementById(id);
    if (el) el.style.display = "none";
  }
  const det = document.getElementById("rb-detail");
  function message(text) {
    det.innerHTML = '<button type="button" class="view-link" id="rb-back">← All routes</button>' +
      '<p class="muted">' + esc(text) + '</p>';
    const back = document.getElementById("rb-back");
    back.onclick = rbShowCatalog;
    back.focus({ preventScroll: true });
  }
  message("Loading route…");
  const requestCity = current;
  const requestId = ++rbRequestId;
  let d;
  try {
    d = await getJSON("/route-detail?city=" + encodeURIComponent(requestCity) +
      "&route_id=" + encodeURIComponent(routeId));
  } catch (e) {
    if (requestId === rbRequestId && current === requestCity && rbSelected === routeId) {
      message("Could not load this route.");
    }
    return;
  }
  // Race guards (review 2026-07-27): drop this response if a newer detail
  // request started, the city switched, or the selection moved on.
  if (requestId !== rbRequestId || current !== requestCity || rbSelected !== routeId) return;
  if (d.status && d.status !== "ready") {
    message("Preparing this city's timetable… try again in a moment.");
    return;
  }
  // Map overlay: focus the city's REAL geometry for this route when the map
  // already has it (timetable cities); else draw the stop-sequence path
  // (dashed = schematic, same convention as timetable cities).
  // Single geometry owner (review 2026-07-27, race D): the browser draws
  // ONLY into rbLayer — never toggles the scheduled-shapes layer (toggle is
  // not an idempotent "show" and its selection can't be cleared from here).
  if (!rbLayer) { rbLayer = L.layerGroup(); rbLayer.addTo(map); }
  rbLayer.clearLayers();
  const col = MODE_LINE_COLOR[d.mode] || MODE_LINE_COLOR.other;
  if (d.geometry && d.geometry.polylines.length) {
    d.geometry.polylines.forEach((path) => {
      rbLayer.addLayer(L.polyline(path, { color: col, weight: 3, opacity: 0.85, dashArray: "4 6" }));
    });
    d.stops.forEach((s) => {
      if (s.lat == null || s.lon == null) return;
      rbLayer.addLayer(L.circleMarker([s.lat, s.lon], {
        radius: 3, color: col, weight: 1, fillOpacity: 0.9,
      }).bindTooltip(esc(s.name)));
    });
    try {
      const b = L.latLngBounds(d.geometry.polylines[0]);
      if (b.isValid()) map.fitBounds(b.pad(0.15));
    } catch (e) { /* keep view */ }
  }
  // Detail panel — Back first, then the route identity line.
  const name = esc(d.short_name || d.route_id);
  let h = '<button type="button" class="view-link" id="rb-back">← All routes</button>' +
    '<div class="rb-det-head"><strong>' + name + '</strong> · ' +
    esc(MODE_LABEL[d.mode] || d.mode) +
    (d.live.vehicles_now ? ' · ' + d.live.vehicles_now + ' live' : "") +
    (d.retired ? ' <span class="rel-prov">RETIRED</span>' : "") + "</div>";
  if (d.note) h += '<p class="muted">' + esc(d.note) + "</p>";

  // v2 — Above-fold history summary strip (real fields from /api/route-detail).
  if (d.history && d.history.n_observations > 0) {
    const hist = d.history;
    const pct = hist.on_time_pct != null ? hist.on_time_pct.toFixed(1) + "%" : "—";
    const avgD = hist.avg_delay_sec != null ? Math.round(hist.avg_delay_sec) + "s avg delay" : null;
    const obs = hist.n_observations.toLocaleString() + " obs";
    const since = hist.first_day || null;
    h += '<div class="rb-hist-strip">' +
      '<div class="rb-hist-kpi"><span class="rb-hist-val">' + pct + '</span><span class="rb-hist-lbl">on-time</span></div>' +
      (avgD ? '<div class="rb-hist-kpi"><span class="rb-hist-val">' + esc(avgD) + '</span><span class="rb-hist-lbl">avg delay</span></div>' : '') +
      '<div class="rb-hist-kpi"><span class="rb-hist-val">' + obs + '</span><span class="rb-hist-lbl">' + (since ? 'since ' + esc(since) : 'total') + '</span></div>' +
      '</div>';
  } else if (d.history && d.history.n_observations === 0) {
    h += '<p class="muted rb-hist-none">No observation history yet for this route.</p>';
  }

  if (d.schedule) {
    const s = d.schedule;
    const days = s.service_days && s.service_days.length === 7 ? "daily"
      : (s.service_days || []).map((x) => x[0].toUpperCase() + x.slice(1)).join(" ");
    h += '<p class="rb-sched">Runs ' + esc(days || "—") +
      (s.departures_today ? " · " + s.departures_today + " departures today (first " + esc(s.first_departure) + ", last " + esc(s.last_departure) + ")" : " · no departures scheduled today") +
      (s.typical_headway_min ? " · every ~" + s.typical_headway_min + " min" : "") + ".</p>";
    if (!s.calendar_supported) h += '<p class="muted">This feed publishes no service calendar — figures cover the full timetable, day unknown.</p>';
  }
  h += '<p class="rb-live">' + (d.live.vehicles_now
    ? "<strong>" + d.live.vehicles_now + "</strong> vehicle" + (d.live.vehicles_now === 1 ? "" : "s") + " on this route right now."
    : "No vehicles on this route right now.") + "</p>";
  // Direction disclosure from /api/route-detail, or a plain stop-list fallback.
  // Show choices when 2+ directions are available; single-
  // direction and legacy responses fall back to the flat stop list (d.stops).
  function _rbStopListHtml(stops) {
    if (!stops || !stops.length) return "";
    return '<ol class="rb-dir-ol" tabindex="0" aria-label="Route stops">' + stops.map((s) => {
      const clickable = s.lat != null && s.lon != null;
      return '<li class="rb-stop-row">' + (clickable
        ? '<button type="button" class="rb-stop-clickable" data-lat="' + s.lat + '" data-lon="' + s.lon +
          '" data-name="' + esc(s.name) + '" aria-label="Locate ' + esc(s.name) + ' on map">' + esc(s.name) + '</button>'
        : esc(s.name)) + '</li>';
    }).join("") + "</ol>";
  }
  const dirs = Array.isArray(d.directions) ? d.directions : [];
  if (dirs.length >= 2) {
    // Direction tabs: tab bar + panel per direction.
    h += routeDirectionsHtml(dirs, _rbStopListHtml);
  } else if (dirs.length === 1) {
    // Single direction from new field — render its stops directly, no direction chooser.
    const label = dirs[0].headsign ? " towards " + esc(dirs[0].headsign) : "";
    const stops = dirs[0].stops || [];
    if (stops.length) {
      h += '<details class="rb-stops"><summary>' + stops.length + " stops" + label +
        " — click to locate on map</summary>" + _rbStopListHtml(stops) + "</details>";
    }
  } else if (d.stops && d.stops.length) {
    // Legacy fallback: use the flat d.stops array (no direction data).
    h += '<details class="rb-stops"><summary>' + d.stops.length +
      " stops — click to locate on map</summary>" + _rbStopListHtml(d.stops) + "</details>";
  }
  det.innerHTML = h;
  const back = document.getElementById("rb-back");
  if (back) {
    back.onclick = rbShowCatalog;
    back.focus({ preventScroll: true });
  }
  wireRouteDirections(det);
  // v2 — wire stop click-through: pan map + open popup on click/Enter.
  det.querySelectorAll(".rb-stop-clickable").forEach((li) => {
    const lat = parseFloat(li.getAttribute("data-lat"));
    const lon = parseFloat(li.getAttribute("data-lon"));
    const stopName = li.getAttribute("data-name");
    const activate = () => {
      if (!map || isNaN(lat) || isNaN(lon)) return;
      map.setView([lat, lon], Math.max(map.getZoom(), 15), { animate: true });
      L.popup({ closeOnClick: true, autoClose: true })
        .setLatLng([lat, lon])
        .setContent("<strong>" + esc(stopName) + "</strong>")
        .openOn(map);
    };
    li.onclick = activate;
    // Native buttons provide Enter/Space activation without duplicate handlers.
  });
}

(function rbInit() {
  const card = document.getElementById("route-browser-card");
  if (!card) return;
  card.addEventListener("toggle", () => { if (card.open) rbLoadCatalog(); });
  const search = document.getElementById("rb-search");
  if (search) search.addEventListener("input", () => { if (rbCatalog) rbRenderList(); });
})();
